import { useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams, useLocation } from 'react-router-dom';
import { api, apiBaseUrl, resolveApiMediaUrl } from '../api/client.js';
import { formatBrl } from '../utils/currency.js';
import { useTheme } from '../theme/ThemeContext.jsx';
import { parseStoreAccent, storeAccentCssVariables } from '../theme/storeAccent.js';
import { parseStoreHomeLayout } from '../theme/storeHomeLayout.js';
import {
  parseStoreHero,
  resolveHeroSlides,
  shouldShowStoreHero,
} from '../theme/storeHero.js';
import { StoreHeroCarousel } from '../components/store/StoreHeroCarousel.jsx';
import { parseStoreCatalogLuxe } from '../theme/storeCatalogLuxe.js';
import { parseStoreCatalogList } from '../theme/storeCatalogList.js';
import { parseStoreProductDisplay } from '../theme/storeProductDisplay.js';
import { productGalleryUrls } from '../utils/productImage.js';
import { StoreHomeLayout } from '../components/store/StoreHomeLayout.jsx';
import { StoreCatalogLuxeView } from '../components/store/catalog-luxe/StoreCatalogLuxeView.jsx';
import { StoreCatalogList } from '../components/store/StoreCatalogList.jsx';
import { StoreConsentBanner } from '../components/store/StoreConsentBanner.jsx';
import { StorePwaInstallButton } from '../components/store/StorePwaInstallButton.jsx';
import { StorePwaInstallHint } from '../components/store/StorePwaInstallHint.jsx';
import { useStorePwa } from '../hooks/useStorePwa.js';
import { useStoreTracking } from '../hooks/useStoreTracking.js';
import {
  tenantDocumentOrderAiAvailable,
  tenantDocumentOrderEnabled,
} from '../theme/storeDocumentOrder.js';
import { DocumentOrderEntryButton } from '../components/store/document-order/DocumentOrderEntryButton.jsx';
import { DocumentOrderModal } from '../components/store/document-order/DocumentOrderModal.jsx';
import { CatalogExportButton } from '../components/store/catalog-export/CatalogExportButton.jsx';
import { CatalogExportModal } from '../components/store/catalog-export/CatalogExportModal.jsx';
import { StockLimitModal } from '../components/store/StockLimitModal.jsx';
import { ProductLowStockHint } from '../components/store/ProductLowStockHint.jsx';
import { ProductNewHint } from '../components/store/ProductNewHint.jsx';
import {
  CHECKOUT_FULFILL_NEW,
  CHECKOUT_FULFILL_PICKUP,
  checkoutFulfillmentDefaultMode,
  checkoutFulfillmentIsNewAddress,
  checkoutFulfillmentIsPickup,
  checkoutFulfillmentIsSaved,
  checkoutFulfillmentParseSavedId,
  checkoutFulfillmentSavedValue,
} from '../utils/checkoutFulfillment.js';
import {
  buildEffectiveCheckoutPaymentMethods,
  checkoutPaymentIsOnline,
  findCheckoutPaymentOption,
} from '../utils/checkoutPaymentMethods.js';
import { cepDigits, formatCep, extractCepFromAddressText, resolveCheckoutCepDigits } from '../utils/cep.js';
import { orderIsPaidViaOnlineGateway } from '../utils/orderStatus.js';
import { clampCartToStock, productIsSoldOut, productTrackedStock } from '../utils/productStock.js';
import { registerRadarSearch } from '../components/store/customer-radar/customerRadarApi.js';
import { RestockNotifyButton } from '../components/store/customer-radar/RestockNotifyButton.jsx';
import {
  contactFieldHtmlRequired,
  contactRequirementsHint,
  getCustomerContactRequirements,
  loginIdentityPlaceholder,
  validateCustomerContact,
  validateLoginIdentity,
} from '../utils/customerContactRequirements.js';

/** Garante array — evita crash se a API omitir a chave ou devolver tipo inesperado. */
function asStringList(v) {
  return Array.isArray(v) ? v : [];
}

/**
 * Loja com «ocultar preços até login do cliente».
 * A API envia 0/1; aceita string/boolean por robustez (evita `Number('')` → 0 e mostrar preço por engano).
 */
function tenantRequiresCustomerAuth(tenant) {
  if (!tenant || typeof tenant !== 'object') return false;
  const v = tenant.hide_prices_require_login;
  if (v === true) return true;
  if (v === false || v == null) return false;
  if (typeof v === 'number') return v !== 0 && !Number.isNaN(v);
  if (typeof v === 'string') {
    const s = v.trim().toLowerCase();
    return s === '1' || s === 'true' || s === 'yes' || s === 'on';
  }
  return false;
}

/** Cadastro via formulário na loja desactivado pelo dono da empresa (login de clientes existentes mantém-se). */
function tenantBlocksPublicCustomerRegistration(tenant) {
  if (!tenant || typeof tenant !== 'object') return false;
  const v = tenant.block_customer_public_registration;
  if (v === true) return true;
  if (v === false || v == null) return false;
  if (typeof v === 'number') return v !== 0 && !Number.isNaN(v);
  if (typeof v === 'string') {
    const s = v.trim().toLowerCase();
    return s === '1' || s === 'true' || s === 'yes' || s === 'on';
  }
  return false;
}

function tenantDeliveryZonesEnabled(tenant) {
  if (!tenant || typeof tenant !== 'object') return false;
  const v = tenant.delivery_zones_enabled;
  if (v === true) return true;
  if (v === false || v == null) return false;
  if (typeof v === 'number') return v !== 0 && !Number.isNaN(v);
  if (typeof v === 'string') {
    const s = v.trim().toLowerCase();
    return s === '1' || s === 'true' || s === 'yes' || s === 'on';
  }
  return false;
}

function composeAddressFromCep(row, cepFormatted) {
  const street = String(row?.logradouro || '').trim();
  const district = String(row?.bairro || '').trim();
  const city = String(row?.localidade || '').trim();
  const state = String(row?.uf || '').trim();
  const firstLine = [street, district].filter(Boolean).join(', ');
  const cityUf = city && state ? `${city}/${state}` : city || state;
  return [firstLine, cityUf, cepFormatted ? `CEP ${cepFormatted}` : ''].filter(Boolean).join(' - ');
}

function phoneDigits(value) {
  return String(value || '').replace(/\D/g, '');
}

function formatPhoneDisplayBr(value) {
  const d = phoneDigits(value);
  if (!d) return '';
  const local = d.startsWith('55') && d.length >= 12 ? d.slice(2) : d;
  if (local.length === 11) {
    return `(${local.slice(0, 2)}) ${local.slice(2, 7)}-${local.slice(7)}`;
  }
  if (local.length === 10) {
    return `(${local.slice(0, 2)}) ${local.slice(2, 6)}-${local.slice(6)}`;
  }
  return String(value || '');
}

function ResilientImage({
  src,
  alt = '',
  className = '',
  style,
  loading = 'lazy',
  decoding = 'async',
  draggable = false,
  retries = 2,
  fallback = null,
}) {
  const [attempt, setAttempt] = useState(0);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    setAttempt(0);
    setFailed(false);
  }, [src]);

  if (!src || failed) return fallback;

  const srcWithRetry =
    attempt > 0 ? `${src}${src.includes('?') ? '&' : '?'}img_retry=${attempt}` : src;

  return (
    <img
      src={srcWithRetry}
      alt={alt}
      className={className}
      style={style}
      loading={loading}
      decoding={decoding}
      draggable={draggable}
      onError={() => {
        setAttempt((prev) => {
          if (prev >= retries) {
            setFailed(true);
            return prev;
          }
          return prev + 1;
        });
      }}
    />
  );
}

function productYoutubeUrl(p) {
  if (!p || typeof p !== 'object') return null;
  const s = p.youtube_video_url;
  if (typeof s !== 'string') return null;
  const t = s.trim();
  return t === '' ? null : t;
}

/** Extrai ID de vídeo a partir do URL do YouTube para embed nocookie. */
function youtubeVideoId(input) {
  const s = typeof input === 'string' ? input.trim() : '';
  if (!s) return null;
  try {
    const normalized = /^https?:\/\//i.test(s) ? s : `https://${s.replace(/^\/\//, '')}`;
    const u = new URL(normalized);
    const host = u.hostname.replace(/^www\./i, '').toLowerCase();
    if (host === 'youtu.be') {
      const id = u.pathname.replace(/^\//, '').split(/[/?#]/)[0];
      return id || null;
    }
    if (host === 'youtube.com' || host === 'youtube-nocookie.com' || host === 'm.youtube.com') {
      const v = u.searchParams.get('v');
      if (v) return v;
      const parts = u.pathname.split('/').filter(Boolean);
      const embed = parts.indexOf('embed');
      if (embed >= 0 && parts[embed + 1]) return parts[embed + 1].split('?')[0];
      const shorts = parts.indexOf('shorts');
      if (shorts >= 0 && parts[shorts + 1]) return parts[shorts + 1].split('?')[0];
      const live = parts.indexOf('live');
      if (live >= 0 && parts[live + 1]) return parts[live + 1].split('?')[0];
    }
  } catch {
    return null;
  }
  return null;
}

function youtubeEmbedSrc(url) {
  const id = youtubeVideoId(url);
  if (!id) return null;
  return `https://www.youtube-nocookie.com/embed/${encodeURIComponent(id)}?rel=0`;
}

/** Crossfade tipo «nuvem» entre fotos; pausa ao passar o rato por cima. */
function ProductCardFadeGallery({
  urls,
  onOpen,
}) {
  const [idx, setIdx] = useState(0);
  const [pause, setPause] = useState(false);
  const urlsKey = urls.join('|');

  useEffect(() => {
    setIdx(0);
  }, [urlsKey]);

  useEffect(() => {
    if (urls.length <= 1 || pause) return undefined;
    const t = window.setInterval(() => {
      setIdx((j) => (j + 1) % urls.length);
    }, 4200);
    return () => window.clearInterval(t);
  }, [urls.length, pause, urlsKey]);

  const open = () => {
    if (typeof onOpen === 'function') onOpen({ startIndex: idx });
  };

  const imgEase =
    'pointer-events-none absolute inset-0 m-auto max-h-full max-w-full origin-bottom rounded-lg object-contain shadow-sm transition-[opacity,transform,box-shadow] duration-[900ms] ease-in-out motion-reduce:transition-none motion-reduce:opacity-100';

  const hoverGrow =
    'group-hover/cardimg:scale-[1.82] group-hover/cardimg:shadow-[0_22px_44px_-12px_rgba(0,0,0,0.28)] group-hover/cardimg:ring-2 group-hover/cardimg:ring-white/85 dark:group-hover/cardimg:shadow-[0_22px_48px_-10px_rgba(0,0,0,0.55)] dark:group-hover/cardimg:ring-zinc-800/80 motion-reduce:group-hover/cardimg:scale-100 [@media(hover:none)]:group-hover/cardimg:scale-100 [@media(hover:none)]:group-hover/cardimg:shadow-sm [@media(hover:none)]:group-hover/cardimg:ring-0';

  return (
    <button
      type="button"
      aria-label={`Ampliar fotos`}
      title="Clique para ampliar"
      className="group/cardimg relative z-[1] flex h-full w-full max-h-full max-w-full cursor-zoom-in flex-col items-stretch rounded-lg border-0 bg-transparent p-0 outline-none focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--st-500)] focus-visible:ring-offset-2 focus-visible:ring-offset-zinc-100 dark:focus-visible:ring-offset-zinc-800"
      onMouseEnter={() => setPause(true)}
      onMouseLeave={() => setPause(false)}
      onFocus={() => setPause(true)}
      onBlur={() => setPause(false)}
      onClick={open}
    >
      <span className="relative flex min-h-0 flex-1 items-center justify-center">
        {urls.map((u, i) => (
          <ResilientImage
            key={`${u}-${i}`}
            src={resolveApiMediaUrl(u)}
            alt=""
            loading={i === 0 ? 'lazy' : 'lazy'}
            decoding="async"
            draggable={false}
            retries={2}
            className={`${imgEase} ${hoverGrow} ${i === idx ? 'z-[2] opacity-100' : 'z-[1] opacity-0'}`}
            fallback={null}
          />
        ))}
      </span>
    </button>
  );
}

/**
 * Link directo ao produto na vitrine: `?p=id` + opcional `cod_pro` (codigo_erp) e `nome` (nome do produto).
 * Ex.: `/loja?p=9399&cod_pro=7871&nome=Renaut+Boreal`
 */
function buildStoreProductShareUrl(pathname, productOrId) {
  if (typeof window === 'undefined' || pathname == null) return '';
  const row =
    productOrId != null && typeof productOrId === 'object' && !Array.isArray(productOrId)
      ? productOrId
      : null;
  const id = row != null ? row.id : productOrId;
  if (id == null) return '';
  try {
    const u = new URL(`${window.location.origin}${pathname}`);
    u.searchParams.set('p', String(id));
    if (row) {
      const cod = row.codigo_erp != null ? String(row.codigo_erp).trim() : '';
      if (cod !== '') u.searchParams.set('cod_pro', cod);
      const nome = row.nome != null ? String(row.nome).trim() : '';
      if (nome !== '') u.searchParams.set('nome', nome);
    }
    return u.toString();
  } catch {
    return '';
  }
}

/** Garante URL absoluta (https) para serviços de partilha. */
function normalizeShareUrlForSocial(url) {
  if (!url || typeof window === 'undefined') return url;
  try {
    return new URL(url, window.location.origin).href;
  } catch {
    return url;
  }
}

/**
 * URL pública servida pelo backend com Open Graph estáticos por produto.
 * É esta URL que vai nos botões de Facebook / X — quando o crawler dessas
 * redes visita-a, recebe HTML com `og:title`, `og:image`, `og:description`,
 * etc. e mostra o cartão completo (foto, nome e descrição do produto). O
 * humano que clica no link partilhado é imediatamente redireccionado para a
 * URL da SPA via `window.location.replace` + `<meta refresh>` de fallback.
 *
 * O endpoint é `GET {API_BASE}/og/{slug}/p/{id}` e devolve HTML cacheável.
 *
 * @param {string} slug Slug da loja (ex.: "lojateste").
 * @param {number|string} productId
 * @returns {string} URL absoluta ou string vazia se não der.
 */
function buildStoreProductOgUrl(slug, productId) {
  if (!slug || productId == null || typeof window === 'undefined') return '';
  const id = Number(productId);
  if (!Number.isFinite(id) || id <= 0) return '';
  try {
    const base = (apiBaseUrl() || '').replace(/\/+$/, '');
    const root = base.startsWith('http')
      ? base
      : `${window.location.origin}${base.startsWith('/') ? base : `/${base || 'api'}`}`;
    return `${root}/og/${encodeURIComponent(String(slug))}/p/${id}`;
  } catch {
    return '';
  }
}

/**
 * Verifica se a URL é alcançável publicamente pelos crawlers (Facebook, X, etc.).
 * Sharers como o `sharer.php` validam a URL antes de a anexar ao composer:
 * quando o host é localhost / IP privado / mDNS, o Facebook descarta-a
 * silenciosamente e o composer abre vazio. Esta verificação evita esse caso.
 */
function isPubliclyReachableUrl(absoluteUrl) {
  try {
    const u = new URL(absoluteUrl);
    if (u.protocol !== 'http:' && u.protocol !== 'https:') return false;
    const host = u.hostname.toLowerCase();
    if (host === 'localhost' || host === '127.0.0.1' || host === '0.0.0.0' || host === '::1') {
      return false;
    }
    if (/^10\./.test(host)) return false;
    if (/^192\.168\./.test(host)) return false;
    if (/^172\.(1[6-9]|2\d|3[01])\./.test(host)) return false;
    if (/\.(local|lan|internal|home|test)$/.test(host)) return false;
    if (/^\d+\.\d+\.\d+\.\d+$/.test(host)) {
      /* IP público é raro num browser de loja; aceitamos mesmo assim. */
      return true;
    }
    return true;
  } catch {
    return false;
  }
}

/**
 * Partilha Facebook: o endpoint público oficial continua a ser
 * `https://www.facebook.com/sharer/sharer.php` com parâmetro `u` (URL codificada).
 * Aceita também `title` como sugestão (alguns clients mostram-no enquanto a
 * pré-visualização Open Graph não chega).
 * Nota: crawlers do Facebook não executam JS; SPAs sem OG no HTML inicial podem
 * mostrar cartão de pré-visualização vazio.
 * @see https://developers.facebook.com/docs/sharing/webmasters/
 */
function buildFacebookShareUrl(absolutePageUrl, titleText) {
  const qs = new URLSearchParams();
  qs.set('u', absolutePageUrl);
  const t = (titleText && String(titleText).trim()) || '';
  if (t) qs.set('title', t);
  return `https://www.facebook.com/sharer/sharer.php?${qs.toString()}`;
}

/**
 * Partilha X (Twitter): o endpoint canónico é `https://x.com/intent/tweet`
 * (twitter.com/intent/tweet também funciona). Quando o utilizador não está
 * autenticado, o X faz um redirect interno para `/i/flow/login?...`, mas o
 * destino final continua a ser o composer com o texto/URL pré-preenchidos.
 * @see https://developer.x.com/en/docs/twitter-for-websites/tweet-button/guides/web-intent
 */
function buildXShareUrl(absolutePageUrl, text, hashtagsCsv, viaHandle) {
  const qs = new URLSearchParams();
  qs.set('url', absolutePageUrl);
  const t = (text && String(text).trim()) || '';
  if (t) qs.set('text', t);
  const h = (hashtagsCsv && String(hashtagsCsv).trim()) || '';
  if (h) qs.set('hashtags', h);
  const v = (viaHandle && String(viaHandle).replace(/^@/, '').trim()) || '';
  if (v) qs.set('via', v);
  return `https://x.com/intent/tweet?${qs.toString()}`;
}

/** Repor a `true` para voltar a mostrar Facebook, Instagram e X no modal de partilha. */
const STORE_SHARE_MODAL_FB_IG_ENABLED = true;

/** Acções de partilha no modal foto/vídeo (Instagram via área nativa ou cópia). */
function ProductModalShareRow({ shareUrl, shareTitle, shareOgUrl, isDark }) {
  const [hint, setHint] = useState('');
  const labelCls = `text-[11px] font-semibold uppercase tracking-[0.14em] ${isDark ? 'text-zinc-500' : 'text-zinc-500'}`;
  const btnCls = `flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border outline-none transition focus-visible:ring-2 focus-visible:ring-[var(--st-500)] ${
    isDark
      ? 'border-white/15 bg-white/5 text-zinc-400 hover:bg-white/12 hover:text-zinc-200'
      : 'border-zinc-200 bg-zinc-50 text-zinc-600 hover:bg-zinc-100 hover:text-zinc-900'
  }`;

  async function writeClipboard(text, msg) {
    try {
      await navigator.clipboard.writeText(text);
      setHint(msg);
      window.setTimeout(() => setHint(''), 2200);
    } catch {
      setHint('Não foi possível copiar. Copie manualmente.');
      window.setTimeout(() => setHint(''), 2600);
    }
  }

  // Em redes sociais usamos preferencialmente o endpoint OG do backend
  // (HTML estático com `og:title`, `og:image`, etc.) para o crawler render-
  // izar o cartão com foto e nome do produto. O humano que clica no link
  // partilhado é redireccionado para a SPA pelo próprio endpoint.
  function shareLinkForCrawlers() {
    const og = normalizeShareUrlForSocial(shareOgUrl || '');
    if (og) return og;
    return normalizeShareUrlForSocial(shareUrl);
  }

  async function openFb() {
    const absolute = shareLinkForCrawlers();
    if (!absolute) return;
    if (!isPubliclyReachableUrl(absolute)) {
      // O Facebook descarta a URL silenciosamente quando é localhost/IP privado.
      // Copiamos para o clipboard e abrimos o composer mesmo assim — o user cola.
      try {
        await navigator.clipboard.writeText(`${shareTitle}\n${absolute}`);
      } catch {
        /* sem permissão de clipboard — segue sem cópia */
      }
      setHint('URL local — link copiado para colar no Facebook (preview real só em URL pública HTTPS).');
      window.setTimeout(() => setHint(''), 6000);
    }
    const href = buildFacebookShareUrl(absolute, shareTitle);
    window.open(href, '_blank', 'noopener,noreferrer');
  }

  async function openX() {
    const absolute = shareLinkForCrawlers();
    if (!absolute) return;
    if (!isPubliclyReachableUrl(absolute)) {
      try {
        await navigator.clipboard.writeText(`${shareTitle}\n${absolute}`);
      } catch {
        /* sem permissão — abre na mesma */
      }
      setHint('URL local — link copiado para colar no X (preview real só em URL pública HTTPS).');
      window.setTimeout(() => setHint(''), 6000);
    }
    const href = buildXShareUrl(absolute, shareTitle);
    window.open(href, '_blank', 'noopener,noreferrer');
  }

  function openWa() {
    // O WhatsApp também lê Open Graph do link partilhado para mostrar o
    // cartão com foto + título. Preferimos o endpoint OG quando disponível.
    const link = shareOgUrl ? normalizeShareUrlForSocial(shareOgUrl) : shareUrl;
    const t = encodeURIComponent(`${shareTitle}\n${link}`);
    window.open(`https://wa.me/?text=${t}`, '_blank', 'noopener,noreferrer');
  }

  async function shareInstagram() {
    if (typeof navigator !== 'undefined' && typeof navigator.share === 'function') {
      try {
        await navigator.share({ title: shareTitle, text: `${shareTitle}\n`, url: shareUrl });
        return;
      } catch {
        /* abortou ou falhou — cópia */
      }
    }
    await writeClipboard(shareUrl, 'Copiado — cole no Instagram (post ou Stories)');
  }

  if (!shareUrl) return null;

  return (
    <div
      className={`flex flex-col gap-2 border-t pt-3 ${isDark ? 'border-white/10' : 'border-zinc-200'}`}
    >
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        <span className={labelCls}>Compartilhar produto</span>
        <div className="flex items-center gap-2 sm:gap-2.5">
          <button
            type="button"
            className={btnCls}
            aria-label="Copiar link do produto"
            title="Copiar link"
            onClick={() => writeClipboard(shareUrl, 'Link copiado!')}
          >
            <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="1.85" aria-hidden>
              <rect x="9" y="9" width="11" height="11" rx="2" ry="2" />
              <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" strokeLinecap="round" />
            </svg>
          </button>
          {STORE_SHARE_MODAL_FB_IG_ENABLED ? (
            <>
              <button type="button" className={btnCls} aria-label="Compartilhar no Facebook" title="Facebook" onClick={openFb}>
                <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor" aria-hidden>
                  <path d="M22 12a10 10 0 10-11.5 9.95v-7.05H7.9V12h2.65V9.74c0-2.61 1.58-4.05 3.93-4.05 1.13 0 2.31.2 2.31.2v2.54h-1.3c-1.28 0-1.68.8-1.68 1.61V12h2.85l-.45 2.9h-2.4v7.05A10 10 0 0022 12z" />
                </svg>
              </button>
              <button type="button" className={btnCls} aria-label="Compartilhar no X (Twitter)" title="X (antigo Twitter)" onClick={openX}>
                <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor" aria-hidden>
                  <path d="M18.244 2H21.5l-7.512 8.583L23 22h-6.844l-5.36-7.012L4.6 22H1.343l8.046-9.196L1 2h7.012l4.834 6.4L18.244 2zm-2.4 18.05h1.875L7.226 3.844H5.219L15.844 20.05z" />
                </svg>
              </button>
              <button
                type="button"
                className={btnCls}
                aria-label="Compartilhar no Instagram ou copiar link"
                title="Instagram (abre partilhar do sistema ou copia)"
                onClick={() => shareInstagram()}
              >
                <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="1.85" aria-hidden>
                  <rect x="3" y="3" width="18" height="18" rx="5" ry="5" />
                  <circle cx="12" cy="12" r="3.8" />
                  <circle cx="17.2" cy="6.8" r="1.2" fill="currentColor" stroke="none" />
                </svg>
              </button>
            </>
          ) : null}
          <button type="button" className={btnCls} aria-label="Enviar por WhatsApp" title="WhatsApp" onClick={openWa}>
            <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor" aria-hidden>
              <path d="M20 3.9A10 10 0 0 0 4.4 16.1L3 21l5-1.3A10 10 0 1 0 20 3.9zm-8.1 16.2c-1.7 0-3.3-.5-4.7-1.4l-.3-.2-2.8.7.7-2.8-.2-.3A8.2 8.2 0 1 1 11.9 20z" />
              <path d="M16.7 13.9c-.3-.1-1.7-.8-2-.9-.3-.1-.5-.1-.7.1l-.6.8c-.2.2-.3.2-.6.1-1.7-.8-2.8-2.4-2.9-2.6-.2-.3 0-.4.1-.6l.5-.6.2-.4c.1-.2 0-.4 0-.5l-.9-2.1c-.2-.4-.4-.4-.6-.4h-.6c-.2 0-.5.1-.7.3-.2.2-.9.9-.9 2.1s.9 2.4 1 2.6c.1.2 1.8 3 4.5 4.1 2.7 1.2 2.7.8 3.2.8s1.7-.7 1.9-1.3c.2-.6.2-1.2.1-1.3 0-.1-.2-.2-.5-.3z" />
            </svg>
          </button>
        </div>
      </div>
      {hint ? (
        <p className={`text-[11px] font-medium ${isDark ? 'text-[var(--st-400)]' : 'text-[var(--st-700)]'}`} role="status">
          {hint}
        </p>
      ) : null}
    </div>
  );
}

/** Chip «Ver vídeo» sobre a foto do modal (ícone + texto). */
function ModalVerVideoChip({ onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex w-full items-center gap-3 rounded-xl border border-[rgb(var(--st-rgb)/0.45)] bg-[rgb(var(--st-rgb)/0.12)] px-3 py-2.5 text-left text-zinc-900 shadow-[0_8px_22px_rgba(0,0,0,0.1)] transition hover:bg-[rgb(var(--st-rgb)/0.2)] hover:shadow-[0_12px_28px_rgba(0,0,0,0.14)] active:scale-[0.99] dark:text-zinc-100 dark:shadow-[0_10px_26px_rgba(0,0,0,0.35)]"
      aria-label="Ver vídeo do produto"
    >
      <span
        className="relative flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[rgb(var(--st-rgb)/0.92)] shadow-[0_4px_14px_rgb(var(--st-rgb)/0.35)] ring-2 ring-white/35"
        aria-hidden
      >
        <svg viewBox="0 0 24 24" fill="none" className="ml-0.5 h-5 w-5 text-white" aria-hidden>
          <path
            d="M9.5 7.2v9.6l7.2-4.8L9.5 7.2z"
            fill="currentColor"
            stroke="currentColor"
            strokeWidth="1.2"
            strokeLinejoin="round"
          />
        </svg>
      </span>
      <span className="text-[12px] font-bold uppercase tracking-[0.12em] text-zinc-900 dark:text-zinc-50">Ver Vídeo</span>
    </button>
  );
}

/** Modal fotos / vídeo (referência tipo vitrine grande + miniaturas). */
function ProductMediaPreviewModal({ value, onClose }) {
  const { scheme } = useTheme();
  const isDark = scheme === 'dark';
  const [tab, setTab] = useState('imagens');
  const [sel, setSel] = useState(0);
  const [zoomIdx, setZoomIdx] = useState(0);

  const imageUrls = Array.isArray(value?.imageUrls) ? value.imageUrls : [];
  const title = typeof value?.title === 'string' ? value.title : 'Produto';
  const yt = value?.youtubeUrl;
  const ytOk = yt && youtubeEmbedSrc(yt);
  const shareUrl = typeof value?.shareUrl === 'string' ? value.shareUrl.trim() : '';
  const shareOgUrl = typeof value?.shareOgUrl === 'string' ? value.shareOgUrl.trim() : '';
  const shareTitle =
    typeof value?.shareTitle === 'string' && value.shareTitle.trim() !== ''
      ? value.shareTitle.trim()
      : title;

  useEffect(() => {
    if (!value) return;
    let startTab = value.startTab === 'video' && ytOk ? 'video' : 'imagens';
    if (startTab === 'imagens' && imageUrls.length === 0 && ytOk) startTab = 'video';
    setTab(startTab);
    const si = typeof value.startIndex === 'number' && !Number.isNaN(value.startIndex) ? value.startIndex : 0;
    setSel(Math.min(Math.max(0, si), Math.max(0, imageUrls.length - 1)));
    setZoomIdx(0);
  }, [value, ytOk, imageUrls.length]);

  /** Começa em 1 (encaixada); + aumenta para ver detalhes (tamanho de layout real, não transform — evita cortar ao fazer scroll). */
  const scales = useMemo(() => [1, 1.33, 1.75], []);
  const scale = scales[Math.min(Math.max(0, zoomIdx), scales.length - 1)];
  const previewImgMaxHeight = `min(${50 * scale}svh, ${540 * scale}px)`;

  if (!value) return null;

  const showVideoTab = Boolean(ytOk);
  const thumbSrc =
    sel >= 0 && sel < imageUrls.length ? resolveApiMediaUrl(imageUrls[sel]) : '';

  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center p-3 sm:p-5"
      role="dialog"
      aria-modal="true"
      aria-labelledby="media-preview-title"
    >
      <button
        type="button"
        className={`absolute inset-0 backdrop-blur-[3px] ${isDark ? 'bg-black/85' : 'bg-black/35'}`}
        aria-label="Fechar"
        onClick={onClose}
      />
      <div
        className={`relative z-[1] flex max-h-[min(94vh,920px)] w-full max-w-[min(98vw,1100px)] flex-col gap-3 overflow-hidden rounded-2xl shadow-2xl ${
          isDark ? 'border border-white/15 bg-[#151515]' : 'border border-zinc-200 bg-white'
        }`}
      >
        <header className={`flex shrink-0 items-center gap-6 px-4 pb-3 pt-3 sm:px-5 ${isDark ? 'border-b border-white/10' : 'border-b border-zinc-200'}`}>
          <div className="flex gap-8 text-[11px] font-bold uppercase tracking-[0.12em] text-zinc-500">
            {showVideoTab ? (
              <button
                type="button"
                onClick={() => setTab('video')}
                className={`border-b-2 pb-2 transition ${
                  tab === 'video'
                    ? 'border-[rgb(var(--st-rgb))] text-[rgb(var(--st-rgb))]'
                    : isDark
                      ? 'border-transparent hover:text-zinc-200'
                      : 'border-transparent hover:text-zinc-700'
                }`}
              >
                Vídeos
              </button>
            ) : null}
            <button
              type="button"
              onClick={() => setTab('imagens')}
              disabled={imageUrls.length === 0}
              className={`border-b-2 pb-2 transition ${
                tab === 'imagens'
                  ? 'border-[rgb(var(--st-rgb))] text-[rgb(var(--st-rgb))]'
                  : isDark
                    ? 'border-transparent hover:text-zinc-200'
                    : 'border-transparent hover:text-zinc-700'
              } ${imageUrls.length === 0 ? 'opacity-40' : ''}`}
            >
              Imagens
            </button>
          </div>
          <div className="ml-auto flex items-center gap-2">
            <button
              type="button"
              className={`rounded-full px-3 py-2 text-sm font-semibold transition ${isDark ? 'bg-white/10 text-white hover:bg-white/20' : 'bg-zinc-100 text-zinc-700 hover:bg-zinc-200'}`}
              onClick={onClose}
              aria-label="Fechar"
            >
              ✕
            </button>
          </div>
        </header>

        {tab === 'video' && showVideoTab ? (
          <div className="flex min-h-[min(60vh,520px)] shrink-0 flex-col px-4 pb-5 pt-2 sm:px-6">
            <div className="flex flex-1 items-center justify-center">
              <div className="aspect-video w-full max-w-4xl overflow-hidden rounded-xl border border-white/15 bg-black">
                <iframe
                  src={youtubeEmbedSrc(yt) || ''}
                  title={`Vídeo: ${title}`}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                  className="h-full min-h-[200px] w-full border-0"
                />
              </div>
            </div>
            <ProductModalShareRow shareUrl={shareUrl} shareOgUrl={shareOgUrl} shareTitle={shareTitle} isDark={isDark} />
          </div>
        ) : (
          <div className="flex min-h-0 flex-1 flex-col gap-3 lg:flex-row lg:gap-5 lg:overflow-hidden lg:pb-5 lg:pr-5 lg:pt-1">
            <div className="flex min-h-0 min-w-0 flex-1 flex-col gap-3 px-4 sm:px-5 lg:flex-[1.15] lg:self-stretch lg:px-0 lg:pl-5">
              <div className={`flex items-center gap-3 ${isDark ? 'text-white/90' : 'text-zinc-800'}`}>
                <span className="text-xs uppercase tracking-wide text-zinc-500">Zoom</span>
                <button
                  type="button"
                  disabled={zoomIdx <= 0}
                  className={`flex h-9 w-9 items-center justify-center rounded-lg text-lg transition disabled:cursor-not-allowed disabled:opacity-35 ${isDark ? 'bg-white/10 hover:bg-white/18' : 'bg-zinc-200 hover:bg-zinc-300'}`}
                  aria-label="Afastar zoom"
                  onClick={() => setZoomIdx((z) => Math.max(0, z - 1))}
                >
                  −
                </button>
                <button
                  type="button"
                  disabled={zoomIdx >= scales.length - 1}
                  className={`flex h-9 w-9 items-center justify-center rounded-lg text-lg transition disabled:cursor-not-allowed disabled:opacity-35 ${isDark ? 'bg-white/10 hover:bg-white/18' : 'bg-zinc-200 hover:bg-zinc-300'}`}
                  aria-label="Aproximar zoom"
                  onClick={() => setZoomIdx((z) => Math.min(scales.length - 1, z + 1))}
                >
                  +
                </button>
              </div>
              <div
                className={`relative flex min-h-[11rem] max-h-[min(56vh,640px)] flex-1 overflow-auto rounded-xl border ${isDark ? 'border-white/10 bg-black/40' : 'border-zinc-200 bg-zinc-50'}`}
              >
                {thumbSrc ? (
                  <div className="m-auto flex min-h-0 min-w-0 w-full touch-pan-x touch-pan-y items-center justify-center px-3 py-3 sm:p-4">
                    <ResilientImage
                      src={thumbSrc}
                      alt=""
                      decoding="async"
                      loading="eager"
                      retries={2}
                      style={{
                        maxHeight: previewImgMaxHeight,
                        width: 'auto',
                        height: 'auto',
                        maxWidth: '100%',
                      }}
                      className="box-border object-contain"
                      fallback={
                        <p className="px-3 text-center text-sm font-medium text-zinc-300">
                          Nao foi possivel carregar esta imagem agora. Tente novamente em instantes.
                        </p>
                      }
                    />
                  </div>
                ) : (
                  <div className="relative flex min-h-[min(50vh,280px)] w-full flex-col items-center justify-center p-6">
                    <p className={`max-w-sm text-center text-sm ${isDark ? 'text-zinc-400' : 'text-zinc-600'}`}>
                      Sem imagens disponíveis.
                    </p>
                  </div>
                )}
              </div>
            </div>
            <aside className={`flex w-full shrink-0 flex-col gap-3 px-4 pb-5 lg:w-[260px] lg:border-l lg:border-t-0 lg:px-0 lg:pb-0 ${isDark ? 'border-white/10' : 'border-zinc-200'}`}>
              <p
                id="media-preview-title"
                className={`line-clamp-4 px-1 text-left text-[13px] font-semibold leading-snug sm:text-sm ${isDark ? 'text-zinc-100' : 'text-zinc-900'}`}
              >
                {title}
              </p>
              {imageUrls.length > 0 ? (
                <div className="grid grid-cols-4 gap-2 lg:gap-2.5">
                  {imageUrls.map((u, i) => (
                    <button
                      key={`thumb-${u}-${i}`}
                      type="button"
                      aria-label={`Foto ${i + 1}`}
                      aria-current={i === sel ? 'true' : undefined}
                      onClick={() => {
                        setSel(i);
                        setZoomIdx(0);
                        setTab('imagens');
                      }}
                      className={`relative overflow-hidden rounded-md border p-0.5 outline-none transition hover:opacity-95 focus-visible:ring-2 focus-visible:ring-[var(--st-400)] ${isDark ? 'bg-zinc-950/80' : 'bg-zinc-100'} ${i === sel ? 'border-[rgb(var(--st-rgb))] ring-1 ring-[rgb(var(--st-rgb)/0.55)]' : isDark ? 'border-white/15' : 'border-zinc-300'}`}
                    >
                      <ResilientImage
                        src={resolveApiMediaUrl(u)}
                        alt=""
                        className="aspect-square w-full object-cover"
                        loading="lazy"
                        retries={2}
                        fallback={<span className={`block aspect-square w-full ${isDark ? 'bg-zinc-900' : 'bg-zinc-200'}`} />}
                      />
                    </button>
                  ))}
                </div>
              ) : null}
              {showVideoTab ? <ModalVerVideoChip onClick={() => setTab('video')} /> : null}
              <ProductModalShareRow shareUrl={shareUrl} shareOgUrl={shareOgUrl} shareTitle={shareTitle} isDark={isDark} />
            </aside>
          </div>
        )}
      </div>
    </div>
  );
}

/** Garante lista de strings para o combobox de formas de pagamento. */
function coerceTenantCheckoutPaymentMethods(tenant) {
  if (!tenant || typeof tenant !== 'object') return [];
  const raw = tenant.checkout_payment_methods;
  if (Array.isArray(raw)) {
    return raw.map((x) => String(x).trim()).filter(Boolean);
  }
  if (typeof raw === 'string' && raw.trim()) {
    try {
      const p = JSON.parse(raw);
      return Array.isArray(p) ? p.map((x) => String(x).trim()).filter(Boolean) : [];
    } catch {
      return [];
    }
  }
  return [];
}

/** Catálogo sempre com `products`/`groups` iteráveis para a UI. */
function normalizeCatalogPayload(payload) {
  if (!payload || typeof payload !== 'object') {
    return {
      tenant: null,
      products: [],
      sections: [],
      groups: [],
      section_summary: [],
      catalog_product_total: null,
      catalog_home_vitrine: false,
    };
  }
  const products = Array.isArray(payload.products) ? payload.products : [];
  const section_summary = Array.isArray(payload.section_summary)
    ? payload.section_summary.filter((x) => x && typeof x === 'object' && typeof x.secao === 'string')
    : [];
  const catalog_product_total =
    typeof payload.catalog_product_total === 'number' && !Number.isNaN(payload.catalog_product_total)
      ? payload.catalog_product_total
      : null;
  const tenantNorm =
    payload.tenant && typeof payload.tenant === 'object'
      ? {
          ...payload.tenant,
          checkout_payment_methods: coerceTenantCheckoutPaymentMethods(payload.tenant),
        }
      : payload.tenant;
  return {
    ...payload,
    tenant: tenantNorm,
    products,
    sections: asStringList(payload.sections),
    groups: asStringList(payload.groups),
    section_summary,
    catalog_product_total,
    catalog_home_vitrine: Boolean(payload.catalog_home_vitrine),
  };
}

function BagIcon(props) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" aria-hidden {...props}>
      <path d="M6 8V6a4 4 0 118 0v2" strokeLinecap="round" />
      <path d="M4 8h16l-1 12H5L4 8z" strokeLinejoin="round" />
    </svg>
  );
}

function TrashIcon(props) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
      {...props}
    >
      <path d="M3 6h18" />
      <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6h14z" />
      <path d="M10 11v6M14 11v6" />
    </svg>
  );
}

function SearchIcon(props) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden {...props}>
      <circle cx="11" cy="11" r="7" />
      <path d="M20 20l-3.5-3.5" strokeLinecap="round" />
    </svg>
  );
}

function LockIcon(props) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden {...props}>
      <rect x="5" y="10" width="14" height="10" rx="2" />
      <path d="M8 10V7a4 4 0 118 0v3" strokeLinecap="round" />
    </svg>
  );
}

function SunIcon(props) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
      {...props}
    >
      <circle cx="12" cy="12" r="4" />
      <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M6.34 17.66l-1.41 1.41M19.07 4.93l-1.41 1.41" />
    </svg>
  );
}

function MoonIcon(props) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
      {...props}
    >
      <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
    </svg>
  );
}

/** Porta de saída — gradientes alinhados ao guia da marca Ominia (roxo/ciano + verdes «IA»). */
function OminiaExitDoorIcon({ className = '', ...props }) {
  const uid = useId().replace(/:/g, '');
  const gFrame = `ominia-door-frame-${uid}`;
  const gPanel = `ominia-door-panel-${uid}`;
  const gKnob = `ominia-door-knob-${uid}`;
  return (
    <svg viewBox="0 0 24 24" fill="none" className={className} aria-hidden {...props}>
      <defs>
        <linearGradient id={gFrame} x1="4" y1="3" x2="20" y2="21" gradientUnits="userSpaceOnUse">
          <stop stopColor="hsl(var(--ominia-gradient-start))" />
          <stop offset="0.45" stopColor="hsl(var(--ominia-cyan))" />
          <stop offset="1" stopColor="hsl(var(--ominia-ia-mid))" />
        </linearGradient>
        <linearGradient id={gPanel} x1="8" y1="19" x2="16" y2="6" gradientUnits="userSpaceOnUse">
          <stop stopColor="hsl(var(--ominia-ia-start))" />
          <stop offset="1" stopColor="hsl(var(--ominia-ia-mid))" />
        </linearGradient>
        <radialGradient
          id={gKnob}
          cx="0"
          cy="0"
          r="1"
          gradientUnits="userSpaceOnUse"
          gradientTransform="translate(15.25 11.75) rotate(52) scale(2.4)"
        >
          <stop stopColor="hsl(var(--ominia-cyan))" />
          <stop offset="1" stopColor="hsl(var(--ominia-gradient-start))" />
        </radialGradient>
      </defs>
      {/* Moldura / vão */}
      <rect
        x="5.15"
        y="2.85"
        width="13.7"
        height="18.3"
        rx="2.35"
        ry="2.35"
        stroke={`url(#${gFrame})`}
        strokeWidth="1.65"
      />
      {/* Folha da porta */}
      <rect
        x="7.65"
        y="5.35"
        width="8.7"
        height="13.35"
        rx="1.15"
        ry="1.15"
        fill={`url(#${gPanel})`}
        fillOpacity="0.28"
        stroke={`url(#${gFrame})`}
        strokeWidth="1.05"
        strokeOpacity="0.9"
      />
      {/* Friso superior (detalhe) */}
      <path
        d="M9.2 7.85h5.6"
        stroke={`url(#${gFrame})`}
        strokeWidth="0.85"
        strokeLinecap="round"
        opacity="0.75"
      />
      {/* Soleira */}
      <path
        d="M4.35 20.35h15.3"
        stroke={`url(#${gFrame})`}
        strokeWidth="1.35"
        strokeLinecap="round"
        opacity="0.88"
      />
      {/* Maçaneta */}
      <circle cx="15.15" cy="11.9" r="1.38" fill={`url(#${gKnob})`} />
      <circle cx="15.15" cy="11.9" r="1.38" fill="none" stroke="hsl(var(--ominia-ia-start))" strokeOpacity="0.35" strokeWidth="0.4" />
    </svg>
  );
}

function WhatsAppIcon(props) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden {...props}>
      <path d="M20 3.9A10 10 0 0 0 4.4 16.1L3 21l5-1.3A10 10 0 1 0 20 3.9zm-8.1 16.2c-1.7 0-3.3-.5-4.7-1.4l-.3-.2-2.8.7.7-2.8-.2-.3A8.2 8.2 0 1 1 11.9 20z" />
      <path d="M16.7 13.9c-.3-.1-1.7-.8-2-.9-.3-.1-.5-.1-.7.1l-.6.8c-.2.2-.3.2-.6.1-1.7-.8-2.8-2.4-2.9-2.6-.2-.3 0-.4.1-.6l.5-.6.2-.4c.1-.2 0-.4 0-.5l-.9-2.1c-.2-.4-.4-.4-.6-.4h-.6c-.2 0-.5.1-.7.3-.2.2-.9.9-.9 2.1s.9 2.4 1 2.6c.1.2 1.8 3 4.5 4.1 2.7 1.2 2.7.8 3.2.8s1.7-.7 1.9-1.3c.2-.6.2-1.2.1-1.3 0-.1-.2-.2-.5-.3z" />
    </svg>
  );
}

function GoogleMapsIcon(props) {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden {...props}>
      <path d="M12 22s7-6 7-12a7 7 0 1 0-14 0c0 6 7 12 7 12z" fill="#34A853" />
      <path d="M12 14.6a4.6 4.6 0 1 0 0-9.2 4.6 4.6 0 0 0 0 9.2z" fill="#FBBC05" />
      <path d="M12 12.8a2.8 2.8 0 1 0 0-5.6 2.8 2.8 0 0 0 0 5.6z" fill="#4285F4" />
      <path d="M12 22s5.1-4.4 6.6-9H5.4c1.5 4.6 6.6 9 6.6 9z" fill="#EA4335" opacity=".9" />
    </svg>
  );
}

function InstagramIcon(props) {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden {...props}>
      <rect x="2.5" y="2.5" width="19" height="19" rx="6" fill="url(#igGrad)" />
      <circle cx="12" cy="12" r="4.2" stroke="white" strokeWidth="2" />
      <circle cx="17.3" cy="6.8" r="1.2" fill="white" />
      <defs>
        <linearGradient id="igGrad" x1="3" y1="21" x2="21" y2="3" gradientUnits="userSpaceOnUse">
          <stop stopColor="#F58529" />
          <stop offset=".35" stopColor="#DD2A7B" />
          <stop offset=".68" stopColor="#8134AF" />
          <stop offset="1" stopColor="#515BD4" />
        </linearGradient>
      </defs>
    </svg>
  );
}

function FacebookIcon(props) {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden {...props}>
      <rect x="2" y="2" width="20" height="20" rx="10" fill="#1877F2" />
      <path d="M13.5 8.3h2V6h-2.3c-2.6 0-3.4 1.7-3.4 3.3V11H8v2.4h1.8V18h2.7v-4.6h2.2l.4-2.4h-2.6V9.5c0-.7.3-1.2 1-1.2z" fill="white" />
    </svg>
  );
}

function YoutubeIcon(props) {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden {...props}>
      <rect x="2.2" y="5.3" width="19.6" height="13.4" rx="4.3" fill="#FF0000" />
      <path d="M10 9.3l5.6 2.7L10 14.7V9.3z" fill="white" />
    </svg>
  );
}

/** Estado activo do filtro de categoria — alinha ao laranja da loja em claro e escuro. */
const catActiveCls =
  'bg-[var(--st-600)] font-semibold text-white shadow-sm shadow-[0_4px_14px_-4px_rgb(var(--st-rgb)/0.45)] dark:bg-[var(--st-600)] dark:shadow-[var(--st-sh-sort)]';

function SuccessIllustration() {
  return (
    <div className="mx-auto mb-6 flex h-28 w-28 items-center justify-center" aria-hidden>
      <svg viewBox="0 0 120 120" className="h-full w-full text-blue-600 dark:text-sky-400">
        <path
          d="M58 92c0-8 12-26 26-38 10-8 16-22 14-38-14 4-24 22-34 42-10-16-26-42-54-52 18 28 32 72 54 94 4-8 6-8 6-8z"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinejoin="round"
          opacity={0.35}
        />
        <path
          d="M52 58l-10-10-8 8M68 58l28-28"
          fill="none"
          stroke="currentColor"
          strokeWidth="4"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <circle cx="86" cy="34" r="18" fill="currentColor" className="text-blue-600 dark:text-sky-500" />
        <path
          d="M79 34l6 6 12-14"
          fill="none"
          stroke="white"
          strokeWidth="3"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M42 74c-14-28-34-54-54-62 44 54 62 118 94 154 4-52-26-126-74-218"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          opacity={0.25}
        />
      </svg>
    </div>
  );
}

const STORE_CART_LS_PREFIX = 'ominia.storeCart.v1.';
/**
 * Pedido MP em aberto: guardamos orderId + productIds para remover só esses
 * itens da sacola quando o pagamento online for confirmado (URL do MP,
 * webhook ou reconciliação no painel).
 */
const STORE_MP_PENDING_LS_PREFIX = 'ominia.mpPending.v1.';

/** Parâmetros que o Mercado Pago acrescenta às `back_urls`. */
function mpReturnUrlSaysApproved(searchParams) {
  const st = (searchParams.get('status') || '').trim().toLowerCase();
  const col = (searchParams.get('collection_status') || '').trim().toLowerCase();
  return st === 'approved' || col === 'approved';
}

function readMpPendingOrder(slug) {
  if (typeof window === 'undefined' || !slug) return null;
  try {
    const raw = window.localStorage.getItem(`${STORE_MP_PENDING_LS_PREFIX}${slug}`);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    const productIds = Array.isArray(parsed.productIds)
      ? parsed.productIds.map((x) => String(x)).filter(Boolean)
      : [];
    return {
      orderId: Number(parsed.orderId) || null,
      productIds,
      savedAt: Number(parsed.savedAt) || 0,
    };
  } catch {
    return null;
  }
}

function clearMpPendingOrder(slug) {
  if (typeof window === 'undefined' || !slug) return;
  try {
    window.localStorage.removeItem(`${STORE_MP_PENDING_LS_PREFIX}${slug}`);
  } catch {
    /* ignore */
  }
}

function writeMpPendingOrder(slug, orderId, productIds) {
  if (typeof window === 'undefined' || !slug) return;
  try {
    const safeIds = Array.isArray(productIds)
      ? productIds.map((x) => String(x)).filter(Boolean)
      : [];
    window.localStorage.setItem(
      `${STORE_MP_PENDING_LS_PREFIX}${slug}`,
      JSON.stringify({ orderId: Number(orderId) || null, productIds: safeIds, savedAt: Date.now() }),
    );
  } catch {
    /* ignore */
  }
}

function readStoredCart(slugKey) {
  if (!slugKey || typeof window === 'undefined') return {};
  try {
    const raw = window.localStorage.getItem(`${STORE_CART_LS_PREFIX}${slugKey}`);
    if (!raw) return {};
    const obj = JSON.parse(raw);
    if (!obj || typeof obj !== 'object' || Array.isArray(obj)) return {};
    const next = {};
    for (const [k, v] of Object.entries(obj)) {
      const id = String(k);
      const q = Number(v);
      if (!/^\d+$/.test(id) || !Number.isFinite(q) || q <= 0) continue;
      next[id] = Math.min(Math.floor(q), 99999);
    }
    return next;
  } catch {
    return {};
  }
}

function clearCartLocalStorage(slugKey) {
  if (!slugKey || typeof window === 'undefined') return;
  try {
    window.localStorage.removeItem(`${STORE_CART_LS_PREFIX}${slugKey}`);
  } catch {
    /* ignore */
  }
}

/** Normaliza resposta `items` da API da sacola. */
function normalizeCartRemote(raw) {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return {};
  const out = {};
  for (const [k, v] of Object.entries(raw)) {
    const id = String(k).replace(/\D/g, '') || String(k);
    if (!/^\d+$/.test(id)) continue;
    const q = Math.floor(Number(v));
    if (!Number.isFinite(q) || q < 1) continue;
    out[id] = Math.min(q, 99999);
  }
  return out;
}

/** Preenche nome / telefone / e-mail (e endereço base) a partir da ficha do cliente logado. */
function mergeIdentityFromCustomer(prevCheckout, customer) {
  if (!prevCheckout || !customer || typeof customer !== 'object') return prevCheckout;
  const nome = String(customer.nome ?? '').trim();
  const telefone = String(customer.telefone ?? '').trim();
  const email = String(customer.email ?? '').trim();
  const endereco = String(customer.endereco ?? '').trim();
  return {
    ...prevCheckout,
    nome,
    telefone,
    email,
    endereco: endereco || prevCheckout.endereco,
  };
}

export function StorePage() {
  const { slug } = useParams();
  const slugKey = slug ? String(slug).toLowerCase() : '';
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { scheme, toggle } = useTheme();
  const [data, setData] = useState(null);
  /** Acumula linhas de produto por id ao longo dos pedidos `/catalog?secao=&search=` — o array
   * `data.products` só reflecte o filtro actual; sem isto a sacola e os totais «perdem» itens de outras secções. */
  const [catalogById, setCatalogById] = useState(() => ({}));
  const [filters, setFilters] = useState({ secao: '', search: '' });
  /** Inicia vazio: a hidratação do `localStorage` corre em `useLayoutEffect` (antes de persistir). */
  const [cart, setCart] = useState({});
  const [stockAlert, setStockAlert] = useState({ open: false, nome: '', available: 0 });
  /** Só gravamos no LS depois de ler o disco neste `slugKey` — evita apagar a chave no F5. */
  const cartLsHydratedRef = useRef(false);
  const cartServerSaveTimerRef = useRef(null);
  /** Último carrinho — o debounce do PUT lê isto para não enviar estado antigo (corrida com GET). */
  const cartRefForServerSave = useRef({});
  /** Respostas GET `/customer/cart` fora de ordem são ignoradas. */
  const cartRemoteFetchGenRef = useRef(0);
  /** Evita limpar a sacola duas vezes (retorno MP + poll). */
  const storeTrackingRef = useRef({ notifyAddToCart: () => {}, notifyPurchase: () => {} });
  const mpCartFinalizedRef = useRef(false);
  const mpCartPollTimerRef = useRef(null);
  const [checkout, setCheckout] = useState({
    nome: '',
    telefone: '',
    email: '',
    cep: '',
    endereco: '',
    enderecoNumero: '',
    enderecoComplemento: '',
    observacoes: '',
    /** Valor do combobox — uma das opções configuradas pela loja (já normalizadas na API). */
    paymentMethod: '',
  });
  /**
   * Mapa `pid -> false` apenas para itens *desmarcados* na sacola. Itens
   * ausentes do mapa são tratados como selecionados por defeito (UX nova
   * desejada: cliente pode desmarcar itens para deixar "para depois" sem ter
   * que os remover).
   * @type {[Record<string, boolean>, Function]}
   */
  const [cartSelection, setCartSelection] = useState({});
  const [checkoutFulfillmentMode, setCheckoutFulfillmentMode] = useState(CHECKOUT_FULFILL_PICKUP);
  const [cepLookupBusy, setCepLookupBusy] = useState(false);
  const [cepLookupMsg, setCepLookupMsg] = useState('');
  const [cepLookupError, setCepLookupError] = useState('');
  const [deliveryFeeQuote, setDeliveryFeeQuote] = useState(null);
  const [deliveryFeeError, setDeliveryFeeError] = useState('');
  const [deliveryFeeBusy, setDeliveryFeeBusy] = useState(false);
  const [checkoutLookupBusy, setCheckoutLookupBusy] = useState(false);
  const [checkoutLookupMsg, setCheckoutLookupMsg] = useState('');
  const [customerToken, setCustomerToken] = useState('');
  const ageStorageKey = slug ? `ominia.ageOk.v1.${slug}` : '';
  const [ageConfirmed, setAgeConfirmed] = useState(() => {
    if (typeof window === 'undefined' || !slug) return false;
    try {
      return sessionStorage.getItem(`ominia.ageOk.v1.${slug}`) === '1';
    } catch {
      return false;
    }
  });
  const [customerSession, setCustomerSession] = useState(null);
  /**
   * `true` quando o primeiro `loadCustomerSession()` terminou (com ou sem
   * sessão). Usado pelo handler de retorno do Mercado Pago para não mexer no
   * carrinho antes do GET `/customer/cart` ter dado a sua palavra (evita que
   * um cleanup local seja sobrescrito por dados antigos do servidor).
   */
  const [customerSessionResolved, setCustomerSessionResolved] = useState(false);
  /**
   * `true` quando o primeiro GET `/customer/cart` já aplicou um `setCart`.
   * O cleanup pós-retorno do MP precisa esperar isto para não ter uma corrida
   * com o GET inicial (que repopularia os items recém-removidos).
   */
  const [cartServerInitialLoaded, setCartServerInitialLoaded] = useState(false);
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [authMode, setAuthMode] = useState('login');
  const [authError, setAuthError] = useState('');
  const [authBusy, setAuthBusy] = useState(false);
  const [showLoginPassword, setShowLoginPassword] = useState(false);
  const [showRegisterPassword, setShowRegisterPassword] = useState(false);
  const [updateCustomerAddress, setUpdateCustomerAddress] = useState(false);
  const [authRegister, setAuthRegister] = useState({
    nome: '',
    whatsapp_country_code: '+55',
    whatsapp: '',
    email: '',
    cep: '',
    endereco: '',
    senha: '',
  });
  const [registerCepLookupBusy, setRegisterCepLookupBusy] = useState(false);
  const [registerCepLookupMsg, setRegisterCepLookupMsg] = useState('');
  const [registerCepLookupError, setRegisterCepLookupError] = useState('');
  const [registerLookupBusy, setRegisterLookupBusy] = useState(false);
  const [registerLookupMsg, setRegisterLookupMsg] = useState('');
  const [authLogin, setAuthLogin] = useState({ email: '', senha: '' });
  const [error, setError] = useState('');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [documentOrderOpen, setDocumentOrderOpen] = useState(false);
  const [catalogExportOpen, setCatalogExportOpen] = useState(false);
  const [sortAz, setSortAz] = useState(false);
  const [orderSuccess, setOrderSuccess] = useState(null);
  const [couponInput, setCouponInput] = useState('');
  const [couponPctValid, setCouponPctValid] = useState(null);
  const [couponNormApplied, setCouponNormApplied] = useState('');
  const [couponMsg, setCouponMsg] = useState('');
  const [couponBusy, setCouponBusy] = useState(false);
  /** Cliente digitou um cupom mas não clicou em «Aplicar» — perguntar antes de finalizar. */
  const [couponApplyPromptOpen, setCouponApplyPromptOpen] = useState(false);
  /** Modal de confirmação final (resumo do pedido) antes de enviar de facto. */
  const [confirmCheckoutOpen, setConfirmCheckoutOpen] = useState(false);
  const [confirmCheckoutBusy, setConfirmCheckoutBusy] = useState(false);
  const [mpReturnBanner, setMpReturnBanner] = useState('');
  /** `success` (verde) ou `warning` (âmbar) — controla o tom do banner pós-MP. */
  const [mpReturnBannerTone, setMpReturnBannerTone] = useState('success');
  /** Estado do modal foto + vídeo (YouTube). */
  const [mediaPreview, setMediaPreview] = useState(null);
  /** Rodapé informativo: no mobile/tablet (<lg) começa recolhido para liberar a vitrine. */
  const [footerInfoOpen, setFooterInfoOpen] = useState(false);
  /** Rodapé fixo minimizado pelo utilizador (barra fina); lembra-se por loja na sessão. */
  const [footerMinimized, setFooterMinimized] = useState(false);
  /** Moradas guardadas (API) — escolha no checkout. */
  const [savedDeliveryAddresses, setSavedDeliveryAddresses] = useState([]);

  useEffect(() => {
    cartRefForServerSave.current = cart;
  }, [cart]);

  const productFocusRaw = searchParams.get('p');
  useEffect(() => {
    if (!slug || !drawerOpen || !customerToken) return undefined;
    let cancelled = false;
    api(`/store/${encodeURIComponent(slug)}/customer/addresses`, {
      skipAuth: true,
      headers: { 'X-Customer-Token': customerToken },
    })
      .then((d) => {
        if (cancelled) return;
        setSavedDeliveryAddresses(Array.isArray(d?.addresses) ? d.addresses : []);
      })
      .catch(() => {
        if (cancelled) return;
        setSavedDeliveryAddresses([]);
      });
    return () => {
      cancelled = true;
    };
  }, [slug, drawerOpen, customerToken]);

  const pickupOnly = Number(data?.tenant?.pickup_only_mode || 0) === 1;
  const deliveryZonesEnabled = tenantDeliveryZonesEnabled(data?.tenant);

  useEffect(() => {
    if (pickupOnly) {
      setCheckoutFulfillmentMode(CHECKOUT_FULFILL_PICKUP);
      return;
    }
    setCheckoutFulfillmentMode((prev) => {
      if (checkoutFulfillmentIsNewAddress(prev)) return prev;
      if (checkoutFulfillmentIsSaved(prev)) {
        const id = checkoutFulfillmentParseSavedId(prev);
        if (savedDeliveryAddresses.some((a) => Number(a.id) === id)) return prev;
      }
      return checkoutFulfillmentDefaultMode(false, savedDeliveryAddresses);
    });
  }, [pickupOnly, savedDeliveryAddresses]);

  const applyCheckoutFulfillmentMode = useCallback(
    (mode) => {
      setCheckoutFulfillmentMode(mode);
      if (checkoutFulfillmentIsPickup(mode)) {
        setCheckout((p) => ({
          ...p,
          cep: '',
          endereco: '',
          enderecoNumero: '',
          enderecoComplemento: '',
        }));
        setUpdateCustomerAddress(false);
        setCepLookupMsg('');
        setCepLookupError('');
        return;
      }
      if (checkoutFulfillmentIsSaved(mode)) {
        const id = checkoutFulfillmentParseSavedId(mode);
        const hit = savedDeliveryAddresses.find((a) => Number(a.id) === id);
        const addrText = hit?.endereco ? String(hit.endereco) : '';
        const cepFromHit =
          hit?.cep && cepDigits(hit.cep).length === 8
            ? formatCep(hit.cep)
            : extractCepFromAddressText(addrText)
              ? formatCep(extractCepFromAddressText(addrText))
              : '';
        setCheckout((p) => ({
          ...p,
          cep: cepFromHit,
          endereco: addrText || p.endereco,
          enderecoNumero: hit?.endereco_numero ? String(hit.endereco_numero) : '',
          enderecoComplemento: hit?.endereco_complemento ? String(hit.endereco_complemento) : '',
        }));
        setUpdateCustomerAddress(false);
      }
    },
    [savedDeliveryAddresses],
  );

  const resolveCurrentCheckoutCepDigits = useCallback(() => {
    if (pickupOnly || checkoutFulfillmentIsPickup(checkoutFulfillmentMode)) return '';
    if (checkoutFulfillmentIsSaved(checkoutFulfillmentMode)) {
      const id = checkoutFulfillmentParseSavedId(checkoutFulfillmentMode);
      const hit = savedDeliveryAddresses.find((a) => Number(a.id) === id);
      return resolveCheckoutCepDigits({
        cep: checkout.cep,
        cep_column: hit?.cep,
        endereco: hit?.endereco || checkout.endereco,
      });
    }
    return resolveCheckoutCepDigits({
      cep: checkout.cep,
      endereco: checkout.endereco,
    });
  }, [
    pickupOnly,
    checkoutFulfillmentMode,
    checkout.cep,
    checkout.endereco,
    savedDeliveryAddresses,
  ]);

  const refreshDeliveryFeeQuote = useCallback(async () => {
    if (!deliveryZonesEnabled || pickupOnly || checkoutFulfillmentIsPickup(checkoutFulfillmentMode)) {
      setDeliveryFeeQuote(null);
      setDeliveryFeeError('');
      return;
    }
    const digits = resolveCurrentCheckoutCepDigits();
    if (digits.length !== 8) {
      setDeliveryFeeQuote(null);
      setDeliveryFeeError('');
      return;
    }
    setDeliveryFeeBusy(true);
    setDeliveryFeeError('');
    try {
      const res = await api(
        `/store/${encodeURIComponent(slug)}/delivery-fee?cep=${encodeURIComponent(digits)}`,
        { skipAuth: true },
      );
      if (res?.delivery_zones_enabled === false) {
        setDeliveryFeeQuote(null);
        return;
      }
      const row = res?.data;
      if (row && typeof row.fee === 'number') {
        setDeliveryFeeQuote({
          fee: Number(row.fee),
          zone: String(row.zone || ''),
          estimated_days: Number(row.estimated_days) || 1,
        });
      } else {
        setDeliveryFeeQuote(null);
      }
    } catch (e) {
      setDeliveryFeeQuote(null);
      const code = e?.data?.code;
      setDeliveryFeeError(
        e?.status === 422 || code === 'DELIVERY_OUT_OF_ZONE'
          ? 'Não realizamos entregas para este CEP.'
          : e?.message || 'Erro ao consultar taxa de entrega.',
      );
    } finally {
      setDeliveryFeeBusy(false);
    }
  }, [
    deliveryZonesEnabled,
    pickupOnly,
    checkoutFulfillmentMode,
    resolveCurrentCheckoutCepDigits,
    slug,
  ]);

  useEffect(() => {
    if (!deliveryZonesEnabled) return undefined;
    const digits = resolveCurrentCheckoutCepDigits();
    if (digits.length !== 8) {
      setDeliveryFeeQuote(null);
      setDeliveryFeeError('');
      return undefined;
    }
    const t = window.setTimeout(() => {
      void refreshDeliveryFeeQuote();
    }, 350);
    return () => window.clearTimeout(t);
  }, [
    deliveryZonesEnabled,
    checkout.cep,
    checkout.endereco,
    checkoutFulfillmentMode,
    resolveCurrentCheckoutCepDigits,
    refreshDeliveryFeeQuote,
  ]);

  const productFocusId = useMemo(() => {
    const s = productFocusRaw == null ? '' : String(productFocusRaw).trim();
    if (!/^\d+$/.test(s)) return null;
    const n = Number(s);
    return Number.isFinite(n) && n >= 1 ? n : null;
  }, [productFocusRaw]);

  /** Link partilhado ?p=id — só este produto na API e vista focada. */
  const isProductDeepLink = productFocusId != null;
  const productDeepLinkPreviewOpenedRef = useRef(false);
  const mainCatalogRef = useRef(null);
  const catalogBarRef = useRef(null);

  /** Evita `scrollIntoView` no `<main>`: o topo alinha ao viewport e fica por baixo do header sticky, cortando a pesquisa. */
  const scrollStoreMainBelowHeader = useCallback((behavior) => {
    if (typeof window === 'undefined') return;
    const mainEl = mainCatalogRef.current;
    if (!mainEl) return;
    const headerEl = document.querySelector('[data-ominia-store-header="1"]');
    const headerH =
      headerEl instanceof HTMLElement ? Math.ceil(headerEl.getBoundingClientRect().height) : 56;
    const gap = 14;
    const y = mainEl.getBoundingClientRect().top + window.scrollY - headerH - gap;
    window.scrollTo({ top: Math.max(0, y), behavior: behavior === 'smooth' ? 'smooth' : 'auto' });
  }, []);

  useEffect(() => {
    productDeepLinkPreviewOpenedRef.current = false;
  }, [productFocusId, slug]);

  /** Com ?p=id no URL, ignoramos secção/pesquisa no pedido ao catálogo (o backend já filtra por id). */
  const query = useMemo(() => {
    const qs = new URLSearchParams();
    if (productFocusId != null) {
      qs.set('p', String(productFocusId));
      const q = qs.toString();
      return q ? `?${q}` : '';
    }
    if (filters.secao) {
      const listOn =
        parseStoreCatalogList(data?.tenant?.theme_json).enabled &&
        !parseStoreCatalogLuxe(data?.tenant?.theme_json).enabled;
      if (!listOn) qs.set('secao', filters.secao);
    }
    if (filters.search) qs.set('search', filters.search);
    const q = qs.toString();
    return q ? `?${q}` : '';
  }, [filters.secao, filters.search, productFocusId, data?.tenant?.theme_json]);

  useEffect(() => {
    if (!isProductDeepLink) return;
    setFilters({ secao: '', search: '' });
  }, [isProductDeepLink, slug]);

  /** Scroll até ao card apenas quando NÃO há deep link (?p=id), que foca já um único produto. */
  const pHighlightScroll = searchParams.get('p');
  useEffect(() => {
    if (isProductDeepLink || !pHighlightScroll || typeof document === 'undefined') return undefined;
    const tid = window.setTimeout(() => {
      document.getElementById(`store-produto-${pHighlightScroll}`)?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }, 140);
    return () => window.clearTimeout(tid);
  }, [isProductDeepLink, pHighlightScroll, slug, query, data?.products]);

  /** Ao mudar de secção no catálogo, a página volta ao topo da área de produtos (evita ficar no fundo da lista anterior). */
  const prevCatalogSectionRef = useRef({ slugKey: slug ?? '', secao: filters.secao });
  useEffect(() => {
    if (typeof window === 'undefined' || isProductDeepLink) {
      prevCatalogSectionRef.current = { slugKey: slug ?? '', secao: filters.secao };
      return undefined;
    }
    const slugKey = slug ?? '';
    if (prevCatalogSectionRef.current.slugKey !== slugKey) {
      prevCatalogSectionRef.current = { slugKey, secao: filters.secao };
      return undefined;
    }
    if (prevCatalogSectionRef.current.secao === filters.secao) {
      return undefined;
    }
    prevCatalogSectionRef.current = { slugKey, secao: filters.secao };
    const id = window.requestAnimationFrame(() => {
      scrollStoreMainBelowHeader('auto');
    });
    return () => window.cancelAnimationFrame(id);
  }, [filters.secao, slug, isProductDeepLink, scrollStoreMainBelowHeader]);

  useEffect(() => {
    setCatalogById({});
  }, [slug]);

  /**
   * Hidrata sacola no `localStorage` só quando **não** há token de cliente na loja.
   * Com token: a sacola vem só do GET `/customer/cart` — nunca esvaziar aqui (evita corridas com F5 e re-renders da sessão).
   */
  useLayoutEffect(() => {
    cartLsHydratedRef.current = false;
    if (!slugKey) {
      setCart({});
      cartLsHydratedRef.current = true;
      return;
    }
    let tokenLs = '';
    try {
      tokenLs = String(window.localStorage.getItem(`ominia.customer.${slug}`) || '');
    } catch {
      tokenLs = '';
    }
    if (tokenLs) {
      cartLsHydratedRef.current = false;
      return;
    }
    setCart(readStoredCart(slugKey));
    cartLsHydratedRef.current = true;
  }, [slugKey, slug]);

  /**
   * Grava sacola no `localStorage` só para visitante (sem sessão de cliente na loja).
   */
  useEffect(() => {
    if (!slugKey || typeof window === 'undefined') return;
    if (!cartLsHydratedRef.current) return;
    if (customerSession) return;
    try {
      const keys = Object.keys(cart).filter((id) => Number(cart[id]) > 0);
      if (!keys.length) {
        /* Com carrinho vazio, apagar a chave — senão o F5 re-hidrata itens antigos (o fluxo só gravava LS com itens). */
        window.localStorage.removeItem(`${STORE_CART_LS_PREFIX}${slugKey}`);
        return;
      }
      const toSave = {};
      for (const id of keys) {
        toSave[id] = Math.min(Math.floor(Number(cart[id])), 99999);
      }
      window.localStorage.setItem(`${STORE_CART_LS_PREFIX}${slugKey}`, JSON.stringify(toSave));
    } catch {
      /* quota / modo privado */
    }
  }, [slugKey, cart, customerSession]);

  useEffect(() => {
    setFooterInfoOpen(false);
    try {
      if (!slug || typeof sessionStorage === 'undefined') {
        setFooterMinimized(false);
        return;
      }
      setFooterMinimized(sessionStorage.getItem(`ominia-store-footer-min:${slug}`) === '1');
    } catch {
      setFooterMinimized(false);
    }
  }, [slug]);

  useEffect(() => {
    const products = data?.products;
    if (!Array.isArray(products) || products.length === 0) return;
    setCatalogById((prev) => {
      const next = { ...prev };
      for (const p of products) {
        if (!p || p.id == null) continue;
        const id = Number(p.id);
        if (!Number.isFinite(id)) continue;
        next[id] = p;
      }
      return next;
    });
  }, [data?.products]);

  useEffect(() => {
    const products = Object.values(catalogById);
    if (!products.length) return;
    setCart((prev) => {
      const { cart: clamped, message, changed } = clampCartToStock(prev, products);
      if (!changed) return prev;
      return clamped;
    });
  }, [catalogById]);

  useEffect(() => {
    const key = `ominia.customer.${slug}`;
    const saved = typeof window !== 'undefined' ? window.localStorage.getItem(key) : '';
    if (saved) setCustomerToken(saved);
  }, [slug]);

  useEffect(() => {
    if (!slug || typeof window === 'undefined') return;
    try {
      setAgeConfirmed(sessionStorage.getItem(`ominia.ageOk.v1.${slug}`) === '1');
    } catch {
      setAgeConfirmed(false);
    }
  }, [slug]);

  useEffect(() => {
    const storageKey = `ominia.customer.${slug}`;
    const fromLs =
      typeof window !== 'undefined' && window.localStorage.getItem(storageKey)
        ? String(window.localStorage.getItem(storageKey))
        : '';
    const token = customerToken || fromLs;
    const opts = { skipAuth: true };
    if (token) {
      opts.headers = { 'X-Customer-Token': token };
    }
    api(`/store/${encodeURIComponent(slug)}/catalog${query}`, opts)
      .then((payload) => {
        setError('');
        setData(normalizeCatalogPayload(payload));
      })
      .catch((e) => setError(e.message));
  }, [slug, query, customerToken]);

  /** Ao abrir a sacola, atualiza dados da loja (formas de pagamento gravadas em /app/empresa). */
  useEffect(() => {
    if (!slug || !drawerOpen) return;
    const storageKey = `ominia.customer.${slug}`;
    const fromLs =
      typeof window !== 'undefined' && window.localStorage.getItem(storageKey)
        ? String(window.localStorage.getItem(storageKey))
        : '';
    const token = customerToken || fromLs;
    const opts = { skipAuth: true };
    if (token) {
      opts.headers = { 'X-Customer-Token': token };
    }
    const sep = query ? '&' : '?';
    const url = `/store/${encodeURIComponent(slug)}/catalog${query}${sep}_nocache=${Date.now()}`;
    let cancelled = false;
    api(url, opts)
      .then((payload) => {
        if (cancelled) return;
        setData(normalizeCatalogPayload(payload));
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [drawerOpen, slug, query, customerToken]);

  // A opção que dispara o Mercado Pago só aparece se:
  // 1) o lojista activou pagamento online, E
  // 2) há token MP configurado (por empresa OU global no .env).
  // O backend devolve `payment_online_ready` já consolidando estes 2 factos.
  const paymentOnlineReady = Number(data?.tenant?.payment_online_ready || 0) === 1;
  const customOnlineLabel = (() => {
    const raw = data?.tenant?.checkout_online_payment_label;
    return typeof raw === 'string' ? raw : '';
  })();

  /**
   * Opções efectivas do combo de forma de pagamento.
   * Tipo: `Array<{value:string, display:string, kind:'online'|'offline-fallback'|'tenant', subtitle?:string}>`.
   */
  const checkoutPaymentOptions = useMemo(() => {
    const tenantList = Array.isArray(data?.tenant?.checkout_payment_methods)
      ? data.tenant.checkout_payment_methods
      : [];
    return buildEffectiveCheckoutPaymentMethods(tenantList, paymentOnlineReady, customOnlineLabel);
  }, [data?.tenant?.checkout_payment_methods, paymentOnlineReady, customOnlineLabel]);

  const checkoutPaymentValues = useMemo(
    () => checkoutPaymentOptions.map((o) => o.value),
    [checkoutPaymentOptions],
  );

  const hasOnlinePaymentOption = useMemo(
    () => checkoutPaymentOptions.some((o) => o.kind === 'online'),
    [checkoutPaymentOptions],
  );

  useEffect(() => {
    setCheckout((prev) => {
      if (!prev.paymentMethod) return prev;
      if (
        checkoutPaymentValues.length > 0 &&
        !checkoutPaymentValues.includes(prev.paymentMethod)
      ) {
        return { ...prev, paymentMethod: '' };
      }
      return prev;
    });
  }, [checkoutPaymentValues]);

  async function loadCustomerSession(tokenArg = customerToken) {
    if (!tokenArg) {
      setCustomerSession(null);
      setCustomerSessionResolved(true);
      return;
    }
    try {
      const res = await api(`/store/${encodeURIComponent(slug)}/customer/session`, {
        skipAuth: true,
        headers: { 'X-Customer-Token': tokenArg },
      });
      if (res?.logged_in && res?.customer) {
        setCustomerSession({ ...res.customer, radar_enabled: Boolean(res.radar_enabled) });
      } else {
        setCustomerSession(null);
      }
    } catch {
      setCustomerSession(null);
    } finally {
      setCustomerSessionResolved(true);
    }
  }

  function logoutCustomer() {
    if (!slug || typeof window === 'undefined') return;
    if (cartServerSaveTimerRef.current) {
      window.clearTimeout(cartServerSaveTimerRef.current);
      cartServerSaveTimerRef.current = null;
    }
    cartRemoteFetchGenRef.current += 1;
    try {
      window.localStorage.removeItem(`ominia.customer.${slug}`);
    } catch {
      /* ignore */
    }
    if (slugKey) clearCartLocalStorage(slugKey);
    setCart({});
    setCustomerToken('');
    setCustomerSession(null);
    setSavedDeliveryAddresses([]);
    setCheckoutFulfillmentMode(CHECKOUT_FULFILL_PICKUP);
    setCheckout({
      nome: '',
      telefone: '',
      email: '',
      cep: '',
      endereco: '',
      enderecoNumero: '',
      enderecoComplemento: '',
      observacoes: '',
      paymentMethod: '',
    });
  }

  useEffect(() => {
    setCustomerSessionResolved(false);
    setCartServerInitialLoaded(false);
    loadCustomerSession().catch(() => {});
  }, [customerToken, slug]);

  // Radar de Clientes: regista a pesquisa (com debounce) para histórico + alerta de reposição.
  const lastRadarTermRef = useRef('');
  useEffect(() => {
    const term = String(filters.search || '').trim();
    if (!slug || !customerToken || !customerSession?.radar_enabled) return undefined;
    if (term.length < 2 || term === lastRadarTermRef.current) return undefined;
    const handle = setTimeout(() => {
      lastRadarTermRef.current = term;
      registerRadarSearch(slug, customerToken, term).catch(() => {});
    }, 900);
    return () => clearTimeout(handle);
  }, [filters.search, slug, customerToken, customerSession?.radar_enabled]);

  /** Com cliente logado, o checkout reflecte sempre a ficha (até logout). */
  useEffect(() => {
    if (!customerSession || typeof customerSession !== 'object') return;
    setCheckout((prev) => mergeIdentityFromCustomer(prev, customerSession));
  }, [
    customerSession?.id,
    customerSession?.nome,
    customerSession?.telefone,
    customerSession?.email,
    customerSession?.endereco,
  ]);

  const serverCartSync = Boolean(slug && slugKey && customerToken && customerSession);

  /** Sacola na base (cliente logado): carregar e opcionalmente migrar o que estava no LS. */
  useEffect(() => {
    if (!serverCartSync) return undefined;
    if (cartServerSaveTimerRef.current) {
      window.clearTimeout(cartServerSaveTimerRef.current);
      cartServerSaveTimerRef.current = null;
    }
    const fetchGen = ++cartRemoteFetchGenRef.current;
    let cancelled = false;
    (async () => {
      try {
        const res = await api(`/store/${encodeURIComponent(slug)}/customer/cart`, {
          skipAuth: true,
          headers: { 'X-Customer-Token': customerToken },
        });
        if (cancelled || fetchGen !== cartRemoteFetchGenRef.current) return;
        const remote = normalizeCartRemote(res?.items);
        const localLs = readStoredCart(slugKey);
        const hasRemote = Object.keys(remote).length > 0;
        const hasLocalLs = Object.keys(localLs).length > 0;
        let next = remote;
        if (!hasRemote && hasLocalLs) {
          next = localLs;
          await api(`/store/${encodeURIComponent(slug)}/customer/cart`, {
            skipAuth: true,
            headers: { 'X-Customer-Token': customerToken, 'Content-Type': 'application/json' },
            method: 'PUT',
            body: JSON.stringify({ items: next }),
          }).catch(() => {});
        }
        if (cancelled || fetchGen !== cartRemoteFetchGenRef.current) return;
        setCart(next);
        try {
          window.localStorage.removeItem(`${STORE_CART_LS_PREFIX}${slugKey}`);
        } catch {
          /* ignore */
        }
      } catch {
        if (!cancelled && fetchGen === cartRemoteFetchGenRef.current) {
          setCart(readStoredCart(slugKey));
        }
      } finally {
        if (!cancelled && fetchGen === cartRemoteFetchGenRef.current) {
          cartLsHydratedRef.current = true;
          setCartServerInitialLoaded(true);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [serverCartSync, slug, slugKey, customerToken, customerSession?.id]);

  /** Grava sacola no servidor (debounce) — usa `cartRefForServerSave` para não enviar snapshot antigo. */
  useEffect(() => {
    if (!serverCartSync) return undefined;
    if (!cartLsHydratedRef.current) return undefined;
    if (cartServerSaveTimerRef.current) window.clearTimeout(cartServerSaveTimerRef.current);
    cartServerSaveTimerRef.current = window.setTimeout(() => {
      cartServerSaveTimerRef.current = null;
      if (!cartLsHydratedRef.current) return;
      const snap = cartRefForServerSave.current;
      api(`/store/${encodeURIComponent(slug)}/customer/cart`, {
        skipAuth: true,
        headers: { 'X-Customer-Token': customerToken, 'Content-Type': 'application/json' },
        method: 'PUT',
        body: JSON.stringify({ items: snap }),
      }).catch(() => {});
    }, 450);
    return () => {
      if (cartServerSaveTimerRef.current) window.clearTimeout(cartServerSaveTimerRef.current);
    };
  }, [serverCartSync, slug, customerToken, cart]);

  const persistCustomerCartNow = useCallback(
    async (itemsMap) => {
      if (!slug || !customerToken || !customerSession) return;
      try {
        await api(`/store/${encodeURIComponent(slug)}/customer/cart`, {
          skipAuth: true,
          headers: { 'X-Customer-Token': customerToken, 'Content-Type': 'application/json' },
          method: 'PUT',
          body: JSON.stringify({ items: itemsMap }),
        });
      } catch {
        /* ignore */
      }
    },
    [slug, customerToken, customerSession],
  );

  useEffect(() => {
    if (!drawerOpen) return;
    const onKey = (e) => {
      if (e.key === 'Escape') setDrawerOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [drawerOpen]);

  useEffect(() => {
    if (!mediaPreview) return;
    const onKey = (e) => {
      if (e.key === 'Escape') setMediaPreview(null);
    };
    window.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [mediaPreview]);

  /** Sempre conta sobre o catálogo completo na API (`section_summary`). Antes só usávamos
   * `products` já filtrado por pesquisa/secção → a linha «AÇO» sumia com filtro activo. */
  const sectionSidebarRows = useMemo(() => {
    const summary = Array.isArray(data?.section_summary) ? data.section_summary : [];
    if (summary.length > 0) {
      return summary
        .map((row) => ({
          name: String(row.secao),
          count: Number(row.count) || 0,
          imagem: row.imagem ? String(row.imagem) : null,
        }))
        .sort((a, b) => a.name.localeCompare(b.name, 'pt-BR'));
    }
    const rows = Array.isArray(data?.products) ? data.products : [];
    if (!rows.length) return [];
    const m = new Map();
    for (const p of rows) {
      const k = p.secao || 'Geral';
      m.set(k, (m.get(k) || 0) + 1);
    }
    return [...m.entries()]
      .map(([name, count]) => ({ name, count, imagem: null }))
      .sort((a, b) => a.name.localeCompare(b.name, 'pt-BR'));
  }, [data?.section_summary, data?.products]);

  const sectionImageByName = useMemo(() => {
    /** @type {Record<string, string>} */
    const out = {};
    for (const row of sectionSidebarRows) {
      if (row.imagem) out[row.name] = row.imagem;
    }
    return out;
  }, [sectionSidebarRows]);

  const storeProductDisplayParsed = useMemo(
    () => parseStoreProductDisplay(data?.tenant?.theme_json),
    [data?.tenant?.theme_json],
  );

  const productImageCtx = useMemo(
    () => ({
      sectionPhotoFallback: storeProductDisplayParsed.sectionPhotoFallback,
      sectionImages: sectionImageByName,
    }),
    [storeProductDisplayParsed.sectionPhotoFallback, sectionImageByName],
  );

  /** Abre o modal foto/vídeo ao entrar pela partilha, se houver mídia. */
  useEffect(() => {
    if (!isProductDeepLink || productFocusId == null || !Array.isArray(data?.products)) return;
    if (productDeepLinkPreviewOpenedRef.current) return;
    const rows = data.products;
    if (rows.length !== 1 || Number(rows[0]?.id) !== productFocusId) return;
    const pRow = rows[0];
    const galleryUrls = productGalleryUrls(pRow, productImageCtx);
    const yt = productYoutubeUrl(pRow);
    const vu = yt && youtubeEmbedSrc(yt);
    const basePrev = {
      title: pRow.nome || 'Produto',
      shareUrl: buildStoreProductShareUrl(location.pathname, pRow),
      shareOgUrl: buildStoreProductOgUrl(data?.tenant?.slug, pRow.id),
      shareTitle: pRow.nome || 'Produto',
    };
    if (galleryUrls.length > 0) {
      productDeepLinkPreviewOpenedRef.current = true;
      setMediaPreview({
        ...basePrev,
        imageUrls: galleryUrls,
        youtubeUrl: yt,
        startIndex: 0,
        startTab: 'imagens',
      });
      return;
    }
    if (vu) {
      productDeepLinkPreviewOpenedRef.current = true;
      setMediaPreview({
        ...basePrev,
        imageUrls: [],
        youtubeUrl: yt,
        startIndex: 0,
        startTab: 'video',
      });
    }
  }, [isProductDeepLink, productFocusId, data?.products, data?.tenant?.slug, slug, location.pathname, productImageCtx]);

  const catalogTotalForTodas =
    typeof data?.catalog_product_total === 'number' && data.catalog_product_total >= 0
      ? data.catalog_product_total
      : Array.isArray(data?.products)
        ? data.products.length
        : 0;

  const sortedProducts = useMemo(() => {
    const arr = [...(data?.products || [])];
    if (sortAz) arr.sort((a, b) => (a.nome || '').localeCompare(b.nome || '', 'pt-BR'));
    return arr;
  }, [data?.products, sortAz]);

  const storeAccentStyle = useMemo(
    () => storeAccentCssVariables(parseStoreAccent(data?.tenant?.theme_json)),
    [data?.tenant?.theme_json],
  );

  const requiresAdultGate = Number(data?.tenant?.require_adult_confirmation || 0) === 1;
  const showAgeGate = Boolean(slug && data?.tenant && requiresAdultGate && !ageConfirmed);

  useEffect(() => {
    if (!showAgeGate) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [showAgeGate]);

  const defaultDocumentTitle = 'OminIA Pedidos — loja online e painel de pedidos';

  /** Open Graph no documento para o rasto do Facebook (relevante com ?p=id). O crawler não executa React;
   * após publicar, use o Depurador de partilha da Meta para «Extrair de novo» se o cartão estiver vazio. */
  useEffect(() => {
    if (typeof document === 'undefined') return undefined;

    const wipeOg = () => {
      document.title = defaultDocumentTitle;
      document.querySelectorAll('meta[data-ominia-og="1"]').forEach((el) => el.remove());
    };

    if (!isProductDeepLink || productFocusId == null || !data?.tenant) {
      wipeOg();
      return undefined;
    }
    const rows = Array.isArray(data.products) ? data.products : [];
    const p = rows.find((r) => Number(r.id) === productFocusId);
    if (!p) {
      wipeOg();
      return undefined;
    }

    wipeOg();

    const tenantNome = data.tenant.nome || 'Loja';
    const pageUrl = normalizeShareUrlForSocial(buildStoreProductShareUrl(location.pathname, p));

    const setMeta = (key, val) => {
      if (!val) return;
      const el = document.createElement('meta');
      el.setAttribute('property', key);
      el.setAttribute('content', val);
      el.setAttribute('data-ominia-og', '1');
      document.head.appendChild(el);
    };

    setMeta('og:url', pageUrl);
    setMeta('og:title', `${p.nome || 'Produto'} · ${tenantNome}`);
    setMeta('og:description', `Produto na loja ${tenantNome}.`);
    setMeta('og:type', 'website');

    const gal0 = productGalleryUrls(p, productImageCtx)[0];
    if (gal0) {
      let imgAbs = resolveApiMediaUrl(gal0);
      if (imgAbs && !/^https?:\/\//i.test(imgAbs)) {
        try {
          imgAbs = new URL(imgAbs, window.location.origin).href;
        } catch {
          imgAbs = '';
        }
      }
      if (imgAbs) setMeta('og:image', imgAbs);
    }

    document.title = `${p.nome || 'Produto'} · ${tenantNome}`;

    return () => {
      wipeOg();
    };
  }, [isProductDeepLink, productFocusId, data?.tenant, data?.products, location.pathname, productImageCtx]);

  const resolveProductRow = useCallback(
    (id) => {
      const pid = Number(id);
      if (!Number.isFinite(pid) || pid <= 0) return null;
      const list = Array.isArray(data?.products) ? data.products : [];
      return list.find((x) => Number(x.id) === pid) ?? catalogById[pid] ?? null;
    },
    [data?.products, catalogById],
  );

  function openStockAlert(nome, available) {
    setStockAlert({
      open: true,
      nome: String(nome || 'Produto'),
      available: Math.max(0, Math.floor(Number(available) || 0)),
    });
  }

  function addQty(id, delta) {
    if (Number(data?.tenant?.catalog_only_mode || 0) === 1) return;
    const mustAuth = tenantRequiresCustomerAuth(data?.tenant);
    if (mustAuth && !customerSession) {
      setAuthModalOpen(true);
      return;
    }
    const pid = Number(id);
    if (!Number.isFinite(pid) || pid <= 0) return;
    const sid = String(pid);
    const pRow = resolveProductRow(pid);
    const tracked = productTrackedStock(pRow);
    const currentQty = Math.max(0, Math.floor(Number(cart[sid] || 0)));

    if (delta > 0 && tracked !== null) {
      if (tracked <= 0) {
        openStockAlert(pRow?.nome, 0);
        return;
      }
      if (currentQty >= tracked) {
        openStockAlert(pRow?.nome, tracked);
        return;
      }
    }

    /* Abrir gaveta ao adicionar só no desktop — no mobile mantém navegação leve até «Sacola / pedido». */
    if (delta > 0 && typeof window !== 'undefined' && window.matchMedia('(min-width: 1024px)').matches) {
      setDrawerOpen(true);
    }

    setCart((prev) => {
      const next = { ...prev };
      const requestedQty = Math.max((next[sid] || 0) + delta, 0);
      let qty = requestedQty;
      if (tracked !== null && delta > 0 && qty > tracked) {
        openStockAlert(pRow?.nome, tracked);
        qty = tracked;
      }
      if (qty <= 0) delete next[sid];
      else next[sid] = qty;
      if (!Object.keys(next).length && slugKey && !customerSession) {
        queueMicrotask(() => clearCartLocalStorage(slugKey));
      }
      return next;
    });

    if (delta > 0 && pRow) {
      const nextQty = Math.max(
        0,
        Math.min(
          currentQty + delta,
          tracked !== null ? tracked : currentQty + delta,
        ),
      );
      const added = nextQty - currentQty;
      if (added > 0) storeTrackingRef.current.notifyAddToCart(pRow, added);
    }
  }

  function setProductQty(id, nextQty) {
    if (Number(data?.tenant?.catalog_only_mode || 0) === 1) return;
    const mustAuth = tenantRequiresCustomerAuth(data?.tenant);
    if (mustAuth && !customerSession) {
      setAuthModalOpen(true);
      return;
    }
    const pid = Number(id);
    if (!Number.isFinite(pid) || pid <= 0) return;
    const sid = String(pid);
    const pRow = resolveProductRow(pid);
    const tracked = productTrackedStock(pRow);
    const currentQty = Math.max(0, Math.floor(Number(cart[sid] || 0)));
    let qty = Math.max(0, Math.floor(Number(nextQty) || 0));

    if (qty > 0 && tracked !== null) {
      if (tracked <= 0) {
        openStockAlert(pRow?.nome, 0);
        return;
      }
      if (qty > tracked) {
        openStockAlert(pRow?.nome, tracked);
        qty = tracked;
      }
    }

    if (qty > 0 && typeof window !== 'undefined' && window.matchMedia('(min-width: 1024px)').matches) {
      setDrawerOpen(true);
    }

    setCart((prev) => {
      const next = { ...prev };
      if (qty <= 0) delete next[sid];
      else next[sid] = qty;
      if (!Object.keys(next).length && slugKey && !customerSession) {
        queueMicrotask(() => clearCartLocalStorage(slugKey));
      }
      return next;
    });

    if (qty > currentQty && pRow) {
      storeTrackingRef.current.notifyAddToCart(pRow, qty - currentQty);
    }
  }

  const openListProductMedia = useCallback(
    (p) => {
      const galleryUrls = productGalleryUrls(p, productImageCtx);
      const yt = productYoutubeUrl(p);
      setMediaPreview({
        title: p.nome || 'Produto',
        imageUrls: galleryUrls,
        youtubeUrl: yt,
        startIndex: 0,
        startTab: galleryUrls.length > 0 ? 'imagens' : 'video',
        shareUrl: buildStoreProductShareUrl(location.pathname, p),
        shareOgUrl: buildStoreProductOgUrl(data?.tenant?.slug, p.id),
        shareTitle: p.nome || 'Produto',
      });
    },
    [data?.tenant?.slug, location.pathname, productImageCtx],
  );

  function removeCartLine(id) {
    setCart((prev) => {
      const next = { ...prev };
      const pid = Number(id);
      const sid = Number.isFinite(pid) && pid > 0 ? String(pid) : String(id);
      delete next[sid];
      if (!Object.keys(next).length) {
        setCouponPctValid(null);
        setCouponNormApplied('');
        setCouponMsg('');
        if (slugKey && !customerSession) queueMicrotask(() => clearCartLocalStorage(slugKey));
      }
      return next;
    });
  }

  /**
   * `true` se o item está marcado no carrinho. Itens recém-adicionados ou
   * ausentes do mapa `cartSelection` ficam marcados por defeito.
   */
  const isItemSelected = useCallback(
    (pid) => {
      const sid = String(pid);
      if (!(sid in cartSelection)) return true;
      return Boolean(cartSelection[sid]);
    },
    [cartSelection],
  );

  const totals = () => {
    if (!data) {
      return {
        count: 0,
        total: 0,
        subtotal: 0,
        discountPercent: null,
        deliveryFee: 0,
        totalInCart: 0,
        unselectedCount: 0,
      };
    }
    let count = 0;
    let total = 0;
    let totalInCart = 0;
    for (const [rawId, q] of Object.entries(cart)) {
      if (!q || q <= 0) continue;
      const pid = Number(rawId);
      const p = catalogById[pid];
      if (!p) continue;
      totalInCart += q;
      if (!isItemSelected(rawId)) continue;
      count += q;
      total += Number(p.preco) * q;
    }
    const subtotal = Math.round(total * 100) / 100;
    let discountPercent = couponPctValid;
    let payTotal = subtotal;
    if (
      typeof discountPercent === 'number' &&
      discountPercent > 0 &&
      discountPercent <= 100 &&
      !pricingHidden
    ) {
      payTotal = Math.round(subtotal * (100 - discountPercent)) / 100;
    }
    const deliveryFee =
      deliveryZonesEnabled &&
      !pickupOnly &&
      !checkoutFulfillmentIsPickup(checkoutFulfillmentMode) &&
      deliveryFeeQuote &&
      typeof deliveryFeeQuote.fee === 'number'
        ? deliveryFeeQuote.fee
        : 0;
    if (deliveryFee > 0) {
      payTotal = Math.round((payTotal + deliveryFee) * 100) / 100;
    }
    return {
      count,
      total: payTotal,
      subtotal,
      discountPercent: discountPercent ?? null,
      deliveryFee,
      totalInCart,
      unselectedCount: Math.max(totalInCart - count, 0),
    };
  };

  async function applyCheckoutCoupon() {
    const normalized = String(couponInput || '')
      .replace(/\s+/g, '')
      .trim()
      .toUpperCase();
    if (!normalized) {
      setCouponPctValid(null);
      setCouponNormApplied('');
      setCouponMsg('');
      setError('');
      return;
    }
    setCouponBusy(true);
    setCouponMsg('');
    setError('');
    try {
      const res = await api(
        `/store/${encodeURIComponent(slug)}/discount-coupon?code=${encodeURIComponent(normalized)}`,
        { skipAuth: true },
      );
      if (res?.valid === true && typeof res.percent_discount === 'number') {
        setCouponPctValid(res.percent_discount);
        setCouponNormApplied(normalized);
        setCouponMsg(`Cupom válido · ${Number(res.percent_discount)}% sobre o total.`);
      } else {
        setCouponPctValid(null);
        setCouponNormApplied('');
        setCouponMsg('Cupom inválido ou já utilizado.');
      }
    } catch (e) {
      setCouponPctValid(null);
      setCouponNormApplied('');
      setCouponMsg(e.message || 'Não foi possível validar o cupom.');
    } finally {
      setCouponBusy(false);
    }
  }

  function productById(id) {
    const n = Number(id);
    if (Number.isFinite(n) && catalogById[n]) return catalogById[n];
    const list = Array.isArray(data?.products) ? data.products : [];
    return list.find((x) => Number(x.id) === Number(id));
  }

  /**
   * Remove um conjunto de productIds do `cart` (e do servidor se logado).
   * Mantém quaisquer itens não listados — usado para preservar produtos
   * "deixados para depois" via checkbox desmarcado.
   */
  const removeCartItems = useCallback(
    async (productIds) => {
      const idsToRemove = new Set((productIds || []).map((x) => String(x)));
      if (!idsToRemove.size) return;
      // Invalida qualquer GET /customer/cart em vôo (evita corrida que repopula
      // o cart com os items que acabámos de remover após voltar do MP).
      cartRemoteFetchGenRef.current += 1;
      if (cartServerSaveTimerRef.current) {
        window.clearTimeout(cartServerSaveTimerRef.current);
        cartServerSaveTimerRef.current = null;
      }
      let nextCartSnap = {};
      setCart((prev) => {
        const next = {};
        for (const sid of Object.keys(prev)) {
          if (idsToRemove.has(sid)) continue;
          if (prev[sid] > 0) next[sid] = prev[sid];
        }
        nextCartSnap = next;
        return next;
      });
      if (customerSession) {
        try {
          await persistCustomerCartNow(nextCartSnap);
        } catch {
          /* ignore — próxima sync corrige */
        }
      } else if (slugKey) {
        if (Object.keys(nextCartSnap).length === 0) {
          clearCartLocalStorage(slugKey);
        } else {
          try {
            window.localStorage.setItem(
              `${STORE_CART_LS_PREFIX}${slugKey}`,
              JSON.stringify(nextCartSnap),
            );
          } catch {
            /* ignore */
          }
        }
      }
    },
    [customerSession, persistCustomerCartNow, slugKey],
  );

  const fetchMpOrderOnlinePaid = useCallback(
    async (orderId, token) => {
      const oid = Number(orderId);
      if (!slug || !Number.isFinite(oid) || oid < 1) return false;
      if (token) {
        try {
          const res = await api(`/store/${encodeURIComponent(slug)}/customer/orders`, {
            skipAuth: true,
            headers: { 'X-Customer-Token': token },
          });
          const hit = (res?.orders || []).find((o) => Number(o.id) === oid);
          return orderIsPaidViaOnlineGateway(hit);
        } catch {
          /* tenta rota pública abaixo */
        }
      }
      try {
        const res = await api(
          `/store/${encodeURIComponent(slug)}/mp-order-payment?order_id=${encodeURIComponent(String(oid))}`,
          { skipAuth: true },
        );
        return res?.online_paid === true;
      } catch {
        return false;
      }
    },
    [slug],
  );

  const finalizeMpSaleFromCart = useCallback(
    async (pending) => {
      if (mpCartFinalizedRef.current || !pending?.productIds?.length) return false;
      mpCartFinalizedRef.current = true;
      if (mpCartPollTimerRef.current) {
        window.clearTimeout(mpCartPollTimerRef.current);
        mpCartPollTimerRef.current = null;
      }
      const lines = pending.productIds
        .map((pid) => {
          const pr = productById(pid);
          const qty = Number(cart[pid] ?? cart[String(pid)] ?? 0);
          if (!pr || qty < 1) return null;
          const unit = Number(pr.preco) || 0;
          return { nome: pr.nome || `Produto #${pid}`, qty, unit, line: unit * qty };
        })
        .filter(Boolean);
      await removeCartItems(pending.productIds);
      clearMpPendingOrder(slug);
      setCouponInput('');
      setCouponPctValid(null);
      setCouponNormApplied('');
      setCouponMsg('');
      if (pending.orderId) {
        const total = lines.reduce((s, row) => s + row.line, 0);
        setOrderSuccess({
          orderId: pending.orderId,
          total,
          subtotal: total,
          discountPercent: null,
          items: lines,
        });
        storeTrackingRef.current.notifyPurchase({
          orderId: pending.orderId,
          total,
          items: pending.productIds.map((pid) => ({
            product_id: Number(pid),
            qty: Number(cart[pid] ?? cart[String(pid)] ?? 0),
            unit: Number(productById(pid)?.preco) || 0,
          })),
        });
        setDrawerOpen(false);
      }
      setMpReturnBanner(
        'Pagamento online confirmado. Os itens desta compra foram retirados da sua sacola.',
      );
      setMpReturnBannerTone('success');
      return true;
    },
    [slug, cart, removeCartItems, productById],
  );

  /**
   * Retorno do Mercado Pago (`?mp=…` + parâmetros `status` / `collection_status`).
   * Limpa a sacola quando o pagamento online estiver confirmado (PIX incluído).
   */
  useEffect(() => {
    if (!slug) return;
    const mp = searchParams.get('mp');
    if (!mp) return;
    const lsTokenKey = `ominia.customer.${slug}`;
    const lsToken =
      typeof window !== 'undefined' ? window.localStorage.getItem(lsTokenKey) : null;
    const token = customerToken || lsToken || '';
    const willBeLogged = Boolean(token);
    if (willBeLogged && !customerSessionResolved) return;
    if (willBeLogged && customerSession && !cartServerInitialLoaded) return;

    let cancelled = false;

    const run = async () => {
      const pending = readMpPendingOrder(slug);
      const urlApproved = mp === 'success' || mpReturnUrlSaysApproved(searchParams);
      let paid = urlApproved;
      if (!paid && pending?.orderId) {
        paid = await fetchMpOrderOnlinePaid(pending.orderId, token);
      }
      if (cancelled) return;

      if (paid && pending?.productIds?.length) {
        await finalizeMpSaleFromCart(pending);
      } else if (mp === 'failure') {
        clearMpPendingOrder(slug);
        setMpReturnBanner(
          'Pagamento não concluído. Os produtos continuam na sua sacola — pode tentar de novo ou escolher outra forma de pagamento.',
        );
        setMpReturnBannerTone('warning');
      } else if (mp === 'pending') {
        setMpReturnBanner(
          'Pagamento pendente (ex.: PIX). Assim que o Mercado Pago confirmar, os itens desta compra saem da sacola automaticamente.',
        );
        setMpReturnBannerTone('warning');
      } else if (mp === 'success') {
        setMpReturnBanner('Pagamento recebido. Obrigado pela sua compra.');
        setMpReturnBannerTone('success');
        clearMpPendingOrder(slug);
      }

      if (!cancelled) {
        navigate(`/${encodeURIComponent(slug)}`, { replace: true });
      }
    };

    void run();
    return () => {
      cancelled = true;
    };
  }, [
    slug,
    searchParams,
    navigate,
    customerToken,
    customerSessionResolved,
    customerSession,
    cartServerInitialLoaded,
    fetchMpOrderOnlinePaid,
    finalizeMpSaleFromCart,
  ]);

  /** Poll leve enquanto existir pedido MP em aberto (PIX / reconciliação no painel). */
  useEffect(() => {
    if (!slug || mpCartFinalizedRef.current) return;
    const pending = readMpPendingOrder(slug);
    if (!pending?.orderId || !pending.productIds.length) return;

    const lsTokenKey = `ominia.customer.${slug}`;
    const lsToken =
      typeof window !== 'undefined' ? window.localStorage.getItem(lsTokenKey) : null;
    const token = customerToken || lsToken || '';
    const willBeLogged = Boolean(token);
    if (willBeLogged && !customerSessionResolved) return;
    if (willBeLogged && customerSession && !cartServerInitialLoaded) return;

    let cancelled = false;
    let attempts = 0;
    const maxAttempts = 40;

    const tick = async () => {
      if (cancelled || mpCartFinalizedRef.current) return;
      const stillPending = readMpPendingOrder(slug);
      if (!stillPending?.orderId) return;
      const paid = await fetchMpOrderOnlinePaid(stillPending.orderId, token);
      if (cancelled) return;
      if (paid) {
        await finalizeMpSaleFromCart(stillPending);
        return;
      }
      attempts += 1;
      if (attempts < maxAttempts && !cancelled) {
        mpCartPollTimerRef.current = window.setTimeout(tick, 3000);
      }
    };

    mpCartPollTimerRef.current = window.setTimeout(tick, 1500);
    return () => {
      cancelled = true;
      if (mpCartPollTimerRef.current) {
        window.clearTimeout(mpCartPollTimerRef.current);
        mpCartPollTimerRef.current = null;
      }
    };
  }, [
    slug,
    customerToken,
    customerSessionResolved,
    customerSession,
    cartServerInitialLoaded,
    fetchMpOrderOnlinePaid,
    finalizeMpSaleFromCart,
  ]);

  async function submitCheckout(ev, opts = {}) {
    if (ev && typeof ev.preventDefault === 'function') ev.preventDefault();
    // Filtra rigorosamente o payload para casar com o que o cliente vê na UI:
    //   1) só itens cujo produto está no catálogo público actual (`catalogById`).
    //      Produtos que foram ocultados pela loja, desactivados ou removidos
    //      ficam no `cart` do localStorage / servidor (fantasmas), mas a UI já
    //      não os mostra — antes o checkout enviava-os mesmo assim e o pedido
    //      gravava com valor diferente do que o cliente viu.
    //   2) só itens com qty > 0 e marcados (`isItemSelected`).
    const itemsObj = [];
    const ghostIds = [];
    Object.entries(cart).forEach(([pid, qty]) => {
      if (!(qty > 0)) return;
      const pidNum = Number(pid);
      const inCatalog = Boolean(catalogById[pidNum]);
      if (!inCatalog) {
        ghostIds.push(String(pid));
        return;
      }
      if (!isItemSelected(pid)) return;
      itemsObj.push({ product_id: pidNum, qty });
    });
    if (ghostIds.length > 0) {
      // Limpeza silenciosa de fantasmas. Não interrompe o checkout — só os
      // remove do cart (e do servidor se logado) e continua com o que sobrou.
      try {
        await removeCartItems(ghostIds);
      } catch {
        // Best-effort: mesmo que falhe a limpeza, o filtro acima já bloqueou.
      }
    }
    if (!itemsObj.length) {
      setError(
        cartLines.length > 0
          ? 'Selecione pelo menos 1 produto da sacola para finalizar o pedido.'
          : 'Sua sacola está vazia.',
      );
      return;
    }
    if (
      checkoutPaymentValues.length > 0 &&
      !String(checkout.paymentMethod || '').trim()
    ) {
      setError('Selecione a forma de pagamento.');
      return;
    }
    const checkoutContactErr = validateCustomerContact(
      checkout.email,
      checkout.telefone,
      getCustomerContactRequirements(data?.tenant),
    );
    if (checkoutContactErr) {
      setError(checkoutContactErr);
      return;
    }
    const isPickup = pickupOnly || checkoutFulfillmentIsPickup(checkoutFulfillmentMode);
    const savedAddressId = checkoutFulfillmentParseSavedId(checkoutFulfillmentMode);
    const isNewAddress = checkoutFulfillmentIsNewAddress(checkoutFulfillmentMode);
    if (!isPickup && !savedAddressId && !checkout.endereco.trim()) {
      setError('Informe o endereço de entrega, escolha uma morada guardada ou selecione retirar na loja.');
      return;
    }
    if (deliveryZonesEnabled && !isPickup) {
      const cepDigitsCheckout = resolveCurrentCheckoutCepDigits();
      if (cepDigitsCheckout.length !== 8) {
        setError('Informe o CEP de entrega (8 dígitos) para calcular a taxa.');
        return;
      }
      if (deliveryFeeError) {
        setError(deliveryFeeError);
        return;
      }
      if (!deliveryFeeQuote) {
        setError('Aguarde o cálculo da taxa de entrega ou confira o CEP.');
        return;
      }
    }
    if (
      !isPickup &&
      isNewAddress &&
      checkout.endereco.trim() &&
      !String(checkout.enderecoNumero || '').trim() &&
      !String(checkout.enderecoComplemento || '').trim()
    ) {
      setError('Informe número ou complemento do endereço para finalizar o pedido.');
      return;
    }
    setError('');
    // Gate 1 — cupom digitado mas ainda não aplicado: oferecer aplicar antes de prosseguir.
    if (!opts.skipCouponPrompt) {
      const normInput = String(couponInput || '')
        .replace(/\s+/g, '')
        .trim()
        .toUpperCase();
      const couponAlreadyApplied =
        Boolean(couponNormApplied) &&
        normInput === couponNormApplied &&
        typeof couponPctValid === 'number' &&
        couponPctValid > 0;
      if (!pricingHidden && normInput.length > 0 && !couponAlreadyApplied) {
        setCouponApplyPromptOpen(true);
        return;
      }
    }
    // Gate 2 — confirmação final com o resumo do pedido na tela.
    if (!opts.confirmed) {
      setConfirmCheckoutOpen(true);
      return;
    }
    setConfirmCheckoutOpen(false);
    const lines = itemsObj.map(({ product_id: pid, qty }) => {
      const pr = productById(pid);
      const unit = pr ? Number(pr.preco) : 0;
      return {
        nome: pr?.nome || `Produto #${pid}`,
        qty,
        unit,
        line: unit * qty,
      };
    });

    try {
      const payload = {
        nome: checkout.nome.trim(),
        telefone: checkout.telefone.trim(),
        email: checkout.email.trim(),
        endereco: isPickup || savedAddressId ? '' : checkout.endereco.trim(),
        endereco_numero: isPickup || savedAddressId ? '' : String(checkout.enderecoNumero || '').trim(),
        endereco_complemento: isPickup || savedAddressId ? '' : String(checkout.enderecoComplemento || '').trim(),
        observacoes: String(checkout.observacoes || '').trim(),
        retirar_na_loja: isPickup,
        update_customer_address: Boolean(updateCustomerAddress && isNewAddress && !isPickup),
        items: itemsObj,
      };
      if (customerToken && savedAddressId) {
        payload.delivery_address_id = savedAddressId;
      }
      if (
        !pricingHidden &&
        couponNormApplied &&
        typeof couponPctValid === 'number' &&
        couponPctValid > 0
      ) {
        payload.coupon_code = couponNormApplied;
      }
      if (checkoutPaymentValues.length > 0 && String(checkout.paymentMethod || '').trim()) {
        payload.payment_method = String(checkout.paymentMethod).trim();
      }
      if (deliveryZonesEnabled && !isPickup) {
        const cepSend = resolveCurrentCheckoutCepDigits();
        if (cepSend.length === 8) {
          payload.cep = cepSend;
        }
      }
      // Total que o cliente está a ver no momento do clique em "Finalizar".
      // O backend compara com o total recalculado a partir dos preços actuais
      // do DB; se divergir > R$ 0,01 aborta com TOTAL_MISMATCH (em vez de
      // gravar valor diferente do que o cliente aceitou).
      try {
        const tNow = typeof totals === 'function' ? totals() : null;
        if (tNow && typeof tNow.total === 'number' && !pricingHidden && tNow.total >= 0) {
          payload.expected_total = Math.round(tNow.total * 100) / 100;
        }
      } catch {
        // se totals() falhar por qualquer motivo, simplesmente não enviamos
        // expected_total — backend mantém comportamento antigo (sem validação)
      }
      const res = await api(`/store/${encodeURIComponent(slug)}/checkout`, {
        skipAuth: true,
        headers: customerToken ? { 'X-Customer-Token': customerToken } : undefined,
        method: 'POST',
        body: JSON.stringify(payload),
      });
      const submittedProductIds = itemsObj.map((it) => String(it.product_id));
      const mpUrl =
        typeof res?.mercadopago?.init_point === 'string' && res.mercadopago.init_point.trim()
          ? res.mercadopago.init_point.trim()
          : '';
      if (mpUrl) {
        // Sacola mantida até o MP confirmar o pagamento (URL, webhook ou
        // reconciliação); depois removemos só os itens deste pedido.
        writeMpPendingOrder(slug, res.order_id, submittedProductIds);
        window.location.assign(mpUrl);
        return;
      }
      setOrderSuccess({
        orderId: res.order_id,
        total: Number(res.total),
        subtotal:
          res.subtotal !== undefined && res.subtotal !== null ? Number(res.subtotal) : undefined,
        discountPercent:
          res.discount_percent !== undefined && res.discount_percent !== null
            ? Number(res.discount_percent)
            : null,
        items: lines,
      });
      storeTrackingRef.current.notifyPurchase({
        orderId: res.order_id,
        total: Number(res.total),
        items: itemsObj.map((it) => ({
          product_id: it.product_id,
          qty: it.qty,
          unit: Number(productById(it.product_id)?.preco) || 0,
        })),
      });
      // Pedido offline confirmado: remove só os itens que foram enviados —
      // produtos desmarcados (deixados para depois) permanecem na sacola.
      await removeCartItems(submittedProductIds);
      setCouponInput('');
      setCouponPctValid(null);
      setCouponNormApplied('');
      setCouponMsg('');
      const checkoutBase = {
        nome: '',
        telefone: '',
        email: '',
        cep: '',
        endereco: '',
        enderecoNumero: '',
        enderecoComplemento: '',
        observacoes: '',
        paymentMethod: '',
      };
      setCheckout(
        customerSession && typeof customerSession === 'object'
          ? mergeIdentityFromCustomer(checkoutBase, customerSession)
          : checkoutBase,
      );
      setCheckoutFulfillmentMode(
        checkoutFulfillmentDefaultMode(pickupOnly, savedDeliveryAddresses),
      );
      setDeliveryFeeQuote(null);
      setDeliveryFeeError('');
      setCepLookupMsg('');
      setCepLookupError('');
      setCheckoutLookupMsg('');
      setDrawerOpen(false);
    } catch (e) {
      // Erros estruturados que o backend devolve quando a sacola "envelheceu":
      //   - ITEMS_UNAVAILABLE: 1 ou + produtos foram ocultados/removidos pela
      //     loja entre o momento em que o cliente abriu a vitrine e o clique
      //     em finalizar. Removemos esses IDs do carrinho e mostramos aviso.
      //   - TOTAL_MISMATCH: o total que o cliente viu (expected_total) já não
      //     bate com o calculado pelo servidor (preço de algum item mudou).
      //     Não gravamos nada; pedimos ao cliente para conferir e reenviar.
      const errData = e && typeof e === 'object' ? e.data : null;
      const errCode = errData && typeof errData === 'object' ? String(errData.code || '') : '';
      if (errCode === 'ITEMS_UNAVAILABLE' && Array.isArray(errData?.unavailable)) {
        const badIds = errData.unavailable
          .map((u) => (u && (u.product_id ?? u.id)) ?? null)
          .filter((v) => v != null)
          .map((v) => String(v));
        if (badIds.length > 0) {
          try {
            await removeCartItems(badIds);
          } catch {
            // best-effort
          }
        }
        setError(
          e.message ||
            'Alguns itens da sua sacola já não estão disponíveis. Removemos da sacola — confira o resumo e tente novamente.',
        );
        return;
      }
      if (errCode === 'TOTAL_MISMATCH') {
        setError(
          e.message ||
            'O valor da sua sacola mudou. Confira o resumo actualizado e finalize novamente.',
        );
        return;
      }
      if (errCode === 'INSUFFICIENT_STOCK' && Array.isArray(errData?.stock)) {
        setCart((prev) => {
          const next = { ...prev };
          for (const sp of errData.stock) {
            if (!sp || typeof sp !== 'object') continue;
            const pid = sp.product_id ?? sp.id;
            if (pid == null) continue;
            const sid = String(pid);
            const av = Math.max(0, Math.floor(Number(sp.available) || 0));
            if (av <= 0) delete next[sid];
            else next[sid] = av;
          }
          return next;
        });
        setError(
          e.message ||
            'Stock insuficiente. Ajustámos as quantidades na sacola — confira e tente novamente.',
        );
        return;
      }
      setError(e.message);
    }
  }

  async function lookupCheckoutCep() {
    const formatted = formatCep(checkout.cep);
    const digits = cepDigits(formatted);
    if (digits.length !== 8) {
      setCepLookupError('Digite um CEP válido com 8 números.');
      setCepLookupMsg('');
      return;
    }
    setCepLookupBusy(true);
    setCepLookupMsg('');
    setCepLookupError('');
    try {
      const res = await fetch(`https://viacep.com.br/ws/${digits}/json/`);
      if (!res.ok) throw new Error('Falha ao consultar o CEP.');
      const row = await res.json();
      if (row?.erro) throw new Error('CEP não encontrado.');
      const nextAddress = composeAddressFromCep(row, formatted);
      if (!nextAddress) throw new Error('Não foi possível preencher o endereço para este CEP.');
      setCheckout((prev) => ({ ...prev, cep: formatted, endereco: nextAddress }));
      setCepLookupMsg('Endereço preenchido. Agora informe número e complemento.');
      if (deliveryZonesEnabled) {
        void refreshDeliveryFeeQuote();
      }
    } catch (e) {
      setCepLookupError(e instanceof Error ? e.message : 'Erro ao buscar CEP.');
    } finally {
      setCepLookupBusy(false);
    }
  }

  async function lookupStoreCustomer({ email = '', telefone = '' } = {}) {
    const em = String(email || '').trim().toLowerCase();
    const phoneRaw = String(telefone || '').trim();
    if (!em && !phoneRaw) return null;
    const qs = new URLSearchParams();
    if (em) qs.set('email', em);
    if (phoneRaw) qs.set('telefone', phoneRaw);
    const res = await api(`/store/${encodeURIComponent(slug)}/customer/lookup?${qs.toString()}`, {
      skipAuth: true,
    });
    return res?.found && res?.customer ? res.customer : null;
  }

  function mergeCheckoutFromKnownCustomer(customer) {
    if (!customer || typeof customer !== 'object') return;
    setCheckout((prev) => ({
      ...prev,
      nome: prev.nome || String(customer.nome || ''),
      telefone: prev.telefone || String(customer.telefone || ''),
      email: prev.email || String(customer.email || ''),
      endereco: prev.endereco || String(customer.endereco || ''),
    }));
  }

  function mergeRegisterFromKnownCustomer(customer) {
    if (!customer || typeof customer !== 'object') return;
    setAuthRegister((prev) => ({
      ...prev,
      nome: prev.nome || String(customer.nome || ''),
      whatsapp_country_code: prev.whatsapp_country_code || String(customer.whatsapp_country_code || '+55'),
      whatsapp: prev.whatsapp || normalizeWhatsappMask(String(customer.telefone || ''), prev.whatsapp_country_code || '+55'),
      email: prev.email || String(customer.email || ''),
      endereco: prev.endereco || String(customer.endereco || ''),
    }));
  }

  async function tryAutofillCheckoutByIdentity(fromField) {
    const email = String(checkout.email || '').trim().toLowerCase();
    const tel = String(checkout.telefone || '').trim();
    const hasEmail = !!email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    const hasPhone = phoneDigits(tel).length >= 8;
    if (!hasEmail && !hasPhone) return;
    setCheckoutLookupBusy(true);
    setCheckoutLookupMsg('');
    try {
      const customer = await lookupStoreCustomer({
        email: hasEmail ? email : '',
        telefone: hasPhone ? tel : '',
      });
      if (!customer) return;
      mergeCheckoutFromKnownCustomer(customer);
      const label = fromField === 'telefone' ? 'WhatsApp' : 'e-mail';
      setCheckoutLookupMsg(`Dados encontrados pelo ${label} e preenchidos automaticamente.`);
    } catch {
      // silencioso para não bloquear checkout em falha eventual de lookup
    } finally {
      setCheckoutLookupBusy(false);
    }
  }

  async function tryAutofillRegisterByIdentity(fromField) {
    const email = String(authRegister.email || '').trim().toLowerCase();
    const wpp = String(authRegister.whatsapp || '').trim();
    const hasEmail = !!email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    const hasPhone = phoneDigits(wpp).length >= 8;
    if (!hasEmail && !hasPhone) return;
    setRegisterLookupBusy(true);
    setRegisterLookupMsg('');
    try {
      const customer = await lookupStoreCustomer({
        email: hasEmail ? email : '',
        telefone: hasPhone ? wpp : '',
      });
      if (!customer) return;
      mergeRegisterFromKnownCustomer(customer);
      const label = fromField === 'whatsapp' ? 'WhatsApp' : 'e-mail';
      setRegisterLookupMsg(`Cadastro existente localizado pelo ${label}. Campos preenchidos.`);
    } catch {
      // silencioso para não atrapalhar o fluxo de cadastro
    } finally {
      setRegisterLookupBusy(false);
    }
  }

  async function lookupRegisterCep() {
    const formatted = formatCep(authRegister.cep);
    const digits = cepDigits(formatted);
    if (digits.length !== 8) {
      setRegisterCepLookupError('Digite um CEP válido com 8 números.');
      setRegisterCepLookupMsg('');
      return;
    }
    setRegisterCepLookupBusy(true);
    setRegisterCepLookupMsg('');
    setRegisterCepLookupError('');
    try {
      const res = await fetch(`https://viacep.com.br/ws/${digits}/json/`);
      if (!res.ok) throw new Error('Falha ao consultar o CEP.');
      const row = await res.json();
      if (row?.erro) throw new Error('CEP não encontrado.');
      const nextAddress = composeAddressFromCep(row, formatted);
      if (!nextAddress) throw new Error('Não foi possível preencher o endereço para este CEP.');
      setAuthRegister((prev) => ({ ...prev, cep: formatted, endereco: nextAddress }));
      setRegisterCepLookupMsg('Endereço preenchido. Agora informe número e complemento.');
    } catch (e) {
      setRegisterCepLookupError(e instanceof Error ? e.message : 'Erro ao buscar CEP.');
    } finally {
      setRegisterCepLookupBusy(false);
    }
  }

  function normalizeWhatsappMask(raw, countryCode) {
    const digits = String(raw || '').replace(/\D/g, '').slice(0, countryCode === '+55' ? 11 : 14);
    if (countryCode === '+55') {
      const a = digits.slice(0, 2);
      const b = digits.slice(2, 7);
      const c = digits.slice(7, 11);
      if (!a) return '';
      return `(${a}) ${b}${c ? `-${c}` : ''}`;
    }
    return digits;
  }

  async function submitRegister(ev) {
    ev.preventDefault();
    setAuthError('');
    if (tenantBlocksPublicCustomerRegistration(data?.tenant)) {
      setAuthError('Esta loja não aceita cadastro público. Use o login ou contacte a loja.');
      return;
    }
    const contactErr = validateCustomerContact(
      authRegister.email,
      authRegister.whatsapp,
      getCustomerContactRequirements(data?.tenant),
    );
    if (contactErr) {
      setAuthError(contactErr);
      return;
    }
    setAuthBusy(true);
    try {
      const res = await api(`/store/${encodeURIComponent(slug)}/customer/register`, {
        skipAuth: true,
        method: 'POST',
        body: JSON.stringify(authRegister),
      });
      const token = String(res?.token || '');
      if (!token) throw new Error('Cadastro sem token de sessão.');
      const key = `ominia.customer.${slug}`;
      window.localStorage.setItem(key, token);
      setCustomerToken(token);
      setAuthModalOpen(false);
      setAuthRegister({ nome: '', whatsapp_country_code: '+55', whatsapp: '', email: '', cep: '', endereco: '', senha: '' });
      setRegisterLookupMsg('');
      setRegisterCepLookupMsg('');
      setRegisterCepLookupError('');
    } catch (e) {
      setAuthError(e.message);
    } finally {
      setAuthBusy(false);
    }
  }

  async function submitLogin(ev) {
    ev.preventDefault();
    setAuthError('');
    const loginErr = validateLoginIdentity(authLogin.email, getCustomerContactRequirements(data?.tenant));
    if (loginErr) {
      setAuthError(loginErr);
      return;
    }
    setAuthBusy(true);
    try {
      const res = await api(`/store/${encodeURIComponent(slug)}/customer/login`, {
        skipAuth: true,
        method: 'POST',
        body: JSON.stringify(authLogin),
      });
      const token = String(res?.token || '');
      if (!token) throw new Error('Login sem token de sessão.');
      const key = `ominia.customer.${slug}`;
      window.localStorage.setItem(key, token);
      setCustomerToken(token);
      setAuthModalOpen(false);
    } catch (e) {
      setAuthError(e.message);
    } finally {
      setAuthBusy(false);
    }
  }

  const mustAuth = tenantRequiresCustomerAuth(data?.tenant);
  const blockPublicCustomerRegistration = tenantBlocksPublicCustomerRegistration(data?.tenant);
  const contactReq = useMemo(
    () => getCustomerContactRequirements(data?.tenant),
    [data?.tenant],
  );
  const catalogOnly = Number(data?.tenant?.catalog_only_mode || 0) === 1;
  const catalogNoPhoto = Number(data?.tenant?.catalog_no_photo_mode || 0) === 1;
  const blockSaleZeroStock = Number(data?.tenant?.block_sale_zero_stock || 0) === 1;
  const documentOrderEnabled = tenantDocumentOrderEnabled(data?.tenant) && !catalogOnly;
  const documentOrderAiAvailable = tenantDocumentOrderAiAvailable(data?.tenant);
  /** Sem sessão de cliente: não mostrar valores em R$ em lado nenhum (cards, sacola, barra). */
  const pricingHidden = mustAuth && !customerSession;

  useEffect(() => {
    if (blockPublicCustomerRegistration && authMode === 'register') {
      setAuthMode('login');
    }
  }, [blockPublicCustomerRegistration, authMode]);

  const storeHomeLayoutParsed = useMemo(
    () => parseStoreHomeLayout(data?.tenant?.theme_json),
    [data?.tenant?.theme_json],
  );
  const storeHeroParsed = useMemo(
    () => parseStoreHero(data?.tenant?.theme_json),
    [data?.tenant?.theme_json],
  );
  const resolvedHeroSlides = useMemo(
    () => resolveHeroSlides(data?.tenant?.theme_json),
    [data?.tenant?.theme_json],
  );
  const storeCatalogLuxeParsed = useMemo(
    () => parseStoreCatalogLuxe(data?.tenant?.theme_json),
    [data?.tenant?.theme_json],
  );
  const storeCatalogListParsed = useMemo(
    () => parseStoreCatalogList(data?.tenant?.theme_json),
    [data?.tenant?.theme_json],
  );
  const showCatalogLuxe = Boolean(storeCatalogLuxeParsed.enabled) && Boolean(data);
  const showCatalogList =
    Boolean(storeCatalogListParsed.enabled) && Boolean(data) && !showCatalogLuxe;

  useEffect(() => {
    if (!showCatalogList || !String(filters.secao || '').trim()) return;
    setFilters((f) => ({ ...f, secao: '' }));
  }, [showCatalogList, filters.secao]);

  useLayoutEffect(() => {
    if (typeof window === 'undefined' || showCatalogLuxe) return undefined;
    const root = document.documentElement;
    const measure = () => {
      const headerEl = document.querySelector('[data-ominia-store-header="1"]');
      const barEl = catalogBarRef.current;
      const headerH =
        headerEl instanceof HTMLElement ? Math.ceil(headerEl.getBoundingClientRect().height) : 56;
      const barH = barEl instanceof HTMLElement ? Math.ceil(barEl.getBoundingClientRect().height) : 0;
      root.style.setProperty('--store-header-h', `${headerH}px`);
      root.style.setProperty('--store-catalog-sticky-top', `${headerH + barH}px`);
    };
    measure();
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
    const headerEl = document.querySelector('[data-ominia-store-header="1"]');
    if (ro && headerEl instanceof HTMLElement) ro.observe(headerEl);
    if (ro && catalogBarRef.current) ro.observe(catalogBarRef.current);
    window.addEventListener('resize', measure);
    return () => {
      ro?.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [
    showCatalogLuxe,
    isProductDeepLink,
    filters.secao,
    filters.search,
    catalogOnly,
    customerSession,
    data?.tenant?.nome,
    data?.tenant?.logo_url,
  ]);

  const luxeSectionNames = useMemo(
    () => sectionSidebarRows.map((row) => row.name),
    [sectionSidebarRows],
  );
  const showStoreHomeLayout =
    Boolean(storeHomeLayoutParsed.enabled) &&
    !showCatalogLuxe &&
    !showCatalogList &&
    !isProductDeepLink &&
    !String(filters.search || '').trim() &&
    !String(filters.secao || '').trim();
  const showStoreHeroCarousel =
    shouldShowStoreHero(data?.tenant?.theme_json) &&
    !showCatalogLuxe &&
    !showCatalogList &&
    !isProductDeepLink &&
    !String(filters.search || '').trim() &&
    !String(filters.secao || '').trim();
  /** Hero standalone quando activado sem vitrine modular completa. */
  const showStandaloneHero = showStoreHeroCarousel && !showStoreHomeLayout;

  const t = totals();
  const contactWhatsDigits = String(data?.tenant?.contact_whatsapp || '').replace(/\D+/g, '');
  const contactWhatsDisplay = formatPhoneDisplayBr(data?.tenant?.contact_whatsapp || '');
  const footerSpeakItems = [
    contactWhatsDigits
      ? {
          icon: <WhatsAppIcon className="h-5 w-5 text-[#25D366]" />,
          label: 'Fale com o vendedor',
          value: contactWhatsDisplay || String(data?.tenant?.contact_whatsapp || ''),
          href: `https://wa.me/${contactWhatsDigits}`,
        }
      : null,
    data?.tenant?.contact_opening_hours
      ? { icon: '🕒', label: 'Atendimento', value: String(data.tenant.contact_opening_hours) }
      : null,
  ].filter(Boolean);
  const contactAddrRaw = data?.tenant?.contact_address ? String(data.tenant.contact_address).trim() : '';
  const contactMapsRaw = data?.tenant?.contact_google_maps_url
    ? String(data.tenant.contact_google_maps_url).trim()
    : '';
  const footerAddressBlock =
    contactAddrRaw !== '' || contactMapsRaw !== ''
      ? { address: contactAddrRaw !== '' ? contactAddrRaw : null, mapsUrl: contactMapsRaw !== '' ? contactMapsRaw : null }
      : null;
  const footerSocialItems = [
    data?.tenant?.contact_facebook_url
      ? { icon: <FacebookIcon className="h-6 w-6" />, label: 'Facebook', href: String(data.tenant.contact_facebook_url) }
      : null,
    data?.tenant?.contact_instagram_url
      ? { icon: <InstagramIcon className="h-6 w-6" />, label: 'Instagram', href: String(data.tenant.contact_instagram_url) }
      : null,
    data?.tenant?.contact_youtube_url
      ? { icon: <YoutubeIcon className="h-6 w-6" />, label: 'YouTube', href: String(data.tenant.contact_youtube_url) }
      : null,
  ].filter(Boolean);
  const showFooterPanel =
    footerSpeakItems.length > 0 || footerAddressBlock != null || footerSocialItems.length > 0;
  const showSpeakCol = footerSpeakItems.length > 0;
  const showAddressCol = footerAddressBlock != null;
  const footerDetailGridClass =
    showSpeakCol && showAddressCol
      ? 'grid-cols-1 gap-2 sm:grid-cols-2 sm:gap-3 lg:grid-cols-3 lg:gap-3'
      : showSpeakCol || showAddressCol
        ? 'grid-cols-1 gap-2 sm:grid-cols-2 sm:gap-3'
        : 'grid-cols-1 gap-2';

  const cartLines = useMemo(() => {
    const out = [];
    for (const rawId of Object.keys(cart)) {
      const q = cart[rawId];
      if (!q || q <= 0) continue;
      const pid = Number(rawId);
      const p = catalogById[pid];
      if (!p) continue;
      out.push({ p, q, line: Number(p.preco) * q });
    }
    return out;
  }, [cart, catalogById]);

  /** Remove do mapa de selecção entradas que já não estão no cart. */
  useEffect(() => {
    setCartSelection((prev) => {
      const next = { ...prev };
      let changed = false;
      for (const sid of Object.keys(next)) {
        if (!cart[sid] || cart[sid] <= 0) {
          delete next[sid];
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [cart]);

  const storeTracking = useStoreTracking({
    slug: slugKey,
    themeJson: data?.tenant?.theme_json,
    productFocusId: isProductDeepLink ? productFocusId : null,
    drawerOpen,
    cartLines,
    resolveProduct: productById,
  });
  storeTrackingRef.current = storeTracking;

  const storePwa = useStorePwa({ slug: slugKey, tenant: data?.tenant });

  const selectedCartLines = useMemo(
    () => cartLines.filter(({ p }) => isItemSelected(p.id)),
    [cartLines, isItemSelected],
  );

  const setItemSelected = useCallback((pid, selected) => {
    const sid = String(pid);
    setCartSelection((prev) => {
      const isSelected = !(sid in prev) ? true : Boolean(prev[sid]);
      const desired = Boolean(selected);
      if (isSelected === desired) return prev;
      const next = { ...prev };
      if (desired) {
        delete next[sid];
      } else {
        next[sid] = false;
      }
      return next;
    });
  }, []);

  const selectAllCartItems = useCallback(() => {
    setCartSelection({});
  }, []);

  const deselectAllCartItems = useCallback(() => {
    setCartSelection(() => {
      const next = {};
      for (const sid of Object.keys(cart)) {
        if (cart[sid] > 0) next[sid] = false;
      }
      return next;
    });
  }, [cart]);

  /** Sem barra fixa de sacola no mobile: menos padding inferior; rodapé minimizado ocupa menos altura. */
  const shellCls = [
    'min-h-screen bg-zinc-50 text-zinc-900 dark:bg-zinc-950 dark:text-zinc-100',
    showFooterPanel && footerMinimized ? 'pb-20 max-lg:pb-20 lg:pb-20' : 'pb-36 max-lg:pb-36 lg:pb-28',
  ].join(' ');

  function toggleFooterMinimized() {
    setFooterMinimized((prev) => {
      const next = !prev;
      try {
        if (slug && typeof sessionStorage !== 'undefined') {
          sessionStorage.setItem(`ominia-store-footer-min:${slug}`, next ? '1' : '0');
        }
      } catch {
        /* ignore */
      }
      return next;
    });
  }

  function confirmAdultAge() {
    if (!ageStorageKey) return;
    try {
      sessionStorage.setItem(ageStorageKey, '1');
    } catch {
      /* quota / modo privado */
    }
    setAgeConfirmed(true);
  }

  function declineAdultAge() {
    if (!slug) return;
    navigate(`/${encodeURIComponent(slug)}/acesso-idade`, { replace: true });
  }

  return (
    <div className={shellCls} style={storeAccentStyle}>
      {showAgeGate ? (
        <div
          className="fixed inset-0 z-[90] flex items-center justify-center p-4 sm:p-6"
          role="dialog"
          aria-modal="true"
          aria-labelledby="age-gate-title"
        >
          <div className="absolute inset-0 bg-zinc-950/70 backdrop-blur-md dark:bg-black/80" aria-hidden />
          <div className="relative z-[1] w-full max-w-[min(100%,26rem)] overflow-hidden rounded-[1.75rem] border border-zinc-200/90 bg-gradient-to-b from-white via-white to-zinc-50/95 shadow-[0_28px_90px_-24px_rgba(0,0,0,0.45)] dark:border-zinc-700/70 dark:from-zinc-900 dark:via-zinc-900 dark:to-zinc-950/95">
            <div
              className="pointer-events-none absolute -left-24 top-0 h-48 w-48 rounded-full opacity-90 blur-2xl"
              style={{
                background: 'radial-gradient(circle at 40% 40%, rgb(var(--st-rgb) / 0.38), transparent 62%)',
              }}
            />
            <div className="pointer-events-none absolute -bottom-20 -right-16 h-52 w-52 rounded-full bg-rose-500/15 blur-3xl dark:bg-rose-500/10" />
            <div className="relative px-6 pb-7 pt-8 sm:px-8 sm:pb-8 sm:pt-9">
              <p className="text-center text-[10px] font-bold uppercase tracking-[0.35em] text-zinc-400 dark:text-zinc-500">
                Acesso à loja
              </p>
              <h2
                id="age-gate-title"
                className="mt-3 text-center font-display text-2xl font-bold leading-tight text-zinc-900 dark:text-zinc-50 sm:text-[1.65rem]"
              >
                Confirme se tem 18 anos ou mais
              </h2>
              <p className="mt-3 text-center text-sm leading-relaxed text-zinc-600 dark:text-zinc-400">
                Esta vitrine pode incluir produtos ou mensagens dirigidas a adultos. Para continuar, confirme a sua
                idade.
              </p>
              <div className="mt-8 flex flex-col gap-3">
                <button
                  type="button"
                  onClick={confirmAdultAge}
                  className="w-full rounded-2xl bg-[rgb(var(--st-rgb))] px-4 py-3.5 text-center text-sm font-bold text-white shadow-[0_12px_36px_-12px_rgb(var(--st-rgb)/0.75)] transition hover:brightness-105 active:scale-[0.99]"
                >
                  Sim, sou maior de idade
                </button>
                <button
                  type="button"
                  onClick={declineAdultAge}
                  className="w-full rounded-2xl border border-zinc-200 bg-white/80 px-4 py-3.5 text-center text-sm font-semibold text-zinc-800 transition hover:bg-zinc-50 dark:border-zinc-600 dark:bg-zinc-800/60 dark:text-zinc-100 dark:hover:bg-zinc-800"
                >
                  Não, sou menor de idade
                </button>
              </div>
              <p className="mt-5 text-center text-[11px] leading-relaxed text-zinc-500 dark:text-zinc-500">
                A confirmação é memorizada apenas neste navegador (sessão). Não substitui verificação legal onde for
                exigida.
              </p>
            </div>
          </div>
        </div>
      ) : null}
      {/* Top: uma só linha — logo/nome à esquerda, sessão + tema à direita (altura fixa, sem empilhar). */}
      <header
        data-ominia-store-header="1"
        className="sticky top-0 z-[35] border-b border-zinc-200/70 bg-white/75 backdrop-blur-xl dark:border-zinc-800/75 dark:bg-zinc-900/70 max-lg:border-b-0 max-lg:bg-white max-lg:backdrop-blur-none max-lg:dark:bg-zinc-950 max-lg:pt-[env(safe-area-inset-top,0px)]"
      >
        <div className="mx-auto flex max-w-7xl flex-row items-center justify-between gap-2 px-3 py-2 sm:gap-3 sm:px-4 sm:py-2.5">
          {slug ? (
            <Link
              to={`/${encodeURIComponent(slug)}`}
              className="group flex min-w-0 flex-1 items-center gap-2 rounded-lg py-0.5 outline-none transition hover:opacity-90 focus-visible:ring-2 focus-visible:ring-[rgb(var(--st-rgb)/0.45)] focus-visible:ring-offset-2 focus-visible:ring-offset-white dark:focus-visible:ring-offset-zinc-900 sm:gap-3 sm:pr-1"
              aria-label="Voltar à vitrine da loja"
              title="Voltar à vitrine da loja"
            >
              {data?.tenant?.logo_url ? (
                <ResilientImage
                  src={resolveApiMediaUrl(String(data.tenant.logo_url))}
                  alt=""
                  className="h-10 w-auto max-w-[4.5rem] shrink-0 object-contain transition group-hover:brightness-95 dark:group-hover:brightness-110 sm:h-[52px] sm:max-w-[182px] md:max-w-[234px]"
                  loading="eager"
                  retries={1}
                />
              ) : null}
              <div className="min-w-0 flex-1 text-left">
                <p className="truncate text-[9px] font-bold uppercase tracking-[0.18em] text-zinc-400 dark:text-zinc-500 sm:text-[10px] sm:tracking-[0.28em]">
                  Loja · {slug}
                </p>
                <p className="truncate font-display text-sm font-semibold leading-tight text-zinc-900 underline-offset-2 group-hover:underline dark:text-zinc-50 sm:text-lg">
                  {data?.tenant?.nome ?? 'Catálogo'}
                </p>
              </div>
            </Link>
          ) : (
            <div className="flex min-w-0 flex-1 items-center gap-2 sm:gap-3">
              {data?.tenant?.logo_url ? (
                <ResilientImage
                  src={resolveApiMediaUrl(String(data.tenant.logo_url))}
                  alt=""
                  className="h-10 w-auto max-w-[4.5rem] shrink-0 object-contain sm:h-[52px] sm:max-w-[182px]"
                  loading="eager"
                  retries={1}
                />
              ) : null}
              <div className="min-w-0 flex-1">
                <p className="text-[9px] font-bold uppercase tracking-[0.28em] text-zinc-400 dark:text-zinc-500 sm:text-[10px]">
                  Catálogo
                </p>
                <p className="truncate font-display text-sm font-semibold leading-tight text-zinc-900 dark:text-zinc-50 sm:text-lg">
                  {data?.tenant?.nome ?? 'Catálogo'}
                </p>
              </div>
            </div>
          )}
          <div className="flex shrink-0 items-center gap-1.5 sm:gap-2">
            {customerSession ? (
              <div
                className="flex max-w-[min(10.5rem,38vw)] min-w-0 items-center gap-1 rounded-full border border-zinc-200/70 bg-zinc-50/90 px-1.5 py-0.5 shadow-sm dark:border-zinc-600/60 dark:bg-zinc-800/55 sm:max-w-[14rem] sm:gap-1.5 sm:px-2 sm:py-1"
                title={String(customerSession.nome || '').trim() || undefined}
              >
                <span className="min-w-0 flex-1 truncate text-left text-[10px] font-medium leading-tight text-zinc-700 dark:text-zinc-200 sm:text-[11px]">
                  {String(customerSession.nome || '').trim() || 'Cliente'}
                </span>
                <Link
                  to={`/${encodeURIComponent(slug)}/conta`}
                  className="shrink-0 rounded-md px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-[var(--st-700)] underline-offset-2 hover:underline dark:text-[var(--st-400)]"
                >
                  Conta
                </Link>
                <button
                  type="button"
                  onClick={logoutCustomer}
                  aria-label="Sair da conta nesta loja"
                  title="Sair"
                  className="group/door flex h-7 w-7 shrink-0 items-center justify-center rounded-md border border-transparent transition hover:border-[rgb(var(--st-rgb)/0.25)] hover:bg-[rgb(var(--st-rgb)/0.08)] sm:h-8 sm:w-8 dark:hover:border-[rgb(var(--st-rgb)/0.3)] dark:hover:bg-[rgb(var(--st-rgb)/0.12)]"
                >
                  <OminiaExitDoorIcon className="h-[1.15rem] w-[1.15rem] transition group-hover/door:scale-105 sm:h-[1.35rem] sm:w-[1.35rem]" />
                </button>
              </div>
            ) : null}
            {documentOrderEnabled ? (
              <DocumentOrderEntryButton onClick={() => setDocumentOrderOpen(true)} />
            ) : null}
            {customerSession?.can_download_catalog ? (
              <CatalogExportButton onClick={() => setCatalogExportOpen(true)} />
            ) : null}
            <StorePwaInstallButton
              canShowInstall={storePwa.canShowInstall}
              canInstallNative={storePwa.canInstallNative}
              onInstall={() => {
                void storePwa.install();
              }}
            />
            <button
              type="button"
              onClick={toggle}
              aria-label={scheme === 'dark' ? 'Ativar modo claro' : 'Ativar modo escuro'}
              title={scheme === 'dark' ? 'Modo claro' : 'Modo escuro'}
              className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-zinc-200 text-zinc-600 transition hover:border-[rgb(var(--st-rgb)/0.4)] hover:bg-[rgb(var(--st-rgb)/0.08)] hover:text-[var(--st-700)] sm:h-10 sm:w-10 dark:border-zinc-600 dark:text-zinc-300 dark:hover:border-[rgb(var(--st-rgb)/0.35)] dark:hover:bg-zinc-800/90 dark:hover:text-[var(--st-400)]"
            >
              {scheme === 'dark' ? <SunIcon className="h-[18px] w-[18px] sm:h-5 sm:w-5" /> : <MoonIcon className="h-[18px] w-[18px] sm:h-5 sm:w-5" />}
            </button>
            <p className="hidden text-xs text-zinc-500 sm:block dark:text-zinc-400">
              tecnologia{' '}
              <Link className="font-display font-semibold" to="/">
                <span className="text-gradient">Omin</span>
                <span className="text-gradient-ia">IA</span>
              </Link>
            </p>
          </div>
        </div>
      </header>

      <StorePwaInstallHint
        open={storePwa.hintOpen}
        hintKind={storePwa.hintKind}
        tenantName={data?.tenant?.nome ? String(data.tenant.nome) : 'esta loja'}
        onClose={storePwa.dismissHint}
      />

      {mpReturnBanner ? (
        <div
          className={`border-b px-4 py-3 text-center text-sm ${
            mpReturnBannerTone === 'warning'
              ? 'border-amber-200 bg-amber-50 text-amber-950 dark:border-amber-800/60 dark:bg-amber-950/40 dark:text-amber-50'
              : 'border-emerald-200 bg-emerald-50 text-emerald-950 dark:border-emerald-800 dark:bg-emerald-950/50 dark:text-emerald-50'
          }`}
        >
          <p>{mpReturnBanner}</p>
          <button
            type="button"
            className={`mt-2 text-xs font-semibold underline underline-offset-2 ${
              mpReturnBannerTone === 'warning'
                ? 'text-amber-800 dark:text-amber-200'
                : 'text-emerald-800 dark:text-emerald-200'
            }`}
            onClick={() => setMpReturnBanner('')}
          >
            Fechar aviso
          </button>
        </div>
      ) : null}

      {showCatalogLuxe ? (
        <StoreCatalogLuxeView
          config={storeCatalogLuxeParsed}
          tenant={data.tenant}
          slug={slug}
          products={sortedProducts}
          sections={luxeSectionNames}
          filters={filters}
          setFilters={setFilters}
          cart={cart}
          addQty={addQty}
          formatBrl={formatBrl}
          pricingHidden={pricingHidden}
          catalogOnly={catalogOnly}
          blockSaleZeroStock={blockSaleZeroStock}
          cartCount={t.count}
          cartTotal={t.total}
          onOpenDrawer={() => setDrawerOpen(true)}
          onRequireLogin={() => setAuthModalOpen(true)}
          onOpenMediaPreview={setMediaPreview}
          productImageCtx={productImageCtx}
          productStockCap={(p) => productTrackedStock(p)}
          deepLinkProductId={productFocusId ?? 0}
        />
      ) : (
      <div className="mx-auto flex max-w-7xl flex-col gap-6 px-4 pt-0 pb-6 sm:pt-2 lg:flex-row lg:items-start lg:gap-8 lg:overflow-x-visible lg:px-4 lg:py-6">
        {/* Sidebar categorias — sempre visível no desktop: sticky abaixo do header + scroll próprio */}
        <aside
          id="store-categorias"
          aria-label="Categorias da loja"
          className={`min-w-[13rem] max-w-[15rem] flex-shrink-0 basis-52 overflow-x-visible lg:sticky lg:top-[calc(3.5rem+env(safe-area-inset-top,0px))] lg:z-20 lg:max-h-[calc(100dvh-4.75rem-env(safe-area-inset-bottom,0px))] lg:overflow-y-auto lg:overscroll-contain lg:pr-1 lg:[scrollbar-gutter:stable] xl:min-w-[14rem] xl:basis-56 ${isProductDeepLink || showCatalogList ? 'hidden' : 'hidden lg:block'}`}
        >
          <p className="mb-3 shrink-0 text-[11px] font-bold uppercase tracking-wider text-zinc-400 dark:text-zinc-500">
            Categorias
          </p>
          <nav className="min-w-0 space-y-0.5 pb-2">
            <button
              type="button"
              onClick={() => setFilters((f) => ({ ...f, secao: '' }))}
              className={`flex w-full items-center justify-between rounded-lg px-3 py-2.5 text-left text-sm transition-colors ${!filters.secao ? catActiveCls : 'text-zinc-700 hover:bg-zinc-100 dark:text-zinc-300 dark:hover:bg-zinc-800'}`}
            >
              <span>Todas</span>
              <span className="text-xs opacity-70">{catalogTotalForTodas}</span>
            </button>
            {sectionSidebarRows.map(({ name, count, imagem }) => (
              <button
                key={name}
                type="button"
                title={name}
                onClick={() => setFilters((f) => ({ ...f, secao: name }))}
                className={`flex w-full items-center justify-between gap-2 rounded-lg px-3 py-2.5 text-left text-sm font-medium leading-snug tracking-tight transition-colors ${filters.secao === name ? catActiveCls : 'text-zinc-600 hover:bg-zinc-100 dark:text-zinc-400 dark:hover:bg-zinc-800'}`}
              >
                <span className="flex min-w-0 flex-1 items-center gap-2 pr-2">
                  {imagem ? (
                    <img
                      src={resolveApiMediaUrl(imagem)}
                      alt=""
                      className="h-8 w-8 shrink-0 rounded-lg border border-zinc-200/80 object-cover dark:border-zinc-700"
                    />
                  ) : null}
                  <span className="min-w-0 break-words normal-case text-pretty">{name}</span>
                </span>
                <span className="shrink-0 tabular-nums text-xs opacity-70">({count})</span>
              </button>
            ))}
          </nav>
          <Link
            to="/"
            className="mt-6 inline-flex text-xs font-medium text-[var(--st-700)] underline-offset-4 hover:underline dark:text-[var(--st-400)]"
          >
            ← Voltar ao site
          </Link>
        </aside>

        {/* Catálogo */}
        <main
          ref={mainCatalogRef}
          className="min-w-0 flex-1 space-y-5 scroll-mt-[var(--store-catalog-sticky-top,4.75rem)]"
        >
          {/* Barra de catálogo: no mobile cola ao header (sem vão); fundo opaco para não ver produtos por baixo. */}
          <section
            ref={catalogBarRef}
            data-ominia-store-catalog-bar="1"
            className="-mx-4 sticky top-[var(--store-header-h,3.5rem)] z-[32] border-b border-zinc-200/70 bg-zinc-50 px-4 py-1 shadow-[0_1px_0_rgba(0,0,0,0.04)] dark:border-zinc-800/70 dark:bg-zinc-950 dark:shadow-[0_1px_0_rgba(255,255,255,0.04)] sm:static sm:top-0 sm:z-0 sm:mx-0 sm:rounded-none sm:border-0 sm:bg-transparent sm:px-0 sm:py-0 sm:shadow-none sm:backdrop-blur-0"
          >
            <div className="mx-auto max-w-none space-y-1 sm:space-y-2 xl:max-w-none">
              {isProductDeepLink ? (
                <div className="flex flex-row flex-wrap items-center justify-between gap-3">
                  <button
                    type="button"
                    onClick={() => navigate(`/${encodeURIComponent(slug)}`, { replace: true })}
                    className="inline-flex min-w-0 max-w-full flex-1 basis-[12rem] items-center rounded-2xl border border-zinc-200 bg-white px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-[var(--st-700)] shadow-sm transition hover:border-[rgb(var(--st-rgb)/0.5)] hover:bg-zinc-50 sm:flex-initial dark:border-zinc-600 dark:bg-zinc-900 dark:text-[var(--st-400)] dark:hover:bg-zinc-800/85"
                  >
                    ← Ver vitrine completa da loja
                  </button>
                  {!catalogOnly ? (
                    <button
                      type="button"
                      disabled={t.count === 0}
                      onClick={() => t.count > 0 && setDrawerOpen(true)}
                      aria-label="Sacola e pedido"
                      className="flex min-h-[44px] shrink-0 items-center gap-2 rounded-2xl border border-zinc-200 bg-white px-3 py-2.5 text-left text-xs font-semibold shadow-sm transition hover:border-[rgb(var(--st-rgb)/0.4)] hover:bg-zinc-50 enabled:cursor-pointer disabled:cursor-not-allowed disabled:opacity-45 sm:px-4 dark:border-zinc-700 dark:bg-zinc-900 dark:hover:border-[rgb(var(--st-rgb)/0.3)] dark:hover:bg-zinc-800/80"
                    >
                      <BagIcon className="h-[18px] w-[18px] text-[var(--st-600)] dark:text-[var(--st-400)]" aria-hidden />
                      <span className="hidden text-zinc-500 sm:inline dark:text-zinc-400">Seu pedido</span>
                      <span className="rounded-full bg-red-600 px-2 py-0.5 text-[10px] font-bold tabular-nums text-white">{t.count}</span>
                      <span className="max-w-[5.5rem] truncate tabular-nums text-zinc-900 sm:max-w-none dark:text-white">
                        {pricingHidden ? '—' : formatBrl(t.total)}
                      </span>
                    </button>
                  ) : null}
                </div>
              ) : (
                <>
                  <div className="relative rounded-[1.2rem] border border-[rgb(var(--st-rgb)/0.3)] bg-white p-0.5 shadow-[var(--st-sh-search)] ring-1 ring-zinc-900/[0.04] dark:border-[rgb(var(--st-rgb)/0.25)] dark:bg-zinc-900 dark:shadow-[var(--st-sh-search-d)] dark:ring-white/[0.06]">
                    <label className="relative flex items-center gap-2 rounded-[1.05rem] bg-zinc-50/70 px-1.5 py-0.5 dark:bg-zinc-900/50">
                      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[var(--st-500)] text-white shadow-inner shadow-black/10 dark:bg-[var(--st-600)]">
                        <SearchIcon className="h-5 w-5" aria-hidden />
                      </span>
                      <input
                        type="search"
                        autoComplete="off"
                        aria-label="Buscar produtos no catálogo"
                        className="min-h-[2.375rem] w-full min-w-0 flex-1 border-0 bg-transparent py-1.5 pr-14 text-[13px] text-zinc-900 outline-none placeholder:text-zinc-400 focus:ring-0 sm:min-h-[2.75rem] sm:pr-11 sm:text-[15px] dark:text-zinc-50 dark:placeholder:text-zinc-500"
                        placeholder="Buscar por nome ou código…"
                        value={filters.search}
                        onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                      />
                      {filters.search ? (
                        <button
                          type="button"
                          aria-label="Limpar pesquisa"
                          className="absolute right-3 rounded-full px-3 py-1.5 text-xs font-semibold text-zinc-500 hover:bg-zinc-200/80 hover:text-zinc-800 dark:hover:bg-zinc-800 dark:hover:text-zinc-200"
                          onClick={() => setFilters({ ...filters, search: '' })}
                        >
                          Limpar
                        </button>
                      ) : null}
                    </label>
                  </div>

                  <div
                    className={`flex flex-row flex-wrap items-center gap-2 sm:gap-3 ${catalogOnly ? 'justify-end' : 'justify-between'}`}
                  >
                    {!catalogOnly ? (
                      <button
                        type="button"
                        disabled={t.count === 0}
                        onClick={() => t.count > 0 && setDrawerOpen(true)}
                        aria-label="Sacola e pedido"
                        className="flex min-h-[44px] min-w-0 flex-1 items-center gap-2 rounded-2xl border border-zinc-200 bg-white px-3 py-2.5 text-left text-xs font-semibold shadow-sm transition hover:border-[rgb(var(--st-rgb)/0.4)] hover:bg-zinc-50 enabled:cursor-pointer disabled:cursor-not-allowed disabled:opacity-45 sm:max-w-md sm:flex-initial sm:px-4 dark:border-zinc-700 dark:bg-zinc-900 dark:hover:border-[rgb(var(--st-rgb)/0.3)] dark:hover:bg-zinc-800/80"
                      >
                        <BagIcon className="h-[18px] w-[18px] shrink-0 text-[var(--st-600)] dark:text-[var(--st-400)]" aria-hidden />
                        <span className="hidden shrink-0 text-zinc-500 sm:inline dark:text-zinc-400">Seu pedido</span>
                        <span className="shrink-0 rounded-full bg-red-600 px-2 py-0.5 text-[10px] font-bold tabular-nums text-white">
                          {t.count}
                        </span>
                        <span className="min-w-0 flex-1 truncate text-right tabular-nums text-zinc-900 sm:text-left dark:text-white">
                          {pricingHidden ? '—' : formatBrl(t.total)}
                        </span>
                      </button>
                    ) : null}

                    <div className="flex shrink-0 justify-end">
                      <button
                        type="button"
                        onClick={() => setSortAz((v) => !v)}
                        className={`flex min-h-[2.75rem] shrink-0 flex-col items-center justify-center gap-0 rounded-2xl border px-2 py-1.5 text-[10px] font-semibold uppercase leading-none tracking-tight transition-colors sm:min-h-0 sm:flex-row sm:gap-1 sm:px-5 sm:py-2.5 sm:text-xs sm:tracking-wide ${sortAz ? 'border-[var(--st-600)] bg-[var(--st-600)] text-white shadow-[var(--st-sh-sort)] dark:border-[var(--st-500)] dark:bg-[var(--st-600)]' : 'border-zinc-200 text-zinc-600 hover:bg-zinc-100 dark:border-zinc-600 dark:text-zinc-300 dark:hover:bg-zinc-800'}`}
                      >
                        <span>Ordenar</span>
                        <span className="font-black tabular-nums">
                          A–Z{sortAz ? ' ✓' : ''}
                        </span>
                      </button>
                    </div>
                  </div>

                  {!showCatalogList && String(filters.secao || '').trim() !== '' ? (
                    <div
                      role="status"
                      aria-live="polite"
                      className="flex flex-col gap-2.5 rounded-2xl border border-[rgb(var(--st-rgb)/0.28)] bg-[rgb(var(--st-rgb)/0.07)] px-3 py-2.5 text-[11px] leading-snug text-zinc-800 shadow-sm dark:border-[rgb(var(--st-rgb)/0.22)] dark:bg-[rgb(var(--st-rgb)/0.1)] dark:text-zinc-100 sm:flex-row sm:items-center sm:justify-between sm:gap-4 sm:text-xs"
                    >
                      <p className="min-w-0 flex-1">
                        <span className="font-bold text-[var(--st-700)] dark:text-[var(--st-400)]">Só esta seção:</span>{' '}
                        a lista e a pesquisa estão limitadas a{' '}
                        <span className="font-semibold">«{String(filters.secao).trim()}»</span>.
                        {String(filters.search || '').trim() !== '' ? (
                          <>
                            {' '}
                            O termo{' '}
                            <span className="break-all font-mono font-semibold">
                              «{String(filters.search).trim()}»
                            </span>{' '}
                            filtra apenas dentro dela.
                          </>
                        ) : null}{' '}
                        Para voltar a ver e pesquisar em <span className="font-semibold">toda a loja</span>, escolha{' '}
                        <span className="font-semibold">Todas</span> nas categorias ou use o botão.
                      </p>
                      <button
                        type="button"
                        className="shrink-0 self-stretch rounded-xl border border-[rgb(var(--st-rgb)/0.38)] bg-white px-3 py-2 text-center text-[11px] font-bold text-[var(--st-700)] transition hover:bg-[rgb(var(--st-rgb)/0.12)] sm:self-center dark:border-[rgb(var(--st-rgb)/0.35)] dark:bg-zinc-900/90 dark:text-[var(--st-300)] dark:hover:bg-zinc-800"
                        onClick={() => setFilters((f) => ({ ...f, secao: '' }))}
                      >
                        Ver toda a loja
                      </button>
                    </div>
                  ) : null}
                </>
              )}
            </div>
          </section>

          {/* Chips mobile categorias */}
          <div className={`flex gap-2 overflow-x-auto pb-1 lg:hidden ${isProductDeepLink || showCatalogList ? 'hidden' : ''}`}>
            <button
              type="button"
              onClick={() => setFilters((f) => ({ ...f, secao: '' }))}
              className={`shrink-0 rounded-full px-4 py-2 text-xs font-semibold transition ${!filters.secao ? `${catActiveCls} shadow-md` : 'border border-transparent bg-zinc-200/80 dark:bg-zinc-800'}`}
            >
              Todas
            </button>
            {sectionSidebarRows.map(({ name, imagem }) => (
              <button
                key={name}
                type="button"
                onClick={() => setFilters((f) => ({ ...f, secao: name }))}
                className={`flex max-w-[min(18rem,calc(100vw-6rem))] shrink-0 items-center gap-1.5 rounded-full px-3 py-2 text-center text-xs font-semibold leading-snug tracking-tight transition [overflow-wrap:anywhere] ${filters.secao === name ? `${catActiveCls} shadow-md` : 'border border-transparent bg-zinc-200/80 dark:bg-zinc-800'}`}
                title={name}
              >
                {imagem ? (
                  <img
                    src={resolveApiMediaUrl(imagem)}
                    alt=""
                    className="h-6 w-6 shrink-0 rounded-full border border-white/40 object-cover"
                  />
                ) : null}
                <span className="normal-case">{name}</span>
              </button>
            ))}
          </div>

          {showStandaloneHero && data ? (
            <div className="mb-4">
              <StoreHeroCarousel
                slides={resolvedHeroSlides}
                autoplaySeconds={storeHeroParsed.autoplaySeconds}
                onNavigateToSection={(secao) => {
                  setFilters((f) => ({ ...f, secao: secao || '', search: '' }));
                  window.requestAnimationFrame(() => {
                    scrollStoreMainBelowHeader('smooth');
                  });
                }}
                onOpenProduct={(id) => {
                  navigate(`/${encodeURIComponent(slug)}?p=${id}`);
                }}
              />
            </div>
          ) : null}

          {showStoreHomeLayout && data ? (
            <StoreHomeLayout
              layout={storeHomeLayoutParsed}
              slug={slug}
              catalogById={catalogById}
              productImageCtx={productImageCtx}
              pricingHidden={pricingHidden}
              formatBrl={formatBrl}
              autoplaySeconds={storeHeroParsed.autoplaySeconds}
              heroSlidesOverride={resolvedHeroSlides}
              onNavigateToSection={(secao) => {
                setFilters((f) => ({ ...f, secao: secao || '', search: '' }));
                window.requestAnimationFrame(() => {
                  scrollStoreMainBelowHeader('smooth');
                });
              }}
              onOpenProduct={(id) => {
                navigate(`/${encodeURIComponent(slug)}?p=${id}`);
              }}
            />
          ) : null}

          {!data && <p className="py-16 text-center text-sm text-zinc-500">Carregando vitrine…</p>}
          {error && (
            <p className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-medium text-red-800 dark:border-red-900/60 dark:bg-red-950/50 dark:text-red-200">
              {error}
            </p>
          )}
          {data && isProductDeepLink && sortedProducts.length === 0 ? (
            <div className="rounded-2xl border border-zinc-200 bg-white px-4 py-10 text-center dark:border-zinc-700 dark:bg-zinc-900/80">
              <p className="text-sm font-medium text-zinc-700 dark:text-zinc-200">Produto não encontrado nesta vitrine ou indisponível.</p>
              <button
                type="button"
                className="mt-4 rounded-xl bg-[var(--st-600)] px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-[var(--st-700)]"
                onClick={() => navigate(`/${encodeURIComponent(slug)}`, { replace: true })}
              >
                Ver vitrine completa
              </button>
            </div>
          ) : null}

          <div
            className={`pt-2 ${
              isProductDeepLink
                ? 'mx-auto grid max-w-md grid-cols-1 gap-6 gap-y-10'
                : showCatalogList
                  ? ''
                  : 'grid grid-cols-1 gap-6 gap-y-12 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4'
            }`}
          >
            {showCatalogList ? (
              <StoreCatalogList
                products={sortedProducts}
                cart={cart}
                config={storeCatalogListParsed}
                sectionImages={sectionImageByName}
                productImageCtx={productImageCtx}
                addQty={addQty}
                setProductQty={setProductQty}
                formatBrl={formatBrl}
                pricingHidden={pricingHidden}
                catalogOnly={catalogOnly}
                onOpenProductMedia={openListProductMedia}
                onRequireLogin={() => setAuthModalOpen(true)}
              />
            ) : (
            sortedProducts.map((p) => {
              const q = cart[p.id] || 0;
              const soldOutRetail = productIsSoldOut(p);
              const erp = p.codigo_erp != null && String(p.codigo_erp).trim() !== '' ? String(p.codigo_erp).trim() : '';
              const code = erp || String(p.id).padStart(6, '0');
              return (
                <article
                  id={`store-produto-${p.id}`}
                  key={p.id}
                  className={`group group/cardimg relative z-0 overflow-visible rounded-2xl border bg-white shadow-sm transition-[transform,box-shadow] duration-200 hover:z-[25] hover:-translate-y-1 hover:shadow-xl dark:bg-zinc-900 ${q > 0 ? 'border-[var(--st-400)] ring-1 ring-[rgb(var(--st-rgb)/0.3)] dark:border-[rgb(var(--st-rgb)/0.5)] dark:ring-[rgb(var(--st-rgb)/0.2)]' : 'border-zinc-100 dark:border-zinc-800'}`}
                >
                  {!catalogNoPhoto ? (
                    <div className="relative flex aspect-[4/3] w-full items-center justify-center overflow-hidden rounded-t-2xl bg-zinc-100 p-2 sm:p-3 dark:bg-zinc-800/80">
                      {(() => {
                        const galleryUrls = productGalleryUrls(p, productImageCtx);
                        const yt = productYoutubeUrl(p);
                        const hasVideo = youtubeVideoId(yt);
                        if (galleryUrls.length > 0) {
                          return (
                            <ProductCardFadeGallery
                              urls={galleryUrls}
                              onOpen={(meta) =>
                                setMediaPreview({
                                  title: p.nome || 'Produto',
                                  imageUrls: galleryUrls,
                                  youtubeUrl: yt,
                                  startIndex: typeof meta?.startIndex === 'number' ? meta.startIndex : 0,
                                  startTab: 'imagens',
                                  shareUrl: buildStoreProductShareUrl(location.pathname, p),
                                  shareOgUrl: buildStoreProductOgUrl(data?.tenant?.slug, p.id),
                                  shareTitle: p.nome || 'Produto',
                                })
                              }
                            />
                          );
                        }
                        if (hasVideo) {
                          return (
                            <button
                              type="button"
                              aria-label={`Ver vídeo: ${p.nome || 'produto'}`}
                              title="Ver vídeo do produto"
                              className="relative z-[1] flex h-full min-h-[7rem] w-full cursor-pointer flex-col items-center justify-center gap-2 rounded-xl border border-dashed border-[rgb(var(--st-rgb)/0.35)] bg-zinc-50/90 p-4 text-center text-[11px] font-bold uppercase tracking-wide text-[var(--st-700)] transition hover:bg-zinc-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--st-500)] focus-visible:ring-offset-2 dark:border-[rgb(var(--st-rgb)/0.38)] dark:bg-zinc-900/60 dark:text-[var(--st-400)] dark:hover:bg-zinc-800/85 dark:focus-visible:ring-offset-zinc-800"
                              onClick={() =>
                                setMediaPreview({
                                  title: p.nome || 'Produto',
                                  imageUrls: [],
                                  youtubeUrl: yt,
                                  startIndex: 0,
                                  startTab: 'video',
                                  shareUrl: buildStoreProductShareUrl(location.pathname, p),
                                  shareOgUrl: buildStoreProductOgUrl(data?.tenant?.slug, p.id),
                                  shareTitle: p.nome || 'Produto',
                                })
                              }
                            >
                              <span className="text-2xl leading-none text-[rgb(var(--st-rgb))]" aria-hidden>
                                ▶
                              </span>
                              Vídeo disponível
                            </button>
                          );
                        }
                        const initial = (p.nome || '?').trim().slice(0, 1) || '?';
                        return (
                          <div
                            className="flex h-full min-h-[7rem] w-full items-center justify-center rounded-xl bg-zinc-200/40 dark:bg-zinc-900/40"
                            aria-hidden
                          >
                            <span className="flex h-16 w-16 items-center justify-center rounded-2xl bg-[rgb(var(--st-rgb)/0.14)] text-2xl font-black uppercase text-[var(--st-600)] shadow-inner shadow-black/5 dark:bg-[rgb(var(--st-rgb)/0.22)] dark:text-[var(--st-400)]">
                              {initial}
                            </span>
                          </div>
                        );
                      })()}
                      <ProductNewHint product={p} />
                      {!soldOutRetail ? <ProductLowStockHint product={p} /> : null}
                    </div>
                  ) : null}
                  <div
                    className={`relative z-0 space-y-1 border-t border-zinc-100 bg-white p-4 dark:border-zinc-800 dark:bg-zinc-900 ${catalogNoPhoto ? 'overflow-hidden rounded-t-2xl border-t-0' : ''}`}
                  >
                    {catalogNoPhoto ? (
                      <ProductNewHint product={p} overlay={false} className="mb-2" />
                    ) : null}
                    {catalogNoPhoto && !soldOutRetail ? (
                      <ProductLowStockHint product={p} overlay={false} className="mb-2" />
                    ) : null}
                    {/* Nome truncado; no hover o texto completo cobre a mesma zona (por cima do título), sem saltar para o lado */}
                    <div className="group/nome-title relative z-[40] isolate">
                      <h3
                        className="line-clamp-2 cursor-default text-sm font-bold uppercase leading-snug tracking-tight text-zinc-900 dark:text-zinc-50 sm:line-clamp-3"
                        title={p.nome || undefined}
                      >
                        {p.nome}
                      </h3>
                      <div
                        role="tooltip"
                        className="invisible absolute left-0 top-0 z-10 w-full min-h-full max-h-[min(60vh,22rem)] cursor-default overflow-y-auto rounded-md border border-zinc-200 bg-white px-2.5 py-2 text-left text-[13px] font-semibold normal-case leading-snug tracking-normal text-pretty text-zinc-900 opacity-0 shadow-lg shadow-black/15 ring-1 ring-black/5 transition duration-150 ease-out group-hover/nome-title:visible group-hover/nome-title:opacity-100 dark:border-zinc-600 dark:bg-zinc-900 dark:text-zinc-100 dark:ring-white/10 [@media(hover:none)]:!invisible [@media(hover:none)]:!opacity-0"
                      >
                        {p.nome}
                      </div>
                    </div>
                    <p className="text-[11px] text-zinc-500 dark:text-zinc-400">(Código {code})</p>
                  </div>
                  <div className="relative z-0 flex flex-col gap-3 rounded-b-2xl border-t border-zinc-100 bg-white px-4 py-3 dark:border-zinc-800 dark:bg-zinc-900">
                    {!pricingHidden ? (
                      <span className="text-base font-bold tabular-nums text-zinc-900 dark:text-white">
                        {formatBrl(p.preco)}
                      </span>
                    ) : null}
                    {soldOutRetail ? (
                      <div>
                        <p className="text-[11px] font-semibold uppercase tracking-wide text-amber-800 dark:text-amber-400">
                          Sem stock
                        </p>
                        {customerToken && customerSession?.radar_enabled ? (
                          <RestockNotifyButton
                            slug={slug}
                            token={customerToken}
                            productId={p.id}
                            productName={p.nome}
                          />
                        ) : null}
                      </div>
                    ) : null}
                    {/* Desktop (lg+): só «Comprar» até existir qty; mobile/tablet: stepper sempre */}
                    {!catalogOnly ? (
                    <div className="hidden items-center gap-2 lg:flex">
                      {q <= 0 ? (
                        <button
                          type="button"
                          disabled={Boolean(soldOutRetail && !pricingHidden)}
                          title={
                            soldOutRetail && !pricingHidden
                              ? 'Este produto está sem stock no momento.'
                              : undefined
                          }
                          className="flex h-11 w-full items-center justify-center rounded-xl bg-gradient-to-br from-[var(--st-500)] to-[var(--st-600)] px-4 text-sm font-bold uppercase tracking-wide text-white shadow-[var(--st-sh-atc)] transition hover:from-[var(--st-600)] hover:to-[var(--st-700)] hover:shadow-[var(--st-sh-atc-h)] active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-45 dark:from-[var(--st-600)] dark:to-[var(--st-700)] dark:hover:from-[var(--st-500)] dark:hover:to-[var(--st-600)]"
                          onClick={() => addQty(p.id, 1)}
                        >
                          {pricingHidden ? (
                            <span className="flex items-center gap-2">
                              <LockIcon className="h-4 w-4" />
                              Entrar para comprar
                            </span>
                          ) : (
                            'Comprar'
                          )}
                        </button>
                      ) : (
                        <>
                          <button
                            type="button"
                            aria-label="Remover uma unidade"
                            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-[var(--st-500)] to-[var(--st-600)] text-lg font-bold text-white shadow-md shadow-[0_6px_14px_-4px_rgb(var(--st-rgb)/0.28)] transition active:scale-95 dark:from-[var(--st-600)] dark:to-[var(--st-700)]"
                            onClick={() => addQty(p.id, -1)}
                          >
                            −
                          </button>
                          <span className="min-w-[2rem] flex-1 text-center text-base font-black tabular-nums text-zinc-900 dark:text-white">
                            {q}
                          </span>
                          <button
                            type="button"
                            aria-label="Adicionar uma unidade"
                            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-[var(--st-500)] to-[var(--st-600)] text-lg font-bold text-white shadow-md shadow-[0_6px_14px_-4px_rgb(var(--st-rgb)/0.28)] transition active:scale-95 dark:from-[var(--st-600)] dark:to-[var(--st-700)]"
                            onClick={() => addQty(p.id, 1)}
                          >
                            +
                          </button>
                        </>
                      )}
                    </div>
                    ) : null}
                    {!catalogOnly ? (
                    <div className="flex items-center justify-center gap-2 sm:justify-start lg:hidden">
                      {pricingHidden ? (
                        <button
                          type="button"
                          className="flex h-9 items-center gap-2 rounded-full bg-zinc-900 px-4 text-xs font-semibold text-white"
                          onClick={() => setAuthModalOpen(true)}
                        >
                          <LockIcon className="h-4 w-4" />
                          Entrar
                        </button>
                      ) : (
                        <>
                          <button
                            type="button"
                            aria-label="Remover uma unidade"
                            className="flex h-9 w-9 items-center justify-center rounded-full bg-zinc-900 text-lg font-medium text-white transition active:scale-95 dark:bg-zinc-100 dark:text-zinc-900"
                            onClick={() => addQty(p.id, -1)}
                          >
                            −
                          </button>
                          <span className="min-w-[2.25rem] text-center text-sm font-bold tabular-nums">{q}</span>
                          <button
                            type="button"
                            aria-label="Adicionar uma unidade"
                            className="flex h-9 w-9 items-center justify-center rounded-full bg-zinc-900 text-lg font-medium text-white transition active:scale-95 dark:bg-zinc-100 dark:text-zinc-900"
                            onClick={() => addQty(p.id, 1)}
                          >
                            +
                          </button>
                        </>
                      )}
                    </div>
                    ) : null}
                  </div>
                </article>
              );
            })
            )}
          </div>
        </main>

      </div>
      )}

      {showFooterPanel && !showCatalogLuxe ? (
        <footer className="fixed bottom-0 left-0 right-0 z-30 border-t border-zinc-200/70 bg-white/72 px-3 py-2 backdrop-blur-xl dark:border-zinc-700/70 dark:bg-zinc-900/65 lg:px-5">
          <div className="relative mx-auto max-w-7xl">
            {footerMinimized ? (
              <div className="flex min-h-[44px] items-center justify-between gap-3">
                <span className="text-xs font-semibold text-zinc-700 dark:text-zinc-200">
                  Contactos da loja
                </span>
                <button
                  type="button"
                  className="shrink-0 rounded-full border border-zinc-200/80 bg-white/90 px-3 py-1.5 text-[11px] font-semibold text-zinc-800 shadow-sm transition hover:bg-white dark:border-zinc-600 dark:bg-zinc-900/80 dark:text-zinc-100 dark:hover:bg-zinc-800"
                  onClick={toggleFooterMinimized}
                >
                  Mostrar
                </button>
              </div>
            ) : (
              <>
                <div className="mb-1.5 flex items-stretch gap-2 lg:mb-0 lg:hidden">
                  <button
                    type="button"
                    id="store-footer-info-toggle"
                    className="flex min-h-[44px] min-w-0 flex-1 items-center justify-between gap-3 rounded-2xl border border-zinc-200/70 bg-white/80 px-3 py-2 text-left transition hover:bg-white dark:border-zinc-600/70 dark:bg-zinc-950/50 dark:hover:bg-zinc-900/80"
                    aria-expanded={footerInfoOpen}
                    aria-controls="store-footer-info-panel"
                    onClick={() => setFooterInfoOpen((v) => !v)}
                  >
                    <span className="min-w-0">
                      <span className="sr-only">{footerInfoOpen ? 'Recolher' : 'Expandir'}</span>
                      <span className="text-[10px] font-semibold uppercase tracking-[0.16em] text-zinc-500 dark:text-zinc-400">
                        Contato da loja
                      </span>
                      <span className="mt-0.5 block truncate text-xs font-semibold text-zinc-800 dark:text-zinc-100">
                        WhatsApp, endereço e redes
                      </span>
                    </span>
                    <span
                      className={`shrink-0 text-zinc-500 transition-transform duration-200 dark:text-zinc-400 ${footerInfoOpen ? 'rotate-180' : ''}`}
                      aria-hidden
                    >
                      ▼
                    </span>
                  </button>
                  <button
                    type="button"
                    onClick={toggleFooterMinimized}
                    className="flex w-11 shrink-0 flex-col items-center justify-center rounded-2xl border border-zinc-200/80 bg-white/85 text-[10px] font-semibold leading-tight text-zinc-600 shadow-sm transition hover:bg-white dark:border-zinc-600 dark:bg-zinc-950/70 dark:text-zinc-300 dark:hover:bg-zinc-900"
                    aria-label="Recolher rodapé"
                    title="Recolher rodapé"
                  >
                    <span aria-hidden className="text-sm">
                      ▾
                    </span>
                    <span className="mt-0.5 max-w-[2.5rem] text-center">Fechar</span>
                  </button>
                </div>
                <button
                  type="button"
                  onClick={toggleFooterMinimized}
                  className="pointer-events-auto absolute right-1 top-1 z-20 hidden h-7 items-center gap-1 rounded-full border border-zinc-200/90 bg-white/95 px-2.5 text-[9px] font-semibold uppercase tracking-wide text-zinc-700 shadow-md backdrop-blur-sm transition hover:bg-white dark:border-zinc-600 dark:bg-zinc-900/95 dark:text-zinc-200 dark:hover:bg-zinc-800 lg:inline-flex"
                  aria-label="Recolher rodapé"
                  title="Recolher rodapé"
                >
                  <span aria-hidden className="text-[10px] leading-none">
                    ▾
                  </span>
                  Recolher
                </button>
                <div
                  id="store-footer-info-panel"
                  role="region"
                  aria-labelledby="store-footer-info-toggle"
                  className={`mt-2 grid text-[11px] text-zinc-700 dark:text-zinc-200 ${footerDetailGridClass} lg:mt-0 lg:pr-[5.75rem] ${footerInfoOpen ? '' : 'max-lg:hidden'}`}
                >
                  {showSpeakCol ? (
                    <div className="min-w-0 rounded-2xl border border-zinc-200/60 bg-white/55 px-3 py-2 dark:border-zinc-700/55 dark:bg-zinc-950/35">
                      <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-zinc-500 dark:text-zinc-400">
                        Fale com a loja
                      </p>
                      <div className="mt-1.5 space-y-1">
                        {footerSpeakItems.map((item) =>
                          item.href ? (
                            <a
                              key={`${item.label}-${item.value}`}
                              href={item.href}
                              target="_blank"
                              rel="noreferrer"
                              className="flex items-center gap-2 rounded-lg px-1 py-0.5 transition hover:bg-zinc-900/10 dark:hover:bg-white/10"
                            >
                              <span className="shrink-0">{item.icon}</span>
                              <span className="truncate">{item.value}</span>
                            </a>
                          ) : (
                            <span key={`${item.label}-${item.value}`} className="flex items-center gap-2 px-1 py-0.5">
                              <span className="shrink-0">{item.icon}</span>
                              <span className="truncate">{item.value}</span>
                            </span>
                          )
                        )}
                      </div>
                    </div>
                  ) : null}
                  {showAddressCol && footerAddressBlock ? (
                    <div className="min-w-0 rounded-2xl border border-zinc-200/60 bg-white/55 px-3 py-2 dark:border-zinc-700/55 dark:bg-zinc-950/35">
                      <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-zinc-500 dark:text-zinc-400">
                        Endereço da loja
                      </p>
                      <div className="mt-1.5 space-y-2">
                        {footerAddressBlock.address ? (
                          <p className="flex gap-2 leading-snug text-zinc-800 dark:text-zinc-100">
                            <span className="shrink-0" aria-hidden>
                              📍
                            </span>
                            <span>{footerAddressBlock.address}</span>
                          </p>
                        ) : null}
                        {footerAddressBlock.mapsUrl ? (
                          <a
                            href={footerAddressBlock.mapsUrl}
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex items-center gap-2 rounded-lg border border-zinc-200/70 bg-white/80 px-2 py-1.5 text-[10px] font-semibold text-zinc-800 transition hover:bg-white dark:border-zinc-600 dark:bg-zinc-900/60 dark:text-zinc-100 dark:hover:bg-zinc-800"
                          >
                            <GoogleMapsIcon className="h-5 w-5 shrink-0" />
                            Ver localização no mapa
                          </a>
                        ) : null}
                      </div>
                    </div>
                  ) : null}
                  <div className="min-w-0 rounded-2xl border border-zinc-200/60 bg-white/55 px-3 py-2 dark:border-zinc-700/55 dark:bg-zinc-950/35">
                    <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-zinc-500 dark:text-zinc-400">
                      Redes sociais
                    </p>
                    <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                      {footerSocialItems.length > 0 ? (
                        footerSocialItems.map((item) => (
                          <a
                            key={item.label}
                            href={item.href}
                            target="_blank"
                            rel="noreferrer"
                            aria-label={item.label}
                            className="inline-flex items-center gap-1.5 rounded-full border border-zinc-200/70 bg-white/75 px-2.5 py-1 text-[10px] font-medium transition hover:border-zinc-300 hover:bg-white dark:border-zinc-700 dark:bg-zinc-900/70 dark:hover:border-zinc-500"
                          >
                            <span className="shrink-0">{item.icon}</span>
                            <span>{item.label}</span>
                          </a>
                        ))
                      ) : (
                        <span className="px-1 text-zinc-500 dark:text-zinc-400">Sem redes informadas.</span>
                      )}
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        </footer>
      ) : null}

      {/* Sacola lateral — todas as larguras; abre ao adicionar item ou ao clicar «Seu pedido» */}
      {!catalogOnly ? (
      <>
      <div
        className={`fixed inset-0 z-50 bg-black/45 backdrop-blur-[2px] transition-opacity duration-300 ${drawerOpen ? 'opacity-100' : 'pointer-events-none opacity-0'}`}
        onClick={() => setDrawerOpen(false)}
        aria-hidden={!drawerOpen}
      />
      <aside
        role="dialog"
        aria-modal="true"
        aria-label="Confira seu pedido"
        className={`fixed inset-y-0 right-0 z-[51] flex w-full max-w-md flex-col overflow-hidden shadow-[-12px_0_48px_rgba(0,0,0,0.18)] transition-[transform] duration-300 ease-out lg:max-w-lg ${drawerOpen ? 'translate-x-0' : 'translate-x-full'}`}
      >
        <div className="flex shrink-0 items-center justify-between gap-3 bg-gradient-to-r from-[var(--st-600)] to-[var(--st-700)] px-4 py-4 text-white dark:from-[var(--st-700)] dark:to-[var(--st-900)]">
          <div className="min-w-0">
            <p className="truncate font-display text-lg font-bold tracking-tight">Confira seu pedido</p>
            <p className="truncate text-[11px] font-medium uppercase tracking-[0.2em] text-white/85">
              {t.count} ite{t.count === 1 ? 'm' : 'ns'} · {pricingHidden ? '—' : formatBrl(t.total)}
            </p>
          </div>
          <button
            type="button"
            className="shrink-0 rounded-full bg-white/15 p-2.5 text-white backdrop-blur-sm transition hover:bg-white/25"
            onClick={() => setDrawerOpen(false)}
            aria-label="Fechar sacola"
          >
            <span aria-hidden className="text-lg leading-none">
              ✕
            </span>
          </button>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain bg-[rgb(var(--st-rgb)/0.9)] p-3 dark:bg-[rgb(var(--st-drawer)/0.88)] lg:p-4">
          <div className="min-h-[min(100%,32rem)] rounded-2xl border border-[var(--st-bd-card)] bg-white p-4 shadow-xl dark:border-[var(--st-bd-card-d)] dark:bg-zinc-950 lg:p-5">
            <SacolaPanel
              checkout={checkout}
              setCheckout={setCheckout}
              submitCheckout={submitCheckout}
              checkoutError={error}
              t={t}
              cartLines={cartLines}
              compact
              variant="drawer"
              onAddOne={(pid) => addQty(pid, 1)}
              onRemoveLine={removeCartLine}
              couponInput={couponInput}
              setCouponInput={setCouponInput}
              onApplyCoupon={applyCheckoutCoupon}
              couponMsg={couponMsg}
              couponBusy={couponBusy}
              couponPctValid={couponPctValid}
              canUseCoupon={!pricingHidden}
              checkoutPaymentOptions={checkoutPaymentOptions}
              checkoutPaymentHasOnline={hasOnlinePaymentOption}
              selectedCount={t.count}
              unselectedCount={t.unselectedCount}
              isItemSelected={isItemSelected}
              onToggleItemSelected={setItemSelected}
              onSelectAllItems={selectAllCartItems}
              onDeselectAllItems={deselectAllCartItems}
              onClear={async () => {
                if (customerSession) {
                  await persistCustomerCartNow({}).catch(() => {});
                } else if (slugKey) {
                  clearCartLocalStorage(slugKey);
                }
                setCart({});
                setCouponInput('');
                setCouponPctValid(null);
                setCouponNormApplied('');
                setCouponMsg('');
              }}
              customerSession={customerSession}
              updateCustomerAddress={updateCustomerAddress}
              setUpdateCustomerAddress={setUpdateCustomerAddress}
              pricingHidden={pricingHidden}
              cepLookupBusy={cepLookupBusy}
              cepLookupMsg={cepLookupMsg}
              cepLookupError={cepLookupError}
              onLookupCep={lookupCheckoutCep}
              checkoutLookupBusy={checkoutLookupBusy}
              checkoutLookupMsg={checkoutLookupMsg}
              onLookupKnownCustomer={tryAutofillCheckoutByIdentity}
              pickupOnly={pickupOnly}
              deliveryZonesEnabled={deliveryZonesEnabled}
              deliveryFeeQuote={deliveryFeeQuote}
              deliveryFeeError={deliveryFeeError}
              deliveryFeeBusy={deliveryFeeBusy}
              checkoutFulfillmentMode={checkoutFulfillmentMode}
              onFulfillmentModeChange={applyCheckoutFulfillmentMode}
              savedDeliveryAddresses={savedDeliveryAddresses}
              productImageCtx={productImageCtx}
              contactRequirements={contactReq}
            />
          </div>
        </div>
      </aside>
      </>
      ) : null}

      {/* Modal sucesso */}
      {mediaPreview ? (
        <ProductMediaPreviewModal value={mediaPreview} onClose={() => setMediaPreview(null)} />
      ) : null}

      <StockLimitModal
        open={stockAlert.open}
        productName={stockAlert.nome}
        available={stockAlert.available}
        onClose={() => setStockAlert({ open: false, nome: '', available: 0 })}
      />

      <DocumentOrderModal
        open={documentOrderOpen}
        onClose={() => setDocumentOrderOpen(false)}
        slug={slug}
        aiAvailable={documentOrderAiAvailable}
        blockSaleZeroStock={blockSaleZeroStock}
        pricingHidden={pricingHidden}
        resolveProductRow={resolveProductRow}
        addQty={addQty}
        mustAuth={mustAuth}
        customerSession={customerSession}
        onRequireLogin={() => setAuthModalOpen(true)}
      />

      <CatalogExportModal
        open={catalogExportOpen}
        onClose={() => setCatalogExportOpen(false)}
        slug={slug}
        customerToken={customerToken}
        sections={data?.section_summary}
      />

      {authModalOpen && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm">
          <div className="w-full max-w-md rounded-3xl bg-white p-6 shadow-2xl dark:bg-zinc-900">
            <div className="mb-4 flex items-center justify-between">
              <h3 className="font-display text-xl font-semibold">Acesse sua conta</h3>
              <button type="button" className="text-sm" onClick={() => setAuthModalOpen(false)}>Fechar</button>
            </div>
            {blockPublicCustomerRegistration ? (
              <p className="mb-4 rounded-xl bg-violet-500/10 px-3 py-2 text-xs leading-relaxed text-violet-950 dark:bg-violet-500/15 dark:text-violet-100">
                O cadastro público nesta loja está desactivado. Se já tiver conta, use o separador <strong>Login</strong>.
                Caso contrário, peça à loja para lhe criar o acesso.
              </p>
            ) : null}
            {!blockPublicCustomerRegistration ? (
            <div className="mb-4 flex rounded-xl bg-zinc-100 p-1 dark:bg-zinc-800">
              <button type="button" onClick={() => setAuthMode('login')} className={`flex-1 rounded-lg py-2 text-sm font-semibold ${authMode === 'login' ? 'bg-white dark:bg-zinc-700' : ''}`}>Login</button>
              <button type="button" onClick={() => setAuthMode('register')} className={`flex-1 rounded-lg py-2 text-sm font-semibold ${authMode === 'register' ? 'bg-white dark:bg-zinc-700' : ''}`}>Cadastro</button>
            </div>
            ) : (
            <div className="mb-4 rounded-xl bg-zinc-100 p-1 dark:bg-zinc-800">
              <div className="rounded-lg bg-white py-2 text-center text-sm font-semibold dark:bg-zinc-700">Login</div>
            </div>
            )}
            {authError ? <p className="mb-3 rounded-lg bg-red-50 px-3 py-2 text-xs text-red-700 dark:bg-red-950/40 dark:text-red-200">{authError}</p> : null}
            {authMode === 'login' || blockPublicCustomerRegistration ? (
              <form className="space-y-3" onSubmit={submitLogin}>
                <input
                  required
                  type={contactReq.orMode || (contactReq.requireWhatsapp && !contactReq.requireEmail) ? 'text' : 'email'}
                  inputMode={contactReq.requireWhatsapp && !contactReq.requireEmail && !contactReq.orMode ? 'tel' : undefined}
                  placeholder={loginIdentityPlaceholder(contactReq)}
                  className="w-full rounded-xl border px-3 py-2 text-sm dark:bg-zinc-950"
                  value={authLogin.email}
                  onChange={(e) => setAuthLogin({ ...authLogin, email: e.target.value })}
                />
                <div className="flex gap-2">
                  <input required type={showLoginPassword ? 'text' : 'password'} placeholder="Senha" className="w-full rounded-xl border px-3 py-2 text-sm dark:bg-zinc-950" value={authLogin.senha} onChange={(e) => setAuthLogin({ ...authLogin, senha: e.target.value })} />
                  <button type="button" className="rounded-xl border px-3 text-xs" onClick={() => setShowLoginPassword((v) => !v)}>{showLoginPassword ? 'Ocultar' : 'Mostrar'}</button>
                </div>
                <div className="flex justify-end">
                  <Link
                    to={`/${encodeURIComponent(slug)}/recuperar-senha`}
                    onClick={() => setAuthModalOpen(false)}
                    className="text-xs font-medium text-zinc-600 underline-offset-2 hover:text-[var(--st-700)] hover:underline dark:text-zinc-400 dark:hover:text-[var(--st-400)]"
                  >
                    Esqueceu a senha?
                  </Link>
                </div>
                <button disabled={authBusy} className="w-full rounded-xl bg-zinc-900 py-2 text-sm font-semibold text-white dark:bg-zinc-100 dark:text-zinc-900">Entrar</button>
              </form>
            ) : (
              <form className="space-y-3" onSubmit={submitRegister}>
                <input required placeholder="Nome" className="w-full rounded-xl border px-3 py-2 text-sm dark:bg-zinc-950" value={authRegister.nome} onChange={(e) => setAuthRegister({ ...authRegister, nome: e.target.value })} />
                <div className="flex gap-2">
                  <select className="rounded-xl border px-2 py-2 text-sm dark:bg-zinc-950" value={authRegister.whatsapp_country_code} onChange={(e) => setAuthRegister({ ...authRegister, whatsapp_country_code: e.target.value })}>
                    <option value="+55">🇧🇷 +55</option><option value="+1">🇺🇸 +1</option><option value="+351">🇵🇹 +351</option><option value="+34">🇪🇸 +34</option>
                  </select>
                  <input
                    required={contactFieldHtmlRequired(contactReq, 'phone')}
                    placeholder="WhatsApp"
                    className="w-full rounded-xl border px-3 py-2 text-sm dark:bg-zinc-950"
                    value={authRegister.whatsapp}
                    onChange={(e) => setAuthRegister({ ...authRegister, whatsapp: normalizeWhatsappMask(e.target.value, authRegister.whatsapp_country_code) })}
                    onBlur={() => tryAutofillRegisterByIdentity('whatsapp')}
                  />
                </div>
                <input
                  required={contactFieldHtmlRequired(contactReq, 'email')}
                  type="email"
                  placeholder="E-mail"
                  className="w-full rounded-xl border px-3 py-2 text-sm dark:bg-zinc-950"
                  value={authRegister.email}
                  onChange={(e) => setAuthRegister({ ...authRegister, email: e.target.value })}
                  onBlur={() => tryAutofillRegisterByIdentity('email')}
                />
                {contactRequirementsHint(contactReq) ? (
                  <p className="text-[11px] leading-snug text-zinc-500 dark:text-zinc-400">
                    {contactRequirementsHint(contactReq)}
                  </p>
                ) : null}
                {registerLookupBusy ? (
                  <p className="text-[11px] text-zinc-500 dark:text-zinc-400">Buscando cadastro existente...</p>
                ) : null}
                {!registerLookupBusy && registerLookupMsg ? (
                  <p className="text-[11px] font-medium text-emerald-700 dark:text-emerald-400">{registerLookupMsg}</p>
                ) : null}
                <div className="space-y-2">
                  <div className="flex gap-2">
                    <input
                      inputMode="numeric"
                      autoComplete="postal-code"
                      placeholder="CEP (00000-000)"
                      className="w-full rounded-xl border px-3 py-2 text-sm dark:bg-zinc-950"
                      value={authRegister.cep || ''}
                      onChange={(e) => setAuthRegister((prev) => ({ ...prev, cep: formatCep(e.target.value) }))}
                    />
                    <button
                      type="button"
                      disabled={registerCepLookupBusy}
                      onClick={lookupRegisterCep}
                      className="shrink-0 rounded-xl border px-3 text-xs font-semibold uppercase tracking-wide"
                    >
                      {registerCepLookupBusy ? 'Buscando...' : 'Buscar CEP'}
                    </button>
                  </div>
                  {registerCepLookupError ? (
                    <p className="text-[11px] font-medium text-red-600 dark:text-red-400">{registerCepLookupError}</p>
                  ) : null}
                  {!registerCepLookupError && registerCepLookupMsg ? (
                    <p className="text-[11px] font-medium text-emerald-700 dark:text-emerald-400">{registerCepLookupMsg}</p>
                  ) : null}
                </div>
                <textarea required rows={2} placeholder="Endereço" className="w-full rounded-xl border px-3 py-2 text-sm dark:bg-zinc-950" value={authRegister.endereco} onChange={(e) => setAuthRegister({ ...authRegister, endereco: e.target.value })} />
                <div className="flex gap-2">
                  <input required pattern="\d{4,8}" title="Use 4 a 8 dígitos" type={showRegisterPassword ? 'text' : 'password'} placeholder="Senha (4 a 8 dígitos)" className="w-full rounded-xl border px-3 py-2 text-sm dark:bg-zinc-950" value={authRegister.senha} onChange={(e) => setAuthRegister({ ...authRegister, senha: e.target.value })} />
                  <button type="button" className="rounded-xl border px-3 text-xs" onClick={() => setShowRegisterPassword((v) => !v)}>{showRegisterPassword ? 'Ocultar' : 'Mostrar'}</button>
                </div>
                <button disabled={authBusy} className="w-full rounded-xl bg-zinc-900 py-2 text-sm font-semibold text-white dark:bg-zinc-100 dark:text-zinc-900">Cadastrar</button>
              </form>
            )}
          </div>
        </div>
      )}

      {/* Modal: cliente digitou cupom mas não aplicou — perguntar antes de finalizar */}
      {couponApplyPromptOpen && (
        <div
          className="fixed inset-0 z-[70] flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm"
          role="dialog"
          aria-modal="true"
          aria-labelledby="coupon-prompt-title"
        >
          <div className="w-full max-w-sm rounded-3xl bg-white p-6 shadow-2xl dark:bg-zinc-900">
            <h3
              id="coupon-prompt-title"
              className="font-display text-lg font-bold text-zinc-900 dark:text-zinc-50"
            >
              Aplicar cupom de desconto?
            </h3>
            <p className="mt-2 text-sm leading-relaxed text-zinc-600 dark:text-zinc-300">
              Você digitou o cupom{' '}
              <span className="font-bold uppercase text-zinc-900 dark:text-zinc-100">
                {String(couponInput || '').trim().toUpperCase()}
              </span>{' '}
              mas ainda não o aplicou. Deseja aplicar agora?
            </p>
            <div className="mt-5 flex flex-col gap-2">
              <button
                type="button"
                disabled={couponBusy}
                onClick={async () => {
                  await applyCheckoutCoupon();
                  setCouponApplyPromptOpen(false);
                  setConfirmCheckoutOpen(true);
                }}
                className="w-full rounded-full bg-emerald-600 py-3 text-sm font-bold text-white shadow-lg transition enabled:hover:bg-emerald-500 disabled:cursor-not-allowed disabled:opacity-40"
              >
                {couponBusy ? 'Validando…' : 'Sim, aplicar cupom'}
              </button>
              <button
                type="button"
                disabled={couponBusy}
                onClick={() => {
                  setCouponApplyPromptOpen(false);
                  setConfirmCheckoutOpen(true);
                }}
                className="w-full rounded-full border border-zinc-300 bg-white py-3 text-sm font-semibold text-zinc-700 transition hover:bg-zinc-50 disabled:opacity-40 dark:border-zinc-600 dark:bg-zinc-900 dark:text-zinc-200 dark:hover:bg-zinc-800"
              >
                Continuar sem o cupom
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal: confirmação final com resumo completo do pedido */}
      {confirmCheckoutOpen && (() => {
        const confirmIsPickup = pickupOnly || checkoutFulfillmentIsPickup(checkoutFulfillmentMode);
        const confirmSavedId = checkoutFulfillmentParseSavedId(checkoutFulfillmentMode);
        const confirmSavedAddr = confirmSavedId
          ? savedDeliveryAddresses.find((a) => Number(a.id) === Number(confirmSavedId))
          : null;
        const confirmAddressText = confirmIsPickup
          ? 'Retirar na loja'
          : confirmSavedAddr
            ? String(confirmSavedAddr.endereco || '').trim()
            : [
                String(checkout.endereco || '').trim(),
                String(checkout.enderecoNumero || '').trim(),
                String(checkout.enderecoComplemento || '').trim(),
              ]
                .filter(Boolean)
                .join(', ');
        const confirmPayment = findCheckoutPaymentOption(checkoutPaymentOptions, checkout.paymentMethod);
        return (
          <div
            className="fixed inset-0 z-[70] flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm"
            role="dialog"
            aria-modal="true"
            aria-labelledby="confirm-checkout-title"
          >
            <div className="flex max-h-[92vh] w-full max-w-md flex-col overflow-hidden rounded-3xl bg-white shadow-2xl dark:bg-zinc-900">
              <div className="flex shrink-0 items-center justify-between gap-3 border-b border-zinc-100 px-5 py-4 dark:border-zinc-800">
                <h3
                  id="confirm-checkout-title"
                  className="font-display text-lg font-bold text-zinc-900 dark:text-zinc-50"
                >
                  Confirme seu pedido
                </h3>
                <button
                  type="button"
                  className="rounded-full p-1.5 text-zinc-500 transition hover:bg-zinc-100 hover:text-zinc-800 dark:text-zinc-400 dark:hover:bg-zinc-800 dark:hover:text-zinc-100"
                  onClick={() => setConfirmCheckoutOpen(false)}
                  aria-label="Fechar"
                >
                  <span aria-hidden className="text-lg leading-none">
                    ✕
                  </span>
                </button>
              </div>

              <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-5 py-4">
                <div>
                  <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-zinc-400">
                    Itens ({t.count})
                  </p>
                  <ul className="mt-2 space-y-2">
                    {selectedCartLines.map(({ p, q, line }) => (
                      <li key={p.id} className="flex items-start justify-between gap-3 text-sm">
                        <span className="min-w-0 flex-1 text-zinc-700 dark:text-zinc-200">
                          <span className="font-semibold tabular-nums text-zinc-900 dark:text-zinc-50">
                            {q}×
                          </span>{' '}
                          {p.nome}
                          {!pricingHidden ? (
                            <span className="block text-[11px] text-zinc-400">
                              {formatBrl(Number(p.preco))} cada
                            </span>
                          ) : null}
                        </span>
                        {!pricingHidden ? (
                          <span className="shrink-0 font-semibold tabular-nums text-zinc-900 dark:text-zinc-50">
                            {formatBrl(line)}
                          </span>
                        ) : null}
                      </li>
                    ))}
                  </ul>
                </div>

                {!pricingHidden ? (
                  <div className="space-y-1 border-t border-zinc-100 pt-3 text-sm dark:border-zinc-800">
                    <div className="flex justify-between text-zinc-600 dark:text-zinc-300">
                      <span>Subtotal</span>
                      <span className="tabular-nums">{formatBrl(t.subtotal)}</span>
                    </div>
                    {typeof t.discountPercent === 'number' && t.discountPercent > 0 ? (
                      <div className="flex justify-between font-medium text-emerald-700 dark:text-emerald-400">
                        <span>Desconto ({t.discountPercent}%)</span>
                        <span className="tabular-nums">
                          −{formatBrl(Math.round(t.subtotal * t.discountPercent) / 100)}
                        </span>
                      </div>
                    ) : null}
                    {t.deliveryFee > 0 ? (
                      <div className="flex justify-between text-zinc-600 dark:text-zinc-300">
                        <span>Entrega</span>
                        <span className="tabular-nums">{formatBrl(t.deliveryFee)}</span>
                      </div>
                    ) : null}
                    <div className="flex justify-between border-t border-zinc-100 pt-2 text-base font-bold text-zinc-900 dark:border-zinc-800 dark:text-zinc-50">
                      <span>Total</span>
                      <span className="tabular-nums">{formatBrl(t.total)}</span>
                    </div>
                  </div>
                ) : null}

                <div className="space-y-2 border-t border-zinc-100 pt-3 text-sm dark:border-zinc-800">
                  <div>
                    <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-zinc-400">
                      Cliente
                    </p>
                    <p className="mt-1 text-zinc-700 dark:text-zinc-200">
                      {String(checkout.nome || '').trim() || '—'}
                      {String(checkout.telefone || '').trim()
                        ? ` · ${String(checkout.telefone).trim()}`
                        : ''}
                    </p>
                    {String(checkout.email || '').trim() ? (
                      <p className="text-[12px] text-zinc-500 dark:text-zinc-400">
                        {String(checkout.email).trim()}
                      </p>
                    ) : null}
                  </div>
                  <div>
                    <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-zinc-400">
                      {confirmIsPickup ? 'Retirada' : 'Entrega'}
                    </p>
                    <p className="mt-1 text-zinc-700 dark:text-zinc-200">{confirmAddressText || '—'}</p>
                  </div>
                  {confirmPayment ? (
                    <div>
                      <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-zinc-400">
                        Pagamento
                      </p>
                      <p className="mt-1 text-zinc-700 dark:text-zinc-200">{confirmPayment.display}</p>
                    </div>
                  ) : null}
                  {String(checkout.observacoes || '').trim() ? (
                    <div>
                      <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-zinc-400">
                        Observações
                      </p>
                      <p className="mt-1 text-zinc-700 dark:text-zinc-200">
                        {String(checkout.observacoes).trim()}
                      </p>
                    </div>
                  ) : null}
                </div>
              </div>

              <div className="shrink-0 space-y-2 border-t border-zinc-100 px-5 py-4 dark:border-zinc-800">
                <button
                  type="button"
                  disabled={confirmCheckoutBusy}
                  onClick={async () => {
                    setConfirmCheckoutBusy(true);
                    try {
                      await submitCheckout(null, { confirmed: true, skipCouponPrompt: true });
                    } finally {
                      setConfirmCheckoutBusy(false);
                    }
                  }}
                  className="flex w-full items-center justify-center gap-2 rounded-full bg-zinc-900 py-3.5 text-sm font-bold text-white shadow-lg transition enabled:hover:-translate-y-0.5 enabled:hover:shadow-xl disabled:cursor-not-allowed disabled:opacity-40 dark:bg-[var(--st-600)] dark:enabled:hover:bg-[var(--st-500)]"
                >
                  {confirmCheckoutBusy
                    ? 'Enviando…'
                    : `Confirmar e enviar${pricingHidden ? '' : ` · ${formatBrl(t.total)}`}`}
                  <span aria-hidden>›</span>
                </button>
                <button
                  type="button"
                  disabled={confirmCheckoutBusy}
                  onClick={() => setConfirmCheckoutOpen(false)}
                  className="w-full rounded-full border border-zinc-300 bg-white py-3 text-sm font-semibold text-zinc-700 transition hover:bg-zinc-50 disabled:opacity-40 dark:border-zinc-600 dark:bg-zinc-900 dark:text-zinc-200 dark:hover:bg-zinc-800"
                >
                  Voltar e revisar
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {orderSuccess && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm"
          role="dialog"
          aria-modal="true"
          aria-labelledby="order-success-title"
        >
          <div className="max-h-[90vh] w-full max-w-md overflow-y-auto rounded-3xl bg-white p-8 shadow-2xl dark:border dark:border-zinc-800 dark:bg-zinc-950">
            <SuccessIllustration />
            <h2 id="order-success-title" className="text-center font-display text-2xl font-bold text-zinc-900 dark:text-white">
              Pedido feito!
            </h2>
            <p className="mx-auto mt-2 max-w-sm text-center text-sm text-zinc-600 dark:text-zinc-400">
              Pedido #{orderSuccess.orderId} — Aguarde a confirmação da loja por e-mail. Verifique também spam e promoções.
            </p>

            <div className="mt-8 border-t border-zinc-100 pt-6 dark:border-zinc-800">
              <p className="mb-3 text-[11px] font-bold uppercase tracking-wider text-zinc-400">
                Itens ({orderSuccess.items.reduce((s, i) => s + i.qty, 0)})
              </p>
              <ul className="space-y-4">
                {orderSuccess.items.map((row, idx) => (
                  <li key={idx} className="flex justify-between gap-3 text-sm">
                    <span className="font-medium text-zinc-900 dark:text-zinc-100">
                      {row.qty} × {row.nome}
                    </span>
                    <span className="shrink-0 tabular-nums text-zinc-600 dark:text-zinc-400">
                      {formatBrl(row.line)}
                    </span>
                  </li>
                ))}
              </ul>
              {orderSuccess.subtotal != null &&
            orderSuccess.discountPercent != null &&
            orderSuccess.discountPercent > 0 ? (
              <div className="mt-4 space-y-1 text-sm text-zinc-600 dark:text-zinc-400">
                <div className="flex justify-between gap-2 tabular-nums">
                  <span>Subtotal</span>
                  <span>{formatBrl(orderSuccess.subtotal)}</span>
                </div>
                <div className="flex justify-between gap-2 font-medium text-emerald-800 tabular-nums dark:text-emerald-300">
                  <span>Desconto ({Number(orderSuccess.discountPercent)}%)</span>
                  <span>-{formatBrl(Math.max(0, orderSuccess.subtotal - orderSuccess.total))}</span>
                </div>
              </div>
            ) : null}

            <div className="mt-6 flex justify-between border-t border-zinc-100 pt-4 dark:border-zinc-800">
                <span className="font-semibold text-zinc-900 dark:text-white">Total</span>
                <span className="text-lg font-bold tabular-nums text-zinc-900 dark:text-white">
                  {formatBrl(orderSuccess.total)}
                </span>
              </div>
            </div>

            <button
              type="button"
              className="mt-10 w-full rounded-full bg-zinc-900 py-4 text-center text-sm font-bold text-white shadow-lg transition hover:bg-zinc-800 dark:bg-[var(--st-600)] dark:hover:bg-[var(--st-500)]"
              onClick={() => setOrderSuccess(null)}
            >
              Continuar comprando
            </button>
          </div>
        </div>
      )}
      <StoreConsentBanner
        slug={slugKey}
        ga4MeasurementId={storeTracking.analytics.ga4MeasurementId}
        metaPixelId={storeTracking.analytics.metaPixelId}
        tenantName={data?.tenant?.nome ? String(data.tenant.nome) : 'esta loja'}
      />
    </div>
  );
}

function SacolaPanel({
  checkout,
  setCheckout,
  submitCheckout,
  checkoutError = '',
  t,
  cartLines,
  compact,
  variant = 'panel',
  onAddOne,
  onRemoveLine,
  onClear,
  customerSession,
  updateCustomerAddress,
  setUpdateCustomerAddress,
  pricingHidden = false,
  couponInput = '',
  setCouponInput,
  onApplyCoupon,
  couponMsg = '',
  couponBusy = false,
  couponPctValid = null,
  canUseCoupon = true,
  checkoutPaymentOptions = [],
  checkoutPaymentHasOnline = false,
  selectedCount = 0,
  unselectedCount = 0,
  isItemSelected,
  onToggleItemSelected,
  onSelectAllItems,
  onDeselectAllItems,
  cepLookupBusy = false,
  cepLookupMsg = '',
  cepLookupError = '',
  onLookupCep,
  checkoutLookupBusy = false,
  checkoutLookupMsg = '',
  onLookupKnownCustomer,
  pickupOnly = false,
  deliveryZonesEnabled = false,
  deliveryFeeQuote = null,
  deliveryFeeError = '',
  deliveryFeeBusy = false,
  checkoutFulfillmentMode = CHECKOUT_FULFILL_PICKUP,
  onFulfillmentModeChange,
  savedDeliveryAddresses = [],
  productImageCtx,
  contactRequirements = null,
}) {
  const contactReq = contactRequirements || {
    requireEmail: true,
    requireWhatsapp: false,
    orMode: false,
  };
  const inDrawer = variant === 'drawer';
  const isPickup = pickupOnly || checkoutFulfillmentIsPickup(checkoutFulfillmentMode);
  const isNewAddress = checkoutFulfillmentIsNewAddress(checkoutFulfillmentMode);
  const hasSavedAddresses =
    Array.isArray(savedDeliveryAddresses) && savedDeliveryAddresses.length > 0;
  const shell =
    'space-y-4 ' +
    (inDrawer
      ? ''
      : 'rounded-2xl border border-zinc-200 bg-white p-5 shadow-sm dark:border-zinc-800 dark:bg-zinc-900/80');

  return (
    <div className={shell}>
      <div className="flex items-start justify-between gap-2">
        <div>
          <h2
            className={
              inDrawer
                ? 'text-base font-bold text-zinc-900 dark:text-zinc-100'
                : 'font-display text-xl font-semibold text-zinc-900 dark:text-zinc-50'
            }
          >
            {inDrawer ? 'Detalhes do pedido' : 'Sua sacola'}
          </h2>
          {!inDrawer && (
            <p className="text-xs text-zinc-500 dark:text-zinc-400">
              {t.count} itens · Total {pricingHidden ? '—' : formatBrl(t.total)}
            </p>
          )}
        </div>
        {t.count > 0 && (
          <button
            type="button"
            onClick={onClear}
            className="text-xs font-semibold uppercase tracking-wide text-[var(--st-700)] underline-offset-2 hover:underline dark:text-[var(--st-400)]"
          >
            Limpar
          </button>
        )}
      </div>

      {cartLines.length > 0 && (
        <>
          {(() => {
            const totalLinesCount = cartLines.length;
            const selectedLinesCount = cartLines.filter(({ p }) =>
              typeof isItemSelected === 'function' ? isItemSelected(p.id) : true,
            ).length;
            const allSelected = selectedLinesCount === totalLinesCount;
            const noneSelected = selectedLinesCount === 0;
            const handleToggleAll = () => {
              if (allSelected) {
                if (typeof onDeselectAllItems === 'function') onDeselectAllItems();
              } else if (typeof onSelectAllItems === 'function') {
                onSelectAllItems();
              }
            };
            return (
              <div className="flex flex-wrap items-center justify-between gap-2 rounded-xl bg-zinc-100/70 px-3 py-2 dark:bg-zinc-800/40">
                <p className="text-[11px] font-semibold text-zinc-700 dark:text-zinc-200">
                  <span className="tabular-nums">{selectedLinesCount}</span>{' '}
                  <span className="text-zinc-500 dark:text-zinc-400">
                    de <span className="tabular-nums">{totalLinesCount}</span> selecionado
                    {totalLinesCount === 1 ? '' : 's'}
                  </span>
                  {unselectedCount > 0 ? (
                    <span className="ml-1 text-zinc-500 dark:text-zinc-400">
                      · <span className="tabular-nums">{unselectedCount}</span> deixad
                      {unselectedCount === 1 ? 'o' : 'os'} para depois
                    </span>
                  ) : null}
                </p>
                {(typeof onSelectAllItems === 'function' ||
                  typeof onDeselectAllItems === 'function') && totalLinesCount > 1 ? (
                  <button
                    type="button"
                    onClick={handleToggleAll}
                    className="text-[11px] font-semibold uppercase tracking-wide text-[var(--st-700)] underline-offset-2 hover:underline dark:text-[var(--st-400)]"
                  >
                    {allSelected
                      ? 'Desmarcar tudo'
                      : noneSelected
                        ? 'Selecionar tudo'
                        : 'Selecionar tudo'}
                  </button>
                ) : null}
              </div>
            );
          })()}
          <ul
            className={`space-y-3 ${compact ? 'max-h-[min(50vh,22rem)] overflow-y-auto pr-1 [scrollbar-gutter:stable]' : ''}`}
          >
            {cartLines.map(({ p, q, line }) => {
              const thumbUrl = productGalleryUrls(p, productImageCtx)[0];
              const itemSelected =
                typeof isItemSelected === 'function' ? Boolean(isItemSelected(p.id)) : true;
              return (
                <li
                  key={p.id}
                  className={`flex gap-3 rounded-xl border p-3 transition dark:bg-zinc-900/30 ${
                    itemSelected
                      ? 'border-zinc-100 dark:border-zinc-800'
                      : 'border-zinc-200/60 bg-zinc-50/70 opacity-60 dark:border-zinc-700/60 dark:bg-zinc-900/20'
                  }`}
                >
                  {typeof onToggleItemSelected === 'function' ? (
                    <label
                      className="flex shrink-0 cursor-pointer items-start pt-1"
                      title={
                        itemSelected
                          ? 'Desmarque para deixar este produto para depois'
                          : 'Marque para incluir no próximo pedido'
                      }
                    >
                      <input
                        type="checkbox"
                        checked={itemSelected}
                        onChange={(e) => onToggleItemSelected(p.id, e.target.checked)}
                        aria-label={
                          itemSelected
                            ? `Desmarcar ${p.nome || 'produto'}`
                            : `Marcar ${p.nome || 'produto'}`
                        }
                        className="h-5 w-5 cursor-pointer rounded border-zinc-300 text-[var(--st-600)] focus:ring-[var(--st-500)] dark:border-zinc-600 dark:bg-zinc-900"
                      />
                    </label>
                  ) : null}
                  <div className="flex h-14 w-14 shrink-0 items-center justify-center overflow-hidden rounded-lg bg-zinc-100 p-0.5 dark:bg-zinc-800">
                    {thumbUrl ? (
                      <ResilientImage
                        src={resolveApiMediaUrl(thumbUrl)}
                        alt=""
                        className="max-h-full max-w-full object-contain"
                        retries={2}
                        fallback={
                          <div className="flex h-full items-center justify-center text-xs font-bold text-zinc-400">
                            {(p.nome || '?').slice(0, 1)}
                          </div>
                        }
                      />
                    ) : (
                      <div className="flex h-full items-center justify-center text-xs font-bold text-zinc-400">
                        {(p.nome || '?').slice(0, 1)}
                      </div>
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p
                      className={`line-clamp-2 cursor-default text-xs font-bold uppercase ${
                        itemSelected
                          ? 'text-zinc-900 dark:text-zinc-50'
                          : 'text-zinc-500 line-through decoration-1 dark:text-zinc-400'
                      }`}
                      title={p.nome || undefined}
                    >
                      {p.nome}
                    </p>
                    <div className="mt-1.5 flex flex-wrap items-center justify-between gap-2">
                      <p className="text-[10px] font-medium uppercase tracking-wide text-zinc-500 dark:text-zinc-400">
                        Qtde <span className="tabular-nums">{q}</span>
                        {!itemSelected ? (
                          <span className="ml-1 rounded bg-zinc-200 px-1.5 py-px text-[9px] font-bold uppercase tracking-wide text-zinc-600 dark:bg-zinc-700 dark:text-zinc-200">
                            Deixar p/ depois
                          </span>
                        ) : null}
                      </p>
                      <p
                        className={`text-xs font-bold tabular-nums ${
                          itemSelected
                            ? 'text-zinc-800 dark:text-zinc-100'
                            : 'text-zinc-500 dark:text-zinc-400'
                        }`}
                      >
                        {pricingHidden ? '—' : formatBrl(line)}
                      </p>
                    </div>
                  </div>
                  <div className="flex shrink-0 flex-col items-center justify-center gap-1 self-center">
                    {typeof onAddOne === 'function' && (
                      <button
                        type="button"
                        aria-label={`Adicionar uma unidade de ${p.nome || 'produto'}`}
                        className="flex h-7 w-7 items-center justify-center rounded-md bg-[var(--st-600)] text-sm font-bold leading-none text-white shadow-sm transition hover:bg-[var(--st-500)] active:scale-95 dark:bg-[var(--st-500)] dark:hover:bg-[var(--st-400)]"
                        onClick={() => onAddOne(p.id)}
                      >
                        +
                      </button>
                    )}
                    {typeof onRemoveLine === 'function' && (
                      <button
                        type="button"
                        aria-label={`Remover ${p.nome || 'produto'} da sacola`}
                        className="flex h-7 w-7 items-center justify-center rounded-md border border-red-300/80 text-red-600 transition hover:bg-red-500/10 active:scale-95 dark:border-red-900/70 dark:text-red-400 dark:hover:bg-red-950/80"
                        onClick={() => onRemoveLine(p.id)}
                      >
                        <TrashIcon className="h-3.5 w-3.5" />
                      </button>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        </>
      )}

      {t.count > 0 && canUseCoupon && (
        <div className="space-y-2 border-t border-zinc-100 pt-4 dark:border-zinc-800">
          <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-zinc-400">Cupom (uso único)</p>
          <div className="flex gap-2">
            <input
              type="text"
              autoCapitalize="characters"
              autoComplete="off"
              spellCheck="false"
              placeholder="Código"
              className="min-w-0 flex-1 rounded-xl border border-zinc-200 px-3 py-2.5 text-sm uppercase dark:border-zinc-700 dark:bg-zinc-950"
              value={couponInput}
              onChange={(e) => {
                if (typeof setCouponInput === 'function') setCouponInput(e.target.value);
              }}
            />
            <button
              type="button"
              disabled={couponBusy}
              onClick={() => typeof onApplyCoupon === 'function' && onApplyCoupon()}
              className="shrink-0 rounded-xl border border-zinc-300 bg-white px-3 py-2 text-xs font-semibold dark:border-zinc-600 dark:bg-zinc-900"
            >
              {couponBusy ? '…' : 'Aplicar'}
            </button>
          </div>
          {couponMsg ? (
            <p
              className={`text-[11px] font-medium ${
                typeof couponPctValid === 'number' && couponPctValid > 0
                  ? 'text-emerald-700 dark:text-emerald-400'
                  : 'text-red-600 dark:text-red-400'
              }`}
            >
              {couponMsg}
            </p>
          ) : null}
          {typeof couponPctValid === 'number' && couponPctValid > 0 && !pricingHidden && t.subtotal > 0 ? (
            <p className="text-[11px] text-zinc-500 dark:text-zinc-400">
              Subtotal {formatBrl(t.subtotal)} · a pagar{' '}
              <span className="font-semibold text-zinc-800 dark:text-zinc-100">{formatBrl(t.total)}</span>
            </p>
          ) : null}
          {deliveryZonesEnabled && !isPickup && t.deliveryFee > 0 && !pricingHidden ? (
            <p className="text-[11px] text-zinc-500 dark:text-zinc-400">
              Inclui entrega {formatBrl(t.deliveryFee)} · total {formatBrl(t.total)}
            </p>
          ) : null}
        </div>
      )}

      <form className="space-y-4 border-t border-zinc-100 pt-4 dark:border-zinc-800" onSubmit={submitCheckout}>
        {checkoutError ? (
          <p
            role="alert"
            className="rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-xs font-medium text-red-800 dark:border-red-900/60 dark:bg-red-950/50 dark:text-red-200"
          >
            {checkoutError}
          </p>
        ) : null}
        <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-zinc-400">Identifique-se</p>
        <input
          required
          placeholder="Nome completo"
          className="w-full rounded-xl border border-zinc-200 px-4 py-3 text-sm outline-none ring-transparent transition focus:border-[var(--st-500)] focus:ring-2 focus:ring-[rgb(var(--st-rgb)/0.2)] dark:border-zinc-700 dark:bg-zinc-950 dark:text-zinc-100"
          value={checkout.nome}
          onChange={(e) => setCheckout((p) => ({ ...p, nome: e.target.value }))}
        />
        <input
          required={contactFieldHtmlRequired(contactReq, 'phone')}
          placeholder="WhatsApp / Celular"
          className="w-full rounded-xl border border-zinc-200 px-4 py-3 text-sm outline-none focus:border-[var(--st-500)] focus:ring-2 focus:ring-[rgb(var(--st-rgb)/0.2)] dark:border-zinc-700 dark:bg-zinc-950 dark:text-zinc-100"
          value={checkout.telefone}
          onChange={(e) => setCheckout((p) => ({ ...p, telefone: e.target.value }))}
          onBlur={() => (typeof onLookupKnownCustomer === 'function' ? onLookupKnownCustomer('telefone') : null)}
        />
        <div>
          <input
            required={contactFieldHtmlRequired(contactReq, 'email')}
            type="email"
            placeholder="E-mail"
            autoComplete="email"
            className="w-full rounded-xl border border-zinc-200 px-4 py-3 text-sm outline-none focus:border-[var(--st-500)] focus:ring-2 focus:ring-[rgb(var(--st-rgb)/0.2)] dark:border-zinc-700 dark:bg-zinc-950 dark:text-zinc-100"
            value={checkout.email}
            onChange={(e) => setCheckout((p) => ({ ...p, email: e.target.value }))}
            onBlur={() => (typeof onLookupKnownCustomer === 'function' ? onLookupKnownCustomer('email') : null)}
          />
          {contactRequirementsHint(contactReq) ? (
            <p className="mt-1.5 text-[11px] leading-snug text-zinc-500 dark:text-zinc-400">
              {contactRequirementsHint(contactReq)}
            </p>
          ) : (
            <p className="mt-1.5 text-[11px] leading-snug text-zinc-500 dark:text-zinc-400">
              Gravamos na sua ficha de cliente na loja. Se preencher e-mail, use um endereço válido (ex.:
              nome@gmail.com).
            </p>
          )}
          {checkoutLookupBusy ? (
            <p className="mt-1 text-[11px] font-medium text-zinc-500 dark:text-zinc-400">Buscando cadastro existente...</p>
          ) : null}
          {!checkoutLookupBusy && checkoutLookupMsg ? (
            <p className="mt-1 text-[11px] font-medium text-emerald-700 dark:text-emerald-400">{checkoutLookupMsg}</p>
          ) : null}
        </div>
        <div className="space-y-2">
          <label className="block text-[11px] font-bold uppercase tracking-[0.2em] text-zinc-400">
            {pickupOnly ? 'Retirada' : 'Entrega ou retirada'}
          </label>
          <select
            className="w-full rounded-xl border border-zinc-200 bg-white px-3 py-2.5 text-sm outline-none focus:border-[var(--st-500)] focus:ring-2 focus:ring-[rgb(var(--st-rgb)/0.2)] dark:border-zinc-700 dark:bg-zinc-950 dark:text-zinc-100"
            value={checkoutFulfillmentMode}
            disabled={pickupOnly}
            onChange={(e) => {
              if (typeof onFulfillmentModeChange === 'function') {
                onFulfillmentModeChange(e.target.value);
              }
            }}
          >
            <option value={CHECKOUT_FULFILL_PICKUP}>
              {pickupOnly ? 'Retirar na loja (única opção)' : 'Retirar na loja'}
            </option>
            {hasSavedAddresses
              ? savedDeliveryAddresses.map((a) => (
                  <option key={a.id} value={checkoutFulfillmentSavedValue(a.id)}>
                    {(a.label ? `${String(a.label)} — ` : 'Morada — ') +
                      String(a.endereco || '').slice(0, 64)}
                    {String(a.endereco || '').length > 64 ? '…' : ''}
                  </option>
                ))
              : null}
            {!pickupOnly ? (
              <option value={CHECKOUT_FULFILL_NEW}>Adicionar / usar outro endereço</option>
            ) : null}
          </select>
          {isPickup ? (
            <p className="text-[11px] font-medium text-emerald-700 dark:text-emerald-300">
              {pickupOnly
                ? 'Esta loja trabalha só com retirada no balcão — sem entrega.'
                : 'Endereço desobrigado. O pedido será marcado com RETIRAR NA LOJA nas observações.'}
            </p>
          ) : null}
          {!isPickup && checkoutFulfillmentIsSaved(checkoutFulfillmentMode) ? (
            <>
              <p className="text-[11px] text-zinc-500 dark:text-zinc-400">
                Entrega na morada selecionada. Confirme o CEP abaixo se a taxa não aparecer.
              </p>
              {deliveryZonesEnabled ? (
                <input
                  inputMode="numeric"
                  autoComplete="postal-code"
                  placeholder="CEP para entrega (00000-000)"
                  className="w-full rounded-xl border border-zinc-200 px-4 py-3 text-sm outline-none focus:border-[var(--st-500)] focus:ring-2 focus:ring-[rgb(var(--st-rgb)/0.2)] dark:border-zinc-700 dark:bg-zinc-950 dark:text-zinc-100"
                  value={checkout.cep || ''}
                  onChange={(e) =>
                    setCheckout((prev) => ({
                      ...prev,
                      cep: formatCep(e.target.value),
                    }))
                  }
                />
              ) : null}
            </>
          ) : null}
          {!isPickup && isNewAddress ? (
            <>
          <div className="flex items-center gap-2">
            <input
              inputMode="numeric"
              autoComplete="postal-code"
              placeholder="CEP (00000-000)"
              className="w-full rounded-xl border border-zinc-200 px-4 py-3 text-sm outline-none focus:border-[var(--st-500)] focus:ring-2 focus:ring-[rgb(var(--st-rgb)/0.2)] dark:border-zinc-700 dark:bg-zinc-950 dark:text-zinc-100"
              value={checkout.cep || ''}
              onChange={(e) =>
                setCheckout((prev) => ({
                  ...prev,
                  cep: formatCep(e.target.value),
                }))
              }
            />
            <button
              type="button"
              disabled={cepLookupBusy}
              onClick={() => (typeof onLookupCep === 'function' ? onLookupCep() : null)}
              className="h-[46px] shrink-0 rounded-xl border border-[var(--st-600)] px-3 text-xs font-semibold uppercase tracking-wide text-[var(--st-700)] transition hover:bg-[rgb(var(--st-rgb)/0.1)] disabled:cursor-not-allowed disabled:opacity-60 dark:border-[var(--st-500)] dark:text-[var(--st-400)] dark:hover:bg-zinc-800/90"
            >
              {cepLookupBusy ? 'Buscando...' : 'Buscar CEP'}
            </button>
          </div>
          {cepLookupError ? (
            <p className="text-xs font-medium text-red-600 dark:text-red-400">{cepLookupError}</p>
          ) : null}
          {!cepLookupError && cepLookupMsg ? (
            <p className="text-xs font-medium text-emerald-700 dark:text-emerald-400">{cepLookupMsg}</p>
          ) : null}
          <textarea
            required
            rows={compact ? 2 : 3}
            placeholder="Endereço completo para entrega"
            className="w-full resize-none rounded-xl border border-zinc-200 px-4 py-3 text-sm outline-none focus:border-[var(--st-500)] focus:ring-2 focus:ring-[rgb(var(--st-rgb)/0.2)] dark:border-zinc-700 dark:bg-zinc-950 dark:text-zinc-100"
            value={checkout.endereco}
            onChange={(e) => setCheckout((p) => ({ ...p, endereco: e.target.value }))}
          />
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            <input
              placeholder="Número (opcional se houver complemento)"
              className="w-full rounded-xl border border-zinc-200 px-4 py-3 text-sm outline-none focus:border-[var(--st-500)] focus:ring-2 focus:ring-[rgb(var(--st-rgb)/0.2)] dark:border-zinc-700 dark:bg-zinc-950 dark:text-zinc-100"
              value={checkout.enderecoNumero || ''}
              onChange={(e) => setCheckout((p) => ({ ...p, enderecoNumero: e.target.value }))}
            />
            <input
              placeholder="Complemento (opcional se houver número)"
              className="w-full rounded-xl border border-zinc-200 px-4 py-3 text-sm outline-none focus:border-[var(--st-500)] focus:ring-2 focus:ring-[rgb(var(--st-rgb)/0.2)] dark:border-zinc-700 dark:bg-zinc-950 dark:text-zinc-100"
              value={checkout.enderecoComplemento || ''}
              onChange={(e) => setCheckout((p) => ({ ...p, enderecoComplemento: e.target.value }))}
            />
          </div>
          <p className="text-[11px] text-zinc-500 dark:text-zinc-400">
            Para finalizar, preencha pelo menos um: número ou complemento.
          </p>
            </>
          ) : null}
          {deliveryZonesEnabled && !isPickup ? (
            <div className="rounded-xl border border-zinc-200/80 bg-zinc-50 px-3 py-2.5 text-xs dark:border-zinc-700 dark:bg-zinc-900/50">
              {deliveryFeeBusy ? (
                <p className="text-zinc-500 dark:text-zinc-400">A calcular taxa de entrega…</p>
              ) : deliveryFeeError ? (
                <p className="font-medium text-red-600 dark:text-red-400">{deliveryFeeError}</p>
              ) : deliveryFeeQuote ? (
                <p className="font-medium text-emerald-800 dark:text-emerald-300">
                  {deliveryFeeQuote.fee === 0
                    ? 'Entrega grátis'
                    : `Taxa de entrega: ${formatBrl(deliveryFeeQuote.fee)}`}{' '}
                  · {deliveryFeeQuote.estimated_days}{' '}
                  {deliveryFeeQuote.estimated_days === 1 ? 'dia' : 'dias'}
                  {deliveryFeeQuote.zone ? ` (${deliveryFeeQuote.zone})` : ''}
                </p>
              ) : (
                <p className="text-zinc-500 dark:text-zinc-400">
                  Informe o CEP completo para ver a taxa de entrega.
                </p>
              )}
            </div>
          ) : null}
          <textarea
            rows={compact ? 2 : 3}
            placeholder="Observações do pedido (opcional)"
            className="w-full resize-none rounded-xl border border-zinc-200 px-4 py-3 text-sm outline-none focus:border-[var(--st-500)] focus:ring-2 focus:ring-[rgb(var(--st-rgb)/0.2)] dark:border-zinc-700 dark:bg-zinc-950 dark:text-zinc-100"
            value={checkout.observacoes || ''}
            onChange={(e) => setCheckout((p) => ({ ...p, observacoes: e.target.value }))}
          />
          {Array.isArray(checkoutPaymentOptions) && checkoutPaymentOptions.length > 0 ? (
            <div className="space-y-1.5 pt-1">
              <label className="block text-[11px] font-bold uppercase tracking-[0.2em] text-zinc-400">
                {checkoutPaymentHasOnline ? 'Forma de pagamento' : 'Forma de pagamento (informativo)'}
              </label>
              <PaymentMethodSelect
                value={checkout.paymentMethod || ''}
                options={checkoutPaymentOptions}
                onChange={(v) => setCheckout((p) => ({ ...p, paymentMethod: v }))}
              />
              {checkoutPaymentHasOnline && checkoutPaymentIsOnline(checkout.paymentMethod, checkoutPaymentOptions) ? (
                <p className="rounded-lg bg-emerald-500/10 px-3 py-2 text-[11px] font-semibold text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-200">
                  Ao enviar o pedido você será redirecionado ao Mercado Pago para concluir o pagamento (cartão, Pix ou boleto).
                </p>
              ) : checkoutPaymentHasOnline ? (
                <p className="text-[10px] text-zinc-500 dark:text-zinc-400">
                  Escolha a opção online (em verde) para pagar agora; as outras são informação para a loja (pagamento na entrega/retirada).
                </p>
              ) : (
                <p className="text-[10px] text-zinc-500 dark:text-zinc-400">
                  Informação para a loja e envio ao sistema (ERP); não substitui pagamento online, se existir.
                </p>
              )}
            </div>
          ) : null}
        </div>
        {customerSession ? (
          <label className="flex items-start gap-2 text-xs text-zinc-600 dark:text-zinc-300">
            <input
              type="checkbox"
              checked={updateCustomerAddress && isNewAddress && !isPickup}
              disabled={!isNewAddress || isPickup}
              onChange={(e) => setUpdateCustomerAddress(e.target.checked)}
              className="mt-0.5 h-4 w-4 rounded"
            />
            Atualizar meu endereço cadastrado com o endereço digitado acima.
          </label>
        ) : null}

        <button
          type="submit"
          disabled={t.count === 0}
          className="flex w-full items-center justify-center gap-2 rounded-full bg-zinc-900 py-4 text-sm font-bold text-white shadow-lg transition enabled:hover:-translate-y-0.5 enabled:hover:shadow-xl disabled:cursor-not-allowed disabled:opacity-40 dark:bg-[var(--st-600)] dark:enabled:hover:bg-[var(--st-500)]"
        >
          Enviar pedido de: {pricingHidden ? '—' : formatBrl(t.total)}
          <span aria-hidden>›</span>
        </button>
        {cartLines.length > 0 && t.count === 0 ? (
          <p className="-mt-2 text-center text-[11px] font-medium text-amber-700 dark:text-amber-300">
            Selecione pelo menos 1 produto da sacola para enviar o pedido.
          </p>
        ) : null}
      </form>
    </div>
  );
}

function PaymentCreditCardIcon(props) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
      {...props}
    >
      <rect x="2.5" y="5.5" width="19" height="13" rx="2" />
      <line x1="2.5" y1="10" x2="21.5" y2="10" />
      <line x1="6" y1="14.5" x2="9" y2="14.5" />
      <line x1="12" y1="14.5" x2="14" y2="14.5" />
    </svg>
  );
}

function PaymentStoreIcon(props) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
      {...props}
    >
      <path d="M3 9l1.4-4.5h15.2L21 9" />
      <path d="M4 9v10h16V9" />
      <path d="M9 13h6v6H9z" />
    </svg>
  );
}

function PaymentChevronIcon(props) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
      {...props}
    >
      <polyline points="6 9 12 15 18 9" />
    </svg>
  );
}

/**
 * Dropdown customizado para escolher a forma de pagamento no checkout.
 * Diferente do `<select>` nativo, permite ícone destacado e cor verde para a
 * opção que dispara o Mercado Pago, com subtítulo descritivo
 * (ex.: "Cartão · Pix · Boleto via Mercado Pago"). Demais opções mostram um
 * ícone de loja para representar pagamento offline.
 *
 * @param {{
 *   value: string,
 *   options: Array<{value:string, display:string, kind:'online'|'offline-fallback'|'tenant', subtitle?:string}>,
 *   onChange: (value:string) => void,
 * }} props
 */
function PaymentMethodSelect({ value, options, onChange }) {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);
  const selected = findCheckoutPaymentOption(options, value);

  useEffect(() => {
    if (!open) return undefined;
    const onDocClick = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    const onEsc = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onEsc);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onEsc);
    };
  }, [open]);

  const renderOptionIcon = (opt, size = 'h-4 w-4') =>
    opt.kind === 'online' ? (
      <PaymentCreditCardIcon className={size} />
    ) : (
      <PaymentStoreIcon className={size} />
    );

  return (
    <div ref={wrapperRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
        className={`flex w-full items-center gap-3 rounded-xl border px-4 py-3 text-left outline-none transition focus:ring-2 focus:ring-[rgb(var(--st-rgb)/0.2)] ${
          selected?.kind === 'online'
            ? 'border-emerald-300 bg-emerald-50 text-emerald-900 hover:bg-emerald-100 dark:border-emerald-500/40 dark:bg-emerald-500/10 dark:text-emerald-100'
            : 'border-zinc-200 bg-white text-zinc-900 hover:bg-zinc-50 dark:border-zinc-700 dark:bg-zinc-950 dark:text-zinc-100 dark:hover:bg-zinc-900'
        }`}
      >
        {selected ? (
          <>
            <span
              className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${
                selected.kind === 'online'
                  ? 'bg-emerald-500 text-white shadow-sm'
                  : 'bg-zinc-100 text-zinc-600 dark:bg-zinc-800 dark:text-zinc-300'
              }`}
            >
              {renderOptionIcon(selected, 'h-5 w-5')}
            </span>
            <span className="flex min-w-0 flex-1 flex-col">
              <span className="truncate text-sm font-bold uppercase tracking-wide">
                {selected.display}
              </span>
              {selected.subtitle ? (
                <span
                  className={`truncate text-[10px] font-medium ${
                    selected.kind === 'online'
                      ? 'text-emerald-700/80 dark:text-emerald-200/85'
                      : 'text-zinc-500 dark:text-zinc-400'
                  }`}
                >
                  {selected.subtitle}
                </span>
              ) : null}
            </span>
          </>
        ) : (
          <span className="flex-1 text-sm font-medium text-zinc-500 dark:text-zinc-400">
            Selecione a forma de pagamento…
          </span>
        )}
        <PaymentChevronIcon
          className={`h-4 w-4 shrink-0 text-zinc-500 transition dark:text-zinc-400 ${
            open ? 'rotate-180' : ''
          }`}
        />
      </button>

      {open ? (
        <ul
          role="listbox"
          className="absolute z-30 mt-1.5 max-h-72 w-full overflow-y-auto rounded-xl border border-zinc-200 bg-white p-1 shadow-xl ring-1 ring-black/5 dark:border-zinc-700 dark:bg-zinc-950 dark:ring-white/10"
        >
          {options.map((opt) => {
            const isSelected = opt.value === value;
            const isOnline = opt.kind === 'online';
            return (
              <li
                key={opt.value}
                role="option"
                aria-selected={isSelected}
                tabIndex={0}
                onClick={() => {
                  onChange(opt.value);
                  setOpen(false);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onChange(opt.value);
                    setOpen(false);
                  }
                }}
                className={`flex cursor-pointer items-center gap-3 rounded-lg px-3 py-2.5 transition ${
                  isSelected
                    ? isOnline
                      ? 'bg-emerald-100 dark:bg-emerald-500/20'
                      : 'bg-zinc-100 dark:bg-zinc-800'
                    : isOnline
                      ? 'hover:bg-emerald-50 dark:hover:bg-emerald-500/10'
                      : 'hover:bg-zinc-50 dark:hover:bg-zinc-900'
                }`}
              >
                <span
                  className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${
                    isOnline
                      ? 'bg-emerald-500 text-white shadow-sm'
                      : 'bg-zinc-100 text-zinc-600 dark:bg-zinc-800 dark:text-zinc-300'
                  }`}
                >
                  {renderOptionIcon(opt, 'h-4 w-4')}
                </span>
                <span className="flex min-w-0 flex-1 flex-col">
                  <span
                    className={`truncate text-sm font-bold uppercase tracking-wide ${
                      isOnline
                        ? 'text-emerald-800 dark:text-emerald-100'
                        : 'text-zinc-900 dark:text-zinc-100'
                    }`}
                  >
                    {opt.display}
                  </span>
                  {opt.subtitle ? (
                    <span
                      className={`truncate text-[10px] font-medium ${
                        isOnline
                          ? 'text-emerald-700/80 dark:text-emerald-200/85'
                          : 'text-zinc-500 dark:text-zinc-400'
                      }`}
                    >
                      {opt.subtitle}
                    </span>
                  ) : null}
                </span>
                {isSelected ? (
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="3"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className={`h-4 w-4 shrink-0 ${
                      isOnline
                        ? 'text-emerald-600 dark:text-emerald-300'
                        : 'text-zinc-500 dark:text-zinc-400'
                    }`}
                    aria-hidden
                  >
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                ) : null}
              </li>
            );
          })}
        </ul>
      ) : null}
    </div>
  );
}
